import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { LogOut } from 'lucide-react';
import './App.css';
import backupData from './data/backup.json';
// Fase 2 — dados contábeis carregados por empresa ativa via hook.
// Imports diretos dos JSONs da Kzero foram removidos: agora `useEmpresaContabil()`
// resolve o dataset (plano de contas, regras, abertura, referências, lançamentos)
// com base em `useEmpresa()`.
import { useEmpresaContabil } from './hooks/useEmpresaContabil';
import { useContabilLancamentos } from './hooks/useContabilLancamentos';
import FechamentoMes from './components/contabilidade/FechamentoMes';
import { useContabilFechamento } from './hooks/useContabilFechamento';
import { calcularBalancete } from './utils/contabil/balancete';
import { flagConta, situacaoValidacao, totaisDaLinha } from './utils/contabil/conferencia';
import { supabase, CHAVE_RECOVERY } from './lib/supabase';
import { EVENTO_USUARIOS_ALTERADOS } from './utils/criarAcesso';
import { remocaoLiberada, consumirLiberacao } from './lib/liberacaoRemocao';
import { deveDispararBackup, salvarSnapshot } from './utils/autoBackup';
import { uploadSnapshotNuvem, rotacionarNuvem } from './utils/cloudBackup';
import { registrarAuditoria, AUDIT_ACTIONS } from './utils/auditLog';
import { iniciarHeartbeat, pararHeartbeat, removerSessao } from './utils/sessionGuard';
import { encryptData, decryptData } from './utils/encryption';
import { toast } from './utils/toast';
import * as syncQueue from './lib/syncQueue';
import { exportarPDF, htmlReciboProLabore } from './utils/exportDoc';
import logoKzeroWhite from './assets/kzero-logo-white.png';
import logoKzeroColor from './assets/kzero-logo-color.png';

// Componentes
import { Login }               from './components/Login';
import { Painel }              from './components/Painel';
import { Pessoas }             from './components/Pessoas';
import { Calendario }          from './components/Calendario';
import { FinanceiroAvancado }  from './components/FinanceiroAvancado';
import { Documentos }          from './components/Documentos';
import { Backup }              from './components/Backup';
import { Toaster }             from './components/Toaster';
import { Ensino }              from './components/Ensino';
import { Prospeccao }          from './components/Prospeccao';
import Licitacoes              from './components/Licitacoes';
import { DiagnosticoNR1 }     from './components/DiagnosticoNR1';
import { Marketing }          from './components/Marketing';
import MobileMenu             from './components/MobileMenu';
import MenuLateral             from './components/MenuLateral';
import EmpresaSelector        from './components/EmpresaSelector';

// Context multi-empresa (Fase 2 — consumo efetivo)
import { EmpresaProvider, useEmpresa } from './contexts/EmpresaContext';

// Registry único de módulos (fonte de verdade — ver src/config/modulos.js)
import { NAV_MODULOS as NAV } from './config/modulos';

// Referência estável de objeto vazio — usada pelo wrapper de contabilConfig
// quando a empresa ativa ainda não tem entrada em data.contabilConfigPorEmpresa.
// Compartilhar a mesma referência evita invalidar useMemos a cada render.
const EMPTY_CONTABIL_CONFIG = Object.freeze({});
const EMPTY_FLUXO_CAIXA = Object.freeze([]);

const LS_CACHE = 'kzerodiary_cache';

// Remove dados base64 pesados antes de salvar no localStorage (limite ~5MB)
// Varre recursivamente arrays/objetos e substitui strings base64 longas por placeholder
const stripBase64ParaCache = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripBase64ParaCache);
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === 'string' && v.length > 5000 && (v.startsWith('data:') || v.match(/^[A-Za-z0-9+/=]{5000}/))) {
      out[k] = '__b64__';
    } else if (typeof v === 'object' && v !== null) {
      out[k] = stripBase64ParaCache(v);
    } else {
      out[k] = v;
    }
  }
  return out;
};

const safeSetCache = (data) => {
  try { localStorage.setItem(LS_CACHE, JSON.stringify(stripBase64ParaCache(data))); }
  catch { /* quota exceeded — dados salvos na nuvem */ }
};

// Detecta se um payload contem o placeholder '__b64__' (cache stripado).
// Salvar isso no Supabase destroi os anexos originais.
const contemPlaceholderStripado = (valor) => {
  if (!valor) return false;
  if (typeof valor === 'string') return valor === '__b64__';
  if (Array.isArray(valor)) return valor.some(contemPlaceholderStripado);
  if (typeof valor === 'object') {
    for (const v of Object.values(valor)) if (contemPlaceholderStripado(v)) return true;
  }
  return false;
};

// Reidrata os placeholders '__b64__' do valor incoming usando a versao atual do
// Supabase. Para arrays de itens com id (anotacoes/reembolsos/etc), casa por id
// e substitui campo a campo. Para anexos dentro de cada item, tambem casa por nome.
// Recompoe o que o cache stripou, buscando o valor original no registro
// correspondente do Supabase.
//
// A versao anterior so olhava `anexos[]` e `anexo` — mas stripBase64ParaCache
// troca por '__b64__' QUALQUER string grande que pareca base64, em qualquer
// chave (foto, assinatura, imagem de ata...). O que nao fosse anexo nunca era
// recomposto e ia como placeholder pro servidor, destruindo o original.
// Agora a busca e por caminho: mesmo item (id), mesma chave, em qualquer
// profundidade.
const recomporValor = (novo, antigo) => {
  if (novo === '__b64__') {
    // So aceita o valor do servidor se ele mesmo nao for placeholder.
    return (typeof antigo === 'string' && antigo && antigo !== '__b64__') ? antigo : novo;
  }
  if (Array.isArray(novo)) {
    if (!Array.isArray(antigo)) return novo;
    return novo.map((item, i) => {
      // Casa por id quando existe; senao, pela posicao.
      const par = (item && typeof item === 'object' && item.id != null)
        ? antigo.find(x => x && x.id === item.id)
        : antigo[i];
      return recomporValor(item, par);
    });
  }
  if (novo && typeof novo === 'object') {
    if (!antigo || typeof antigo !== 'object') return novo;
    const out = { ...novo };
    for (const chave of Object.keys(novo)) {
      out[chave] = recomporValor(novo[chave], antigo[chave]);
    }
    return out;
  }
  return novo;
};

const reidratarStripped = (incoming, supabaseAtual) => recomporValor(incoming, supabaseAtual);

// Busca dados em todas as chaves localStorage possíveis (migração entre versões)
function buscarDadosLocais() {
  const chaves = [
    LS_CACHE,
    'kzerodiary_data',
    // chaves por usuário geradas na versão anterior
    ...Object.keys(localStorage).filter(k => k.startsWith('kzerodiary_data_') && !k.includes('_ts')),
  ];
  for (const chave of chaves) {
    try {
      const raw = localStorage.getItem(chave);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
          return parsed;
        }
      }
    } catch {}
  }
  return null;
}

function ModalAlterarSenha({ onFechar, perfilUsuario, sessao }) {
  const [novaSenha, setNovaSenha]       = useState('');
  const [confirmSenha, setConfirmSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [salvando, setSalvando]         = useState(false);
  const [erro, setErro]                 = useState('');
  const [ok, setOk]                     = useState(false);

  const email = perfilUsuario?.email || sessao?.user?.email || '';
  const nome  = perfilUsuario?.nome  || '';

  const salvar = async (e) => {
    e.preventDefault();
    if (novaSenha.length < 8) { setErro('A senha deve ter pelo menos 8 caracteres.'); return; }
    if (!/[A-Z]/.test(novaSenha)) { setErro('A senha deve conter pelo menos uma letra maiúscula.'); return; }
    if (!/[0-9]/.test(novaSenha)) { setErro('A senha deve conter pelo menos um número.'); return; }
    if (novaSenha !== confirmSenha) { setErro('As senhas não conferem.'); return; }
    setSalvando(true);
    setErro('');
    const { error } = await supabase.auth.updateUser({ password: novaSenha });
    if (error) { setErro('Erro ao salvar: ' + error.message); setSalvando(false); return; }
    registrarAuditoria(AUDIT_ACTIONS.PASSWORD_CHANGE, 'perfil');
    setOk(true);
    setSalvando(false);
    setTimeout(() => onFechar(), 2000);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, padding:'1rem' }} onClick={onFechar}>
      <div style={{ background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:14, padding:'1.75rem', width:'100%', maxWidth:400, display:'flex', flexDirection:'column', gap:'1rem' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ color:'#1c1530', fontSize:16, fontWeight:700, margin:0 }}>👤 Meu Perfil</h3>
          <button onClick={onFechar} style={{ background:'none', border:'none', color:'#5f5874', cursor:'pointer', fontSize:20, lineHeight:1 }}>×</button>
        </div>

        {/* Dados do perfil */}
        <div style={{ background:'#ffffff', border:'1px solid #eeeaf5', borderRadius:8, padding:'0.875rem', display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {nome && (
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <span style={{ fontSize:12, color:'#5f5874', fontWeight:600, minWidth:48 }}>Nome</span>
              <span style={{ fontSize:13, color:'#1c1530' }}>{nome}</span>
            </div>
          )}
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <span style={{ fontSize:12, color:'#5f5874', fontWeight:600, minWidth:48 }}>E-mail</span>
            <span style={{ fontSize:13, color:'#1c1530' }}>{email}</span>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <span style={{ fontSize:12, color:'#5f5874', fontWeight:600, minWidth:48 }}>Senha</span>
            <span style={{ fontSize:13, color:'#5f5874', fontStyle:'italic' }}>••••••••  (use o formulário abaixo para alterar)</span>
          </div>
        </div>

        {/* Formulário alterar senha */}
        {ok ? (
          <p style={{ color:'#10b981', fontWeight:600, textAlign:'center', padding:'0.5rem 0' }}>✓ Senha alterada com sucesso!</p>
        ) : (
          <form onSubmit={salvar} style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
            <p style={{ fontSize:12, color:'#5f5874', margin:0, fontWeight:600 }}>ALTERAR SENHA</p>
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <label style={{ fontSize:12, color:'#5f5874', fontWeight:600 }}>Nova senha</label>
              <div style={{ position:'relative' }}>
                <input
                  type={mostrarSenha ? 'text' : 'password'}
                  value={novaSenha}
                  onChange={e => setNovaSenha(e.target.value)}
                  placeholder="Mínimo 8 caracteres, 1 maiúscula, 1 número"
                  required
                  style={{ background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:6, color:'#1c1530', padding:'8px 36px 8px 10px', fontSize:13, width:'100%', boxSizing:'border-box' }}
                />
                <button type="button" onClick={() => setMostrarSenha(v => !v)} style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:'#5f5874', cursor:'pointer', fontSize:14 }}>
                  {mostrarSenha ? '🙈' : '👁'}
                </button>
              </div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <label style={{ fontSize:12, color:'#5f5874', fontWeight:600 }}>Confirmar nova senha</label>
              <input
                type={mostrarSenha ? 'text' : 'password'}
                value={confirmSenha}
                onChange={e => setConfirmSenha(e.target.value)}
                placeholder="Repita a senha"
                required
                style={{ background:'#ffffff', border:'1px solid #e5e0ef', borderRadius:6, color:'#1c1530', padding:'8px 10px', fontSize:13, width:'100%', boxSizing:'border-box' }}
              />
            </div>
            {erro && <p style={{ color:'#ef4444', fontSize:13, margin:0 }}>{erro}</p>}
            <div style={{ display:'flex', gap:'0.5rem' }}>
              <button type="submit" disabled={salvando} style={{ flex:1, background:'#7c3aed', color:'white', border:'none', borderRadius:6, padding:'9px', fontSize:13, fontWeight:600, cursor: salvando ? 'not-allowed' : 'pointer', opacity: salvando ? 0.7 : 1 }}>
                {salvando ? 'Salvando...' : 'Salvar nova senha'}
              </button>
              <button type="button" onClick={onFechar} style={{ background:'transparent', border:'1px solid #e5e0ef', borderRadius:6, color:'#5f5874', padding:'9px 16px', fontSize:13, cursor:'pointer' }}>
                Fechar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function RedefinirSenha({ onConcluido }) {
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmSenha, setConfirmSenha] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState(false);

  const salvar = async (e) => {
    e.preventDefault();
    if (novaSenha.length < 8) { setErro('A senha deve ter pelo menos 8 caracteres.'); return; }
    if (!/[A-Z]/.test(novaSenha)) { setErro('A senha deve conter pelo menos uma letra maiúscula.'); return; }
    if (!/[0-9]/.test(novaSenha)) { setErro('A senha deve conter pelo menos um número.'); return; }
    if (novaSenha !== confirmSenha) { setErro('As senhas não conferem.'); return; }
    setSalvando(true);
    setErro('');
    const { error } = await supabase.auth.updateUser({ password: novaSenha });
    if (error) {
      setErro(/different|same/i.test(error.message || '') ? 'A nova senha precisa ser diferente da anterior.' : 'Não foi possível salvar a senha. Abra o link do e-mail de novo e tente outra vez.');
      setSalvando(false);
      return;
    }
    setOk(true);
    setSalvando(false);
    setTimeout(() => onConcluido(), 2000);
  };

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', backgroundColor:'#f0f2f5' }}>
      <div style={{ backgroundColor:'#fff', padding:40, borderRadius:12, boxShadow:'0 4px 24px rgba(0,0,0,0.10)', width:'100%', maxWidth:380, textAlign:'center' }}>
        <h2 style={{ margin:'0 0 8px', fontSize:24, color:'#1a1a2e' }}>Nova senha</h2>
        <p style={{ margin:'0 0 24px', fontSize:13, color:'#888' }}>Defina sua senha de acesso para continuar.</p>
        {ok ? (
          <p style={{ color:'#10b981', fontWeight:600 }}>Senha salva. Entrando no sistema...</p>
        ) : (
          <form onSubmit={salvar} style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div style={{ display:'flex', flexDirection:'column', textAlign:'left', gap:6 }}>
              <label style={{ fontSize:13, fontWeight:600, color:'#444' }}>Nova senha</label>
              <input type="password" value={novaSenha} onChange={e => setNovaSenha(e.target.value)} placeholder="Mínimo 8 caracteres, 1 maiúscula, 1 número" required style={{ padding:'10px 14px', borderRadius:8, border:'1px solid #ddd', fontSize:14 }} />
            </div>
            <div style={{ display:'flex', flexDirection:'column', textAlign:'left', gap:6 }}>
              <label style={{ fontSize:13, fontWeight:600, color:'#444' }}>Confirmar senha</label>
              <input type="password" value={confirmSenha} onChange={e => setConfirmSenha(e.target.value)} placeholder="Repita a senha" required style={{ padding:'10px 14px', borderRadius:8, border:'1px solid #ddd', fontSize:14 }} />
            </div>
            {erro && <p style={{ color:'#e53e3e', fontSize:13, margin:0 }}>{erro}</p>}
            <button type="submit" disabled={salvando} style={{ padding:12, backgroundColor: salvando ? '#888' : '#7c3aed', color:'#fff', border:'none', borderRadius:8, fontSize:15, fontWeight:600, cursor: salvando ? 'not-allowed' : 'pointer', marginTop:8 }}>
              {salvando ? 'Salvando...' : 'Redefinir senha'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function App() {
  const [pagina, setPagina]                   = useState('agenda');
  const [data, setData]                       = useState(null);
  const [sessao, setSessao]                   = useState(null);
  const [carregandoSessao, setCarregando]     = useState(true);
  const [carregandoDados, setCarregandoDados]   = useState(true);
  const [carregandoPerfil, setCarregandoPerfil] = useState(true);
  const [perfilUsuario, setPerfilUsuario]       = useState(null);
  const [todosUsuarios, setTodosUsuarios]     = useState([]);
  // Profissionais disponíveis para seleção em eventos: Bianca + Janaína fixos
  // + nomes dos usuários cadastrados (deduplicados, exclui admin)
  const profissionais = useMemo(() => {
    const fixos = ['Bianca', 'Janaína'];
    const nomesUsuarios = (todosUsuarios || [])
      .filter(u => u.nome && u.papel !== 'admin')
      .map(u => u.nome);
    return [...new Set([...fixos, ...nomesUsuarios])];
  }, [todosUsuarios]);

  // Normaliza agendamentos: recalcula valor com horas arredondadas (1 decimal) × valorHora.
  // ESTAGIARIA não é cobrado do cliente — valor fica 0 na agenda (o cálculo "a pagar"
  // é feito no Painel usando data.configEstagiaria.valorHora).
  const normalizarAgendamentos = useCallback((dados) => {
    if (!dados?.agendamentos || !dados?.clientes) return dados;
    const clientes = dados.clientes || [];
    dados.agendamentos = dados.agendamentos.map(ag => {
      const classif = ag.classificacao || ag.tipo || 'CONSULTORIA';
      const horas = parseFloat(parseFloat(ag.horas || 0).toFixed(1));
      if (classif === 'ESTAGIARIA') {
        return { ...ag, horas: horas.toFixed(1), valor: '0.00' };
      }
      if (classif === 'REUNIAO' && !ag.faturarReuniao) {
        return { ...ag, horas: horas.toFixed(1), valor: '0.00' };
      }
      const cliente = clientes.find(c => String(c.id) === String(ag.cliente));
      if (!cliente) return ag;
      const valor = (horas * parseFloat(cliente.valorHora || 0)).toFixed(2);
      return { ...ag, horas: horas.toFixed(1), valor };
    });
    return dados;
  }, []);
  const [sincronizando, setSincronizando]     = useState(false);
  const [telaRecovery, setTelaRecovery]       = useState(() => { try { return sessionStorage.getItem(CHAVE_RECOVERY) === '1'; } catch { return false; } });
  const [modalSenha, setModalSenha]           = useState(false);
  const debounceRef                           = useRef(null);
  const ignorarRealtimeRef                    = useRef(false); // evita loop ao salvar
  // Campos com escrita pendente (debounce ativo ou upload em andamento).
  // Realtime NÃO sobrescreve esses campos ao mergear — evita perda de dados quando
  // outra aba/usuário salva um campo diferente enquanto esta aba tem edições locais não sincronizadas.
  // Inicialmente isolado para endomarketing (bug reportado); pode ser generalizado.
  const camposPendentesRef                    = useRef(new Set());
  const userIdCarregadoRef                    = useRef(null);  // evita recarregar ao renovar token
  const idleTimerRef                          = useRef(null);
  const [avisoInatividade, setAvisoInatividade] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [mostrarBannerIOS, setMostrarBannerIOS] = useState(false);

  // PWA Install prompt (Chrome/Edge/Android) + auto-update
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS detection — Safari não dispara beforeinstallprompt
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (isIOS && !isStandalone && !localStorage.getItem('pwa-banner-dismissed')) {
      setMostrarBannerIOS(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Lembretes de follow-up de prospecção por e-mail: DESATIVADOS a pedido da
  // Bianca (02/09/2026) — os e-mails automáticos estavam incomodando. O disparo
  // ao abrir o app foi removido daqui; o helper `verificarEEnviarLembretes`
  // (src/utils/lembreteFollowup) e a tela de Backup (que liga/desliga o EmailJS)
  // continuam existindo — é só reimportar e chamar aqui se um dia quiser de volta.

  const instalarApp = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setDeferredPrompt(null);
  };

  // ISO 27001 A.9.4.2 — Logout automático por inatividade (30 min)
  const IDLE_TIMEOUT_MS  = 30 * 60 * 1000; // 30 minutos
  const IDLE_WARNING_MS  = 28 * 60 * 1000; // aviso 2 min antes

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    setAvisoInatividade(false);
    idleTimerRef.current = setTimeout(async () => {
      setAvisoInatividade(true);
      // aguarda 2 min antes do logout final
      idleTimerRef.current = setTimeout(async () => {
        await registrarAuditoria(AUDIT_ACTIONS.SESSION_TIMEOUT, 'sistema');
        await supabase.auth.signOut();
        setAvisoInatividade(false);
      }, IDLE_TIMEOUT_MS - IDLE_WARNING_MS);
    }, IDLE_WARNING_MS);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSessao(session);
      setCarregando(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Link de redefinição: tela própria de nova senha, que só sai depois da troca
      // (antes abria o modal de perfil, que fechava em "Fechar" com acesso normal).
      if (event === 'PASSWORD_RECOVERY') {
        try { sessionStorage.setItem(CHAVE_RECOVERY, '1'); } catch {}
        setTelaRecovery(true);
      }
      if (event === 'SIGNED_OUT') {
        try { sessionStorage.removeItem(CHAVE_RECOVERY); } catch {}
        setTelaRecovery(false);
      }
      if (event === 'SIGNED_IN')  registrarAuditoria(AUDIT_ACTIONS.LOGIN,  'sistema');
      if (event === 'SIGNED_OUT') registrarAuditoria(AUDIT_ACTIONS.LOGOUT, 'sistema');
      setSessao(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Registra/remove event listeners de inatividade conforme sessão
  useEffect(() => {
    if (!sessao) {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      setAvisoInatividade(false);
      return;
    }
    const eventos = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];
    eventos.forEach(ev => window.addEventListener(ev, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      eventos.forEach(ev => window.removeEventListener(ev, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [sessao, resetIdleTimer]);

  // Session Guard — heartbeat de sessão ativa (registra/remove ao login/logout)
  useEffect(() => {
    if (sessao?.user) {
      iniciarHeartbeat(sessao.user.email);
    } else {
      pararHeartbeat();
    }
    return () => pararHeartbeat();
  }, [sessao?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Retry automático da fila de sincronização — 2026-07-31 Dex
  // Se algum save falhou (rede caiu, RLS bloqueou, etc), o item fica em
  // localStorage e é re-tentado a cada 30s. Também no mount (após login)
  // pra pegar itens que ficaram pendentes de sessão anterior.
  const [pendentesSyncCount, setPendentesSyncCount] = useState(0);
  useEffect(() => {
    if (!sessao?.user) return;
    // Subscribe pra atualizar contador visual
    const unsub = syncQueue.subscribe(list => setPendentesSyncCount(list.length));
    // Flush inicial (5s depois do mount pra não competir com carregamento)
    const timerInitial = setTimeout(async () => {
      const r = await syncQueue.flush((campo, valor, opts) => executarSalvamento(campo, valor, opts));
      if (r.ok > 0) {
        window.dispatchEvent(new CustomEvent('kzero-toast', { detail: {
          mensagem: `✅ ${r.ok} alteração(ões) pendente(s) sincronizada(s) com a nuvem.`,
          tipo: 'sucesso',
        }}));
      }
    }, 5000);
    // Retry periódico
    const timerPeriodic = setInterval(async () => {
      if (syncQueue.getPending().length === 0) return;
      await syncQueue.flush((campo, valor, opts) => executarSalvamento(campo, valor, opts));
    }, 30000);
    return () => {
      unsub();
      clearTimeout(timerInitial);
      clearInterval(timerPeriodic);
    };
  }, [sessao?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Busca perfil do usuário logado e lista de todos os usuários
  // Usa userIdCarregadoRef para não recarregar ao simples refresh de token
  useEffect(() => {
    const uid = sessao?.user?.id ?? null;
    if (!sessao || uid === null) {
      userIdCarregadoRef.current = null;
      setPerfilUsuario(null);
      setTodosUsuarios([]);
      setCarregandoPerfil(false);
      return;
    }
    if (uid === userIdCarregadoRef.current) return; // mesmo usuário, token apenas renovado
    userIdCarregadoRef.current = uid;
    setCarregandoPerfil(true);
    supabase.from('perfis').select('*').eq('id', uid).single()
      .then(({ data: p }) => { setPerfilUsuario(p || null); })
      .catch((e) => console.error('[perfis] erro ao carregar perfil:', e))
      .finally(() => setCarregandoPerfil(false));
    supabase.from('perfis').select('*').order('criado_em')
      .then(({ data: lista }) => setTodosUsuarios(lista || []))
      .catch((e) => console.error('[perfis] erro ao carregar lista:', e));
  }, [sessao]);

  // Cadastro ou edição de aluno/usuário (Pessoas, Usuários) recarrega a lista,
  // para a pessoa nova já aparecer na matrícula da turma e nos seletores.
  useEffect(() => {
    if (!sessao) return;
    const recarregar = () => supabase.from('perfis').select('*').order('criado_em')
      .then(({ data: lista }) => { if (lista) setTodosUsuarios(lista); })
      .catch((e) => console.error('[perfis] erro ao recarregar lista:', e));
    window.addEventListener(EVENTO_USUARIOS_ALTERADOS, recarregar);
    return () => window.removeEventListener(EVENTO_USUARIOS_ALTERADOS, recarregar);
  }, [sessao]);

  // Recarrega perfis + dados compartilhados do Supabase (aplica permissões atualizadas)
  const recarregarSistema = async () => {
    if (!sessao) return;
    try {
      const [{ data: p }, { data: lista }, { data: row }] = await Promise.all([
        supabase.from('perfis').select('*').eq('id', sessao.user.id).single(),
        supabase.from('perfis').select('*').order('criado_em'),
        supabase.from('dados_compartilhados').select('dados').eq('id', 'sistema').single(),
      ]);
      if (p) setPerfilUsuario(p);
      if (lista) setTodosUsuarios(lista);
      if (row?.dados) { const norm = normalizarAgendamentos(row.dados); setData(norm); safeSetCache(norm); }
    } catch (e) {
      console.error('Erro ao recarregar sistema:', e);
    }
  };

  // Carrega dados compartilhados + Realtime
  // Só carrega quando o user.id muda (login/logout), não em refresh de token
  useEffect(() => {
    if (!sessao) return;
    // se já carregamos dados para este usuário, não recarrega (preserva estado dos formulários)
    if (data !== null && sessao.user.id === userIdCarregadoRef.current) return;

    const carregarDados = async () => {
      try {
        const { data: row } = await supabase
          .from('dados_compartilhados')
          .select('dados')
          .eq('id', 'sistema')
          .single();

        if (row?.dados && Object.keys(row.dados).length > 0) {
          const decrypted = normalizarAgendamentos(decryptData(row.dados));
          registrarBase(decrypted);
          setData(decrypted);
          safeSetCache(decrypted);
        } else {
          // Nuvem vazia — busca em todas as chaves locais possíveis (migração)
          const dadosMigrados = buscarDadosLocais();
          if (dadosMigrados) {
            const decrypted = normalizarAgendamentos(decryptData(dadosMigrados));
            setData(decrypted);
            await supabase.from('dados_compartilhados').upsert({
              id: 'sistema',
              dados: encryptData(decrypted),
              atualizado_em: new Date().toISOString(),
              atualizado_por: sessao.user.id,
            });
            safeSetCache(decrypted);
          } else {
            setData(normalizarAgendamentos(backupData.dados));
          }
        }
      } catch {
        const cached = buscarDadosLocais();
        setData(normalizarAgendamentos(cached ? decryptData(cached) : backupData.dados));
      } finally {
        setCarregandoDados(false);
      }
    };

    carregarDados();

    // Realtime — recebe mudanças de outros usuários em tempo real
    const canal = supabase
      .channel('dados_compartilhados')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'dados_compartilhados', filter: 'id=eq.sistema' }, (payload) => {
        if (ignorarRealtimeRef.current) return; // mudança foi nossa, ignora
        if (payload.new?.dados) {
          // Merge: preserva dados locais não enviados ainda, incorpora o que veio da nuvem
          setData(prev => {
            const incoming = decryptData(payload.new.dados);
            // Remove do incoming qualquer campo que tenha escrita pendente local —
            // evita que o realtime sobrescreva edições ainda não sincronizadas.
            const incomingFiltrado = { ...incoming };
            camposPendentesRef.current.forEach(campo => { delete incomingFiltrado[campo]; });
            const merged = normalizarAgendamentos({ ...(prev || backupData.dados), ...incomingFiltrado });
            // A tela passa a conhecer esta versão do servidor — é a base do
            // merge de três vias em calcularDiffLista.
            registrarBase(merged, Object.keys(incomingFiltrado));
            safeSetCache(merged);
            return merged;
          });
        }
      })
      .subscribe();

    return () => supabase.removeChannel(canal);
  }, [sessao]);

  // Backup automático a cada 60 minutos — 2 camadas de proteção:
  //  (a) Supabase Storage bucket "Backups" (rede de segurança durável)
  //  (b) RPC salvar_snapshot_historico → tabela dados_historico (50 slots, restore rápido)
  //
  // Regra: NUNCA aborta o snapshot só porque o Supabase contém '__b64__'. Um snapshot
  // parcial (com anexos stripados mas listas intactas) é infinitamente melhor que
  // ficar sem backup — foi o bug que fez 34 dias sem gravar snapshot e perdemos
  // dados de agendamentos em 28/07/26. Marca 'automatico-parcial' pra rastrear.
  useEffect(() => {
    const verificar = async () => {
      if (!deveDispararBackup()) return;
      try {
        const { data: row } = await supabase
          .from('dados_compartilhados')
          .select('dados')
          .eq('id', 'sistema')
          .single();
        if (!row?.dados) return;
        const dadosReais = decryptData(row.dados);
        const parcial = contemPlaceholderStripado(dadosReais);
        if (parcial) {
          console.warn('[cloudBackup] snapshot parcial: contém __b64__. Listas preservadas, anexos podem estar stripados.');
        }
        const { snapshot } = salvarSnapshot(dadosReais, parcial ? 'automatico-parcial' : 'automatico');
        uploadSnapshotNuvem(snapshot).then((r) => {
          if (r.ok) rotacionarNuvem().catch(() => {});
        }).catch(() => {});
        try {
          const dev = typeof navigator !== 'undefined' ? (navigator.userAgent || '').slice(0, 120) : '';
          await supabase.rpc('salvar_snapshot_historico', {
            p_motivo: parcial ? 'auto_hora_parcial' : 'auto_hora',
            p_device_info: dev,
          });
        } catch (e) {
          console.warn('[historicoDb] snapshot na tabela falhou:', e?.message);
        }
      } catch (e) {
        console.error('[cloudBackup] falha:', e);
      }
    };
    verificar();
    const intervalo = setInterval(verificar, 60 * 1000);
    return () => clearInterval(intervalo);
  }, []);

  // Debounces por campo — cada módulo tem seu próprio timer
  const debouncesCampos = useRef({});

  // Campos protegidos contra overwrite do Realtime enquanto escrita está pendente.
  // Ex: endomarketing — bug de março/2026 onde listas/campanhas sumiam por merge
  // com versão antiga vinda de outras atualizações concorrentes.
  const CAMPOS_ISOLADOS_REALTIME = ['endomarketing', 'agendamentos'];
  // Campos críticos — salvos imediatamente, sem debounce (risco alto de perda)
  // Conteúdo criado pelo usuário (anotações, reembolsos, atas, propostas, notas, auditorias)
  // não pode depender de debounce: se a aba fechar antes dos 2s o dado é perdido.
  const CAMPOS_SALVAMENTO_IMEDIATO = [
    'perfisAcesso', 'anotacoes', 'reembolsos', 'atas', 'propostas', 'notasDebito', 'auditorias', 'oportunidades',
    // Variantes *PorEmpresa carregam o mesmo conteúdo após migração lazy multi-tenant
    'anotacoesPorEmpresa', 'reembolsosPorEmpresa', 'atasPorEmpresa', 'propostasPorEmpresa',
    'notasDebitoPorEmpresa', 'auditoriasPorEmpresa', 'bancoClausulasPorEmpresa',
    // Orçamentos KzeroSystem (Financeiro): salvos via botão "Salvar" do modal.
    // Catálogo NÃO entra aqui — vive só em localStorage (config local, muito
    // grande pra ir a cada tecla pro Supabase, que estava dando 504 em massa).
    'orcamentosKzsPorEmpresa',
    // Precos do catalogo: iam so pro localStorage e sumiam ao trocar de
    // navegador ou de maquina. Vao pro banco no clique do botao Salvar --
    // nao a cada tecla, que era o que dava 504 em massa.
    'catalogoPrecosKzsPorEmpresa',
  ];

  // Listas em que o save vai por-item (RPC lista_sync da mig 017; mig 015 é o
  // fallback item a item). Cada aba só toca no que mudou, então sessão paralela
  // não sobrescreve o trabalho da outra.
  //
  // Histórico: começou só com 'agendamentos' (incidente 28/07/26); 'notasDebito'
  // entrou em 31/07 depois de perder 3 notas de junho (Sadel/Magna/Valge).
  // Ficou nesses dois por meses — e todo o resto seguiu no modelo que reescreve
  // a lista inteira a cada save, que é o que faz o dado sumir.
  //
  // 27/08/2026: estendido a TODOS os campos que são array de itens com `id`,
  // que é o requisito da RPC. Conferido contra o banco: cada um destes tem
  // `id` em 100% dos itens. Fora da lista ficam:
  //   - `fin_categorias` — array de strings, sem id;
  //   - os mapas `*PorEmpresa` — são objeto empresa→lista, não array. É onde
  //     mora o grosso do volume (reembolsosPorEmpresa sozinho tem 2,3 MB) e
  //     precisa de uma RPC própria; ver mig 020.
  const CAMPOS_SAVE_POR_ITEM = [
    'agendamentos', 'notasDebito',
    'clientes', 'anotacoes', 'propostas', 'atas', 'reembolsos', 'fluxoCaixa',
    'perfisAcesso', 'trilhas', 'bancoClausulas', 'oportunidades', 'auditorias',
    'fin_recorrencias', 'fin_quotas',
  ];

  // Mapas empresa→lista que também vão por-item, via mapa_lista_sync (mig 020).
  // Conferido contra o banco: nestes, o valor de cada empresa é array e todo
  // item tem `id`. Ficam de fora `contabilConfigPorEmpresa`,
  // `fin_acertosPorEmpresa`, `fin_mensalidadesPorEmpresa` e
  // `catalogoModulosKzsPorEmpresa`, cujos valores são objetos, não listas.
  const CAMPOS_SAVE_POR_ITEM_MAPA = [
    'reembolsosPorEmpresa', 'anotacoesPorEmpresa', 'fluxoCaixaPorEmpresa',
    'propostasPorEmpresa', 'orcamentosKzsPorEmpresa',
    'fin_quotasPorEmpresa', 'fin_recorrenciasPorEmpresa',
  ];

  // ── Trava otimista por item (mig 022, o padrão do kzerosystem) ────────────
  // Cada item sobe com o `_updated_at` da versão que esta aba viu. O banco só
  // aceita se ainda for a versão dele; se outra pessoa gravou no meio, fica a
  // dela, a desta aba vai para dados_conflitos_log e a tela é avisada.
  //
  // Por que (29/09/2026): uma aba aberta desde de manhã gravou o atendimento
  // de 29/09 da Sadel com as observações vazias, por cima do que a Janaína
  // tinha registrado. O merge de três vias no navegador (76857ea) não
  // protegia a aba que ainda rodava o código antigo; a trava no banco protege.
  //
  // Base: última versão de cada item que ESTA aba recebeu do servidor (carga
  // inicial ou o próprio save). Serve para saber o que a pessoa mudou aqui —
  // item igual à base e diferente do servidor foi mudado por OUTRA pessoa: a
  // tela só adota a versão do servidor, sem subir nada.
  const baseServidorRef = useRef(new Map());
  // Último save ACEITO desta aba, por item: { de, para } = carimbo com que
  // subiu e carimbo que o banco deu. Uma edição feita enquanto esse save
  // estava no ar ainda carrega `de`; ela é continuação da mesma pessoa e sobe
  // com `para` — senão a própria pessoa seria recusada como "outra pessoa".
  // Só vale enquanto o banco estiver EXATAMENTE em `para`: se outra pessoa
  // gravou depois, a base muda e a continuação é recusada como deve (QA,
  // 30/09/2026: save que falhou e voltou pela fila depois da Janaína gravar).
  const carimboEnviadoRef = useRef(new Map());

  const mapaPorId = (lista) => new Map(
    (Array.isArray(lista) ? lista : []).filter(x => x && x.id != null).map(x => [String(x.id), x])
  );

  const registrarBase = (dados, campos = null) => {
    if (!dados || typeof dados !== 'object') return;
    for (const campo of CAMPOS_SAVE_POR_ITEM) {
      if (campos && !campos.includes(campo)) continue;
      if (Array.isArray(dados[campo])) baseServidorRef.current.set(campo, mapaPorId(dados[campo]));
    }
    for (const campo of CAMPOS_SAVE_POR_ITEM_MAPA) {
      if (campos && !campos.includes(campo)) continue;
      const mapa = dados[campo];
      if (!mapa || typeof mapa !== 'object' || Array.isArray(mapa)) continue;
      for (const [empresa, lista] of Object.entries(mapa)) {
        if (Array.isArray(lista)) baseServidorRef.current.set(`${campo}/${empresa}`, mapaPorId(lista));
      }
    }
  };

  // Compara sem o carimbo e sem depender da ordem das chaves (o jsonb reordena).
  const semCarimbo = (it) => { const { _updated_at, ...r } = it || {}; return r; };
  const ordenado = (v) => Array.isArray(v)
    ? v.map(ordenado)
    : (v && typeof v === 'object'
        ? Object.keys(v).sort().reduce((o, k) => { o[k] = ordenado(v[k]); return o; }, {})
        : v);
  const mesmoValor = (a, b) => JSON.stringify(ordenado(a)) === JSON.stringify(ordenado(b));
  const igualItem = (a, b) => mesmoValor(semCarimbo(a), semCarimbo(b));
  const carimbo = (it) => it?._updated_at ?? null;

  // Depois do save, a tela adota o item COMO FICOU NO BANCO (`gravados`, que a
  // RPC devolve — aceito ou recusado). Por cima dele só vai o que a pessoa
  // mudou na tela enquanto o save estava no ar: campo diferente do que foi
  // enviado E do que o servidor tinha antes.
  //
  // Nunca "carimbo novo sobre o que está na tela": se a tela não tem o que foi
  // gravado (gravação pendente reenviada depois de um F5), o próximo save
  // subiria a tela velha com carimbo válido e apagaria o que acabou de ser
  // gravado — sem conflito nenhum (QA, 30/09/2026).
  //
  // `doServidor`: itens que outra pessoa mudou e esta aba não — só adota.
  // `excluidos`: recusados porque alguém excluiu — saem da tela.
  const aposSalvar = (rotulo, campo, empresa, { gravados = new Map(), pares = new Map(), doServidor = [], excluidos = [], recusados = [] } = {}) => {
    const servidorPorId = mapaPorId(doServidor);
    const semVolta = new Set(excluidos.map(String));
    if (!gravados.size && !servidorPorId.size && !semVolta.size) return;
    let base = baseServidorRef.current.get(rotulo);
    if (!base) { base = new Map(); baseServidorRef.current.set(rotulo, base); }
    let enviados = carimboEnviadoRef.current.get(rotulo);
    if (!enviados) { enviados = new Map(); carimboEnviadoRef.current.set(rotulo, enviados); }
    for (const id of semVolta) { base.delete(id); enviados.delete(id); }
    const recusadosIds = new Set(recusados.map(String));
    for (const [id, s] of servidorPorId) { base.set(id, s); enviados.delete(id); }
    for (const [id, g] of gravados) {
      base.set(id, g);
      // Só item ACEITO pode ser continuado com o carimbo novo. No recusado, o
      // carimbo do banco é o da outra pessoa: continuar por cima dele seria
      // exatamente o que a trava existe para impedir.
      if (recusadosIds.has(id)) enviados.delete(id);
      else enviados.set(id, { de: carimbo(pares.get(id)?.enviado), para: carimbo(g) });
    }
    const ajusta = (lista) => Array.isArray(lista)
      ? lista
          .filter(x => !(x && x.id != null && semVolta.has(String(x.id))))
          .map(x => {
            if (!x || x.id == null) return x;
            const id = String(x.id);
            if (servidorPorId.has(id)) return servidorPorId.get(id);
            const g = gravados.get(id);
            if (!g) return x;
            const p = pares.get(id);
            const out = { ...g };
            for (const k of new Set([...Object.keys(x), ...Object.keys(g)])) {
              if (k === '_updated_at') continue;
              const mudouNoAr = p && !mesmoValor(x[k], p.enviado?.[k]) && !mesmoValor(x[k], p.antes?.[k]);
              if (mudouNoAr) { if (k in x) out[k] = x[k]; else delete out[k]; }
            }
            return out;
          })
      : lista;
    setData(prev => {
      if (!prev) return prev;
      const novo = empresa == null
        ? { ...prev, [campo]: ajusta(prev[campo]) }
        : { ...prev, [campo]: { ...(prev[campo] || {}), [empresa]: ajusta(prev[campo]?.[empresa]) } };
      safeSetCache(novo);
      return novo;
    });
  };

  const avisarConflitos = (qtd) => {
    if (!qtd) return;
    window.dispatchEvent(new CustomEvent('kzero-toast', { detail: {
      mensagem: `⚠️ Outra pessoa alterou ${qtd === 1 ? 'um registro' : `${qtd} registros`} que você também editou. A versão dela foi mantida e a sua tela foi atualizada — confira e refaça sua alteração se precisar.`,
      tipo: 'aviso',
      duracao: 15000,
    }}));
  };

  // Diferença entre a lista desta aba e a do servidor: o que sobe, o que é
  // apagado e o que a tela só precisa adotar do servidor.
  //
  // EXCLUSÃO NUNCA É DEDUZIDA POR AUSÊNCIA.
  //
  // Item que está no servidor e não está na lista local só é apagado se o id
  // estiver em `remocoes` — ids que a pessoa excluiu de fato nesta aba, captados
  // em set(campo) no momento da exclusão.
  //
  // Até 17/09/2026 a regra era "falta aqui e é mais velho que o item mais novo
  // que conheço → apagar". Em 14/09 a syncQueue reenviou, no login da Isabela,
  // uma lista de agenda gravada no localStorage dias antes: o diff apagou 51
  // lançamentos (setembro inteiro, férias, Natal) e ressuscitou um já excluído.
  // O banco também passou a recusar remoção de mais de 10 itens por gravação e
  // a guardar todo item removido em dados_remocoes_log (mig 021).
  //
  // `somenteNovos`: gravação pendente antiga (ver executarSalvamento). A lista
  // dela pode estar velha — sobe só item que o servidor ainda não tem.
  const calcularDiffLista = (rotulo, listaLocal, listaServer, { remocoes = null, somenteNovos = false } = {}) => {
    const mapLocal = mapaPorId(listaLocal);
    const mapServer = mapaPorId(listaServer);
    const mapBase = baseServidorRef.current.get(rotulo);
    const mapEnviado = carimboEnviadoRef.current.get(rotulo);

    const upserts = [];
    const pares = new Map();
    const doServidor = [];
    for (const [id, local] of mapLocal) {
      const antigo = mapServer.get(id);
      if (!antigo) { upserts.push(local); pares.set(id, { enviado: local, antes: null }); continue; }
      if (igualItem(antigo, local)) continue;
      const base = mapBase?.get(id);
      // Não mudou nesta aba: quem mudou foi outra pessoa. Só adota.
      if (somenteNovos || (base && igualItem(base, local))) { doServidor.push(antigo); continue; }
      // Editado aqui antes do save anterior DESTA aba voltar: continua dele.
      let item = local;
      const ultimo = mapEnviado?.get(id);
      if (ultimo && carimbo(local) === ultimo.de && carimbo(antigo) === ultimo.para && carimbo(local) !== ultimo.para) {
        item = { ...local, _updated_at: ultimo.para };
      }
      // Sobe com o carimbo que esta aba viu — o banco confere.
      upserts.push(item);
      pares.set(id, { enviado: item, antes: antigo });
    }

    const excluidosAqui = new Set((Array.isArray(remocoes) ? remocoes : []).map(String));
    const deletes = [];
    let ignorados = 0;
    for (const id of mapServer.keys()) {
      if (mapLocal.has(id)) continue;
      if (excluidosAqui.has(id)) deletes.push(id);
      else ignorados++;
    }
    if (ignorados) {
      console.info(`[${rotulo}] ${ignorados} item(ns) do servidor ausentes nesta aba preservados (não foram excluídos aqui).`);
    }
    return { upserts, deletes, pares, doServidor };
  };

  // Resposta da RPC (mig 022): `itens` = como cada item enviado ficou no banco;
  // `conflitos_ids` = os recusados. Recusado que não voltou em `itens` foi
  // excluído por outra pessoa.
  const lerResultado = (campo, resumo) => {
    const brutos = resumo?.itens && typeof resumo.itens === 'object' ? Object.values(resumo.itens) : [];
    const gravados = mapaPorId(brutos.length ? (decryptData({ [campo]: brutos })?.[campo] || brutos) : []);
    const conflitos = (Array.isArray(resumo?.conflitos_ids) ? resumo.conflitos_ids : []).map(String);
    return { gravados, recusados: conflitos, excluidos: conflitos.filter(id => !gravados.has(id)), qtdConflitos: conflitos.length };
  };

  // Uma gravação por campo de cada vez. Duas seguidas em paralelo liam a mesma
  // versão do servidor e a segunda voltava como conflito com a primeira — da
  // mesma pessoa. Em fila, a segunda já parte do que a primeira gravou.
  const filaPorCampoRef = useRef({});
  const emFilaPorCampo = (chave, fn) => {
    const anterior = filaPorCampoRef.current[chave] || Promise.resolve();
    const atual = anterior.catch(() => {}).then(fn);
    filaPorCampoRef.current[chave] = atual;
    return atual;
  };

  // Ids que saíram de uma lista entre o valor anterior e o novo, na própria aba.
  // Lista simples → [ids]; mapa por empresa → { empresa: [ids] }.
  const idsRemovidos = (campo, antes, depois) => {
    const ids = (arr) => new Set((Array.isArray(arr) ? arr : []).filter(x => x && x.id != null).map(x => String(x.id)));
    const diff = (a, b) => { const sb = ids(b); return [...ids(a)].filter(id => !sb.has(id)); };
    if (CAMPOS_SAVE_POR_ITEM.includes(campo)) {
      const r = diff(antes, depois);
      return r.length ? r : null;
    }
    if (CAMPOS_SAVE_POR_ITEM_MAPA.includes(campo)) {
      if (!antes || typeof antes !== 'object' || Array.isArray(antes)) return null;
      const d = (depois && typeof depois === 'object' && !Array.isArray(depois)) ? depois : {};
      const out = {};
      for (const [empresa, lista] of Object.entries(antes)) {
        const r = diff(lista, d[empresa]);
        if (r.length) out[empresa] = r;
      }
      return Object.keys(out).length ? out : null;
    }
    return null;
  };

  // Gravação que ficou pendente na fila por mais que isto é tratada como
  // possivelmente velha: só inclui itens novos.
  const IDADE_MAX_PENDENTE_MS = 2 * 60 * 60 * 1000;

  // Mapas `campo -> { empresa: [itens] }`. Depois do multi-tenant é onde mora o
  // grosso do conteúdo — `reembolsosPorEmpresa` sozinho tem 2,3 MB — e eles
  // seguiam no caminho que reescreve o campo INTEIRO: salvar um reembolso de
  // uma empresa reescrevia a lista de todas, apagando o que outra sessão tinha
  // gravado em qualquer uma delas. A RPC mapa_lista_sync (mig 020) toca só a
  // lista da empresa que mudou.
  const salvarMapaPorItem = (campo, mapaLocal, opcoes = {}) =>
    emFilaPorCampo(campo, () => salvarMapaPorItemAgora(campo, mapaLocal, opcoes));

  const salvarMapaPorItemAgora = async (campo, mapaLocal, { remocoes = null, somenteNovos = false } = {}) => {
    if (!mapaLocal || typeof mapaLocal !== 'object' || Array.isArray(mapaLocal)) {
      throw new Error(`campo "${campo}" não é um mapa por empresa`);
    }
    const { data: soCampo, error: errCampo } = await supabase.rpc('lista_get', { p_campo: campo });
    if (errCampo) throw errCampo;
    const decifrado = soCampo ? decryptData({ [campo]: soCampo })?.[campo] : null;
    const mapaServer = (decifrado && typeof decifrado === 'object' && !Array.isArray(decifrado))
      ? decifrado
      : {};

    let upserts = 0;
    let deletes = 0;
    let conflitos = 0;
    for (const [empresa, listaLocal] of Object.entries(mapaLocal)) {
      if (!Array.isArray(listaLocal)) continue;
      const rotulo = `${campo}/${empresa}`;
      const listaServer = Array.isArray(mapaServer[empresa]) ? mapaServer[empresa] : [];
      const diff = calcularDiffLista(rotulo, listaLocal, listaServer, {
        remocoes: remocoes && !Array.isArray(remocoes) ? remocoes[empresa] : null,
        somenteNovos,
      });
      if (diff.upserts.length === 0 && diff.deletes.length === 0) {
        aposSalvar(rotulo, campo, empresa, { doServidor: diff.doServidor });
        continue;
      }

      const itensCripto = diff.upserts.length > 0
        ? encryptData({ [campo]: diff.upserts })[campo]
        : [];
      const { data: resumo, error } = await supabase.rpc('mapa_lista_sync', {
        p_campo: campo,
        p_empresa: empresa,
        p_itens: itensCripto,
        p_remover: diff.deletes,
      });
      if (error) throw error;
      const r = lerResultado(campo, resumo);
      aposSalvar(rotulo, campo, empresa, {
        gravados: r.gravados, pares: diff.pares, doServidor: diff.doServidor,
        excluidos: r.excluidos, recusados: r.recusados,
      });
      conflitos += r.qtdConflitos;
      upserts += resumo?.upserts ?? diff.upserts.length;
      deletes += resumo?.deletes ?? diff.deletes.length;
    }
    avisarConflitos(conflitos);
    // Empresa que existe no servidor e não no mapa local NÃO é removida: seria
    // o mesmo erro de deduzir deleção por ausência, só que apagando a lista
    // inteira de um cliente de uma vez.
    return { ok: true, upserts, deletes, conflitos };
  };

  // Compara duas versões da lista e faz add/edit/delete cirúrgicos no server.
  // Retorna { ok, upserts, deletes } — erro sobe pro chamador.
  const salvarListaPorItem = (campo, listaLocal, opcoes = {}) =>
    emFilaPorCampo(campo, () => salvarListaPorItemAgora(campo, listaLocal, opcoes));

  const salvarListaPorItemAgora = async (campo, listaLocal, { remocoes = null, somenteNovos = false } = {}) => {
    if (!Array.isArray(listaLocal)) throw new Error(`campo "${campo}" não é array`);

    // Lê só a lista deste campo (mig 017). O `select dados` de antes trazia os
    // 2,2 MB de todos os módulos pra comparar uma agenda — em toda gravação.
    let listaServer = [];
    const { data: soCampo, error: errCampo } = await supabase.rpc('lista_get', { p_campo: campo });
    if (!errCampo) {
      const decifrado = soCampo ? decryptData({ [campo]: soCampo })?.[campo] : null;
      listaServer = Array.isArray(decifrado) ? decifrado : [];
    } else {
      // Banco sem a mig 017 — volta a ler a linha inteira.
      console.warn('[lista_get] indisponível, lendo a linha inteira:', errCampo?.message);
      const { data: row, error: errRead } = await supabase
        .from('dados_compartilhados')
        .select('dados')
        .eq('id', 'sistema')
        .single();
      if (errRead) throw errRead;
      const dadosAtuais = row?.dados ? decryptData(row.dados) : {};
      listaServer = Array.isArray(dadosAtuais?.[campo]) ? dadosAtuais[campo] : [];
    }

    const { upserts, deletes, pares, doServidor } = calcularDiffLista(campo, listaLocal, listaServer, {
      remocoes: Array.isArray(remocoes) ? remocoes : null,
      somenteNovos,
    });

    if (upserts.length === 0 && deletes.length === 0) {
      aposSalvar(campo, campo, null, { doServidor });
      return { ok: true, upserts: 0, deletes: 0 };
    }

    // Caminho novo (mig 017): a lista inteira vai numa chamada só.
    //
    // Antes cada item era uma RPC, disparadas de 6 em 6 em paralelo. Como todas
    // pegam `FOR UPDATE` da MESMA linha (dados_compartilhados.id='sistema', 2,2 MB
    // de JSONB), o paralelismo só empilhava conexões esperando o mesmo lock — e
    // cada uma reescrevia a coluna inteira. Com as outras abas, o heartbeat e o
    // realtime junto, o pool do Supabase estourava e o save voltava HTTP 504
    // "Timed out acquiring connection from connection pool": pra quem estava
    // usando, o calendário simplesmente não salvava.
    const upsertsCripto = upserts.length > 0
      ? encryptData({ [campo]: upserts })[campo]
      : [];
    const { data: resumo, error: errSync } = await supabase.rpc('lista_sync', {
      p_campo: campo,
      p_itens: upsertsCripto,
      p_remover: deletes,
    });
    if (!errSync) {
      const r = lerResultado(campo, resumo);
      aposSalvar(campo, campo, null, {
        gravados: r.gravados, pares, doServidor, excluidos: r.excluidos, recusados: r.recusados,
      });
      avisarConflitos(r.qtdConflitos);
      return {
        ok: true,
        upserts: resumo?.upserts ?? upserts.length,
        deletes: resumo?.deletes ?? deletes.length,
        conflitos: r.qtdConflitos,
      };
    }
    // Banco ainda sem a mig 017 (PGRST202 = função não encontrada): cai no
    // caminho antigo, mas em fila de 1 — o paralelismo aqui nunca ajudou.
    console.warn('[lista_sync] indisponível, usando save por item:', errSync?.message);

    const erros = [];
    const gravados = new Map();
    const recusados = [];
    const runLimited = async (jobs, limit = 1) => {
      let i = 0;
      const workers = Array.from({ length: Math.min(limit, jobs.length) }, async () => {
        while (i < jobs.length) { const idx = i++; try { await jobs[idx](); } catch (e) { erros.push(e); } }
      });
      await Promise.all(workers);
    };
    const jobs = [
      ...upserts.map(item => async () => {
        const itemCripto = encryptData({ [campo]: [item] })[campo][0];
        const { data: gravado, error } = await supabase.rpc('lista_item_upsert', { p_campo: campo, p_item: itemCripto });
        if (error) throw error;
        if (!gravado) return;
        // A 022 devolve o item como ficou no banco: se não é o enviado, foi recusado.
        const noBanco = decryptData({ [campo]: [gravado] })?.[campo]?.[0] || gravado;
        gravados.set(String(item.id), noBanco);
        if (!igualItem(noBanco, item)) recusados.push(String(item.id));
      }),
      ...deletes.map(id => async () => {
        const { error } = await supabase.rpc('lista_item_delete', { p_campo: campo, p_id: id });
        if (error) throw error;
      }),
    ];
    await runLimited(jobs, 1);

    if (erros.length) {
      const e = new Error(`${erros.length} erro(s) em lista_item_* pra "${campo}": ${erros[0]?.message || 'desconhecido'}`);
      e.parciais = erros;
      throw e;
    }
    aposSalvar(campo, campo, null, { gravados, pares, doServidor, recusados });
    avisarConflitos(recusados.length);
    return { ok: true, upserts: upserts.length - recusados.length, deletes: deletes.length };
  };

  // Executa a escrita no Supabase (extraído para reuso imediato e debounced)
  const executarSalvamento = async (campo, valor, { remocoes = null, ts = null } = {}) => {
    setSincronizando(true);
    const somenteNovos = !!ts && Date.now() - ts > IDADE_MAX_PENDENTE_MS;
    if (somenteNovos) {
      console.warn(`[salvarCampoNuvem] "${campo}" pendente desde ${new Date(ts).toLocaleString('pt-BR')}: só itens novos, sem sobrescrever nem excluir.`);
    }
    const opcoesItem = { remocoes: somenteNovos ? null : remocoes, somenteNovos };
    let valorFinal = valor;
    // Se o valor contem '__b64__' (cache stripado), reidrata do Supabase atual
    // antes de salvar. Evita sobrescrever anexos bons com placeholders.
    if (contemPlaceholderStripado(valor)) {
      try {
        const { data: row } = await supabase
          .from('dados_compartilhados')
          .select('dados')
          .eq('id', 'sistema')
          .single();
        if (row?.dados) {
          const dadosAtuais = decryptData(row.dados);
          const atualDoCampo = dadosAtuais?.[campo];
          valorFinal = reidratarStripped(valor, atualDoCampo);
          if (contemPlaceholderStripado(valorFinal)) {
            // Placeholder que sobreviveu a reidratacao significa que o anexo
            // nao esta em lugar nenhum — nem no cache, nem no servidor. Gravar
            // assim mesmo (como se fazia) carimba a perda no banco. Agora o
            // aviso vai pra pessoa, e o salvamento segue: o texto do registro
            // precisa ser salvo. O anexo em si ja estava perdido antes daqui.
            console.warn(`[salvarCampoNuvem] "${campo}" tem anexo perdido (__b64__) — o registro sera salvo sem ele.`);
            window.dispatchEvent(new CustomEvent('kzero-toast', { detail: {
              mensagem: `⚠️ Um anexo de "${campo}" nao foi encontrado nem no navegador nem na nuvem. O registro foi salvo, mas o arquivo precisa ser anexado de novo.`,
              tipo: 'aviso',
              duracao: 12000,
            }}));
          }
        }
      } catch (e) {
        console.error('[salvarCampoNuvem] falha ao reidratar:', e);
      }
    }
    ignorarRealtimeRef.current = true;
    try {
      // Save por-item pra campos concorrência-sensíveis
      if (CAMPOS_SAVE_POR_ITEM.includes(campo) && Array.isArray(valorFinal)) {
        // SEM FALLBACK para o array inteiro. Até 27/08/2026, quando o save
        // por-item falhava (504, timeout, rede), o código caía no caminho que
        // reescreve a lista COMPLETA — justamente o caminho que apaga o que as
        // outras sessões gravaram. A proteção se desligava sozinha no momento
        // exato de maior risco: banco lento, várias abas abertas.
        //
        // Falhar aqui é melhor: o erro sobe, a pessoa vê o aviso e o item
        // continua na syncQueue (localStorage), que retenta no próximo mount.
        const r = await salvarListaPorItem(campo, valorFinal, opcoesItem);
        console.info(`[salvarCampoNuvem] "${campo}" por-item: +${r.upserts} / -${r.deletes}`);
        return;
      }
      // Mesma proteção, para os mapas empresa→lista. Também sem fallback.
      if (CAMPOS_SAVE_POR_ITEM_MAPA.includes(campo) && valorFinal && typeof valorFinal === 'object' && !Array.isArray(valorFinal)) {
        const r = await salvarMapaPorItem(campo, valorFinal, opcoesItem);
        console.info(`[salvarCampoNuvem] "${campo}" por-item (mapa): +${r.upserts} / -${r.deletes}`);
        return;
      }
      const valorCriptografado = encryptData({ [campo]: valorFinal })[campo];
      // Retry só em timeout do Postgres — JSONB grande às vezes estoura mesmo
      // com statement_timeout de 60s na RPC. Outros erros propagam direto.
      let ultimoErro = null;
      // Exclusão grande confirmada na tela (liberarRemocaoEmMassa): grava pela
      // RPC que libera a trava só nesta transação (mig 027).
      const confirmada = remocaoLiberada(campo);
      for (let tentativa = 0; tentativa < 3; tentativa++) {
        const { error } = await supabase.rpc(confirmada ? 'atualizar_campo_dados_confirmado' : 'atualizar_campo_dados', {
          p_campo: campo,
          p_valor: valorCriptografado,
        });
        if (!error) { ultimoErro = null; break; }
        ultimoErro = error;
        const ehTimeout = /timeout|canceling statement/i.test(error?.message || '');
        if (!ehTimeout) break;
        if (tentativa < 2) await new Promise(r => setTimeout(r, 500 * (tentativa + 1)));
      }
      if (ultimoErro) throw ultimoErro;
      if (confirmada) consumirLiberacao(campo);
    } catch (e) {
      console.error(`[salvarCampoNuvem] falha ao salvar "${campo}":`, e);
      if (syncQueue.ehBloqueioDeRemocao(e)) {
        avisarTelaDesatualizada();
      } else {
        toast.erro(`Falha ao salvar "${campo}" na nuvem: ${e?.message || 'erro desconhecido'}. Tente novamente.`);
      }
      throw e;
    } finally {
      setSincronizando(false);
      setTimeout(() => {
        ignorarRealtimeRef.current = false;
        if (CAMPOS_ISOLADOS_REALTIME.includes(campo)) {
          camposPendentesRef.current.delete(campo);
        }
      }, 3000);
    }
  };

  // A trava do banco recusou a gravação porque ela apagaria muitos itens de uma
  // vez: quase sempre é uma aba aberta antes de outra pessoa (ou um import)
  // acrescentar dados. Nada foi gravado; a pessoa precisa recarregar.
  const avisarTelaDesatualizada = () => {
    window.dispatchEvent(new CustomEvent('kzero-toast', { detail: {
      mensagem: 'Esta tela está desatualizada: houve alterações feitas em outro lugar. Para não apagar dados, a última alteração não foi salva. Recarregue a página (F5) e refaça a alteração.',
      tipo: 'aviso',
      duracao: 20000,
    }}));
  };

  // Salva apenas o campo alterado no Supabase (sem sobrescrever outros campos)
  const salvarCampoNuvem = (campo, valor, remocoes = null) => {
    if (!sessao) return;
    if (debouncesCampos.current[campo]) clearTimeout(debouncesCampos.current[campo]);
    if (CAMPOS_ISOLADOS_REALTIME.includes(campo)) {
      camposPendentesRef.current.add(campo);
    }
    // 2026-07-31 Dex: enfileirar SEMPRE ANTES do debounce. Se usuária
    // recarregar em <2s (Ctrl+Shift+R), o setTimeout NUNCA dispara e o dado
    // se perde. A fila em localStorage é lida no próximo mount e re-tentada.
    // Só o debounce controla a chamada network, mas o dado já está seguro.
    const filaId = syncQueue.enqueue(campo, valor, remocoes);
    // Campos críticos salvam imediatamente, sem debounce
    if (CAMPOS_SALVAMENTO_IMEDIATO.includes(campo) || CAMPOS_SAVE_POR_ITEM.includes(campo)) {
      salvarComFeedbackComFilaId(campo, valor, filaId);
      return;
    }
    debouncesCampos.current[campo] = setTimeout(() => {
      salvarComFeedbackComFilaId(campo, valor, filaId);
    }, 2000);
  };

  // Wrapper que reusa filaId (evita enqueue duplo — já entrou na fila em salvarCampoNuvem)
  const salvarComFeedbackComFilaId = async (campo, valor, filaId) => {
    try {
      // Remoções acumuladas na fila (inclui exclusão de save anterior que falhou).
      const pendente = syncQueue.getPending().find(e => e.id === filaId);
      await executarSalvamento(campo, valor, { remocoes: pendente?.remocoes || null });
      syncQueue.remove(filaId);
    } catch (e) {
      if (syncQueue.ehBloqueioDeRemocao(e)) {
        // Tela desatualizada: o aviso já saiu em executarSalvamento; não volta para a fila.
        syncQueue.remove(filaId);
        return;
      }
      syncQueue.marcarErro(filaId, e?.message);
      const msg = e?.message || 'erro desconhecido';
      window.dispatchEvent(new CustomEvent('kzero-toast', { detail: {
        mensagem: `⚠️ Falha ao salvar "${campo}" na nuvem: ${msg}. Guardei localmente e vou tentar de novo em 30s.`,
        tipo: 'aviso',
        duracao: 12000,
      }}));
      console.error(`[salvarCampoNuvem] "${campo}" FALHOU (na fila pra retry):`, e);
    }
  };

  const salvar = (novoData) => {
    setData(novoData);
    safeSetCache(novoData);
    // Salva o objeto inteiro apenas no backup/restore
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSincronizando(true);
      ignorarRealtimeRef.current = true;
      try {
        let dataFinal = novoData;
        // Se vier com '__b64__', reidrata cada campo com a versao do Supabase
        if (contemPlaceholderStripado(novoData)) {
          const { data: row } = await supabase.from('dados_compartilhados').select('dados').eq('id','sistema').single();
          if (row?.dados) {
            const atuais = decryptData(row.dados);
            dataFinal = { ...novoData };
            for (const k of Object.keys(novoData)) {
              if (contemPlaceholderStripado(novoData[k])) {
                dataFinal[k] = reidratarStripped(novoData[k], atuais?.[k]);
              }
            }
          }
        }
        await supabase.from('dados_compartilhados').upsert({
          id: 'sistema',
          dados: encryptData(dataFinal),
          atualizado_em: new Date().toISOString(),
          atualizado_por: sessao.user.id,
        });
      } catch {
        window.dispatchEvent(new CustomEvent('kzero-toast', { detail: {
          mensagem: '⚠️ Falha ao sincronizar com a nuvem. Dados salvos localmente.',
          tipo: 'aviso',
        }}));
      } finally {
        setSincronizando(false);
        setTimeout(() => { ignorarRealtimeRef.current = false; }, 3000);
      }
    }, 2000);
  };

  // set(campo) atualiza só aquele campo — suporta valor direto ou função updater (prev => ...)
  const set = (campo) => (valorOuFn) => {
    setData(prev => {
      const base = prev || backupData.dados;
      const novoValor = typeof valorOuFn === 'function' ? valorOuFn(base[campo]) : valorOuFn;
      const novoData = { ...base, [campo]: novoValor };
      safeSetCache(novoData);
      // Exclusão real = o que saiu da lista NESTA mudança. É a única que o
      // save por item aceita (ver calcularDiffLista).
      salvarCampoNuvem(campo, novoValor, idsRemovidos(campo, base[campo], novoValor));
      return novoData;
    });
  };

  const handleLogout = async () => {
    await removerSessao();
    pararHeartbeat();
    await supabase.auth.signOut();
  };

  // Módulos permitidos pelo perfil de acesso do usuário logado
  const navFiltrado = useMemo(() => {
    const papel = perfilUsuario?.papel;
    // Admin vê tudo, sem restrição
    if (papel === 'admin') return NAV;
    // Usuário com perfil de acesso configurado — segue exclusivamente as permissões do perfil
    if (perfilUsuario?.perfil_acesso_id) {
      const pa = (data?.perfisAcesso || []).find(p => String(p.id) === String(perfilUsuario.perfil_acesso_id));
      if (pa) {
        // Compat: 'ensino' agrega Treinamentos+Trilhas; perfis antigos mantêm acesso
        // se tinham qualquer um dos dois antes da consolidação.
        const verPermissao = (id) => {
          if (id === 'ensino') {
            return !!(pa.permissoes?.ensino?.ver || pa.permissoes?.treinamentos?.ver || pa.permissoes?.trilhas?.ver);
          }
          return !!pa.permissoes?.[id]?.ver;
        };
        const verModulo = (id) => {
          if (id === 'ensino') {
            return pa.modulos.includes('ensino') || pa.modulos.includes('treinamentos') || pa.modulos.includes('trilhas');
          }
          return pa.modulos.includes(id);
        };
        // Formato novo: permissoes{}
        if (pa.permissoes) return NAV.filter(n => verPermissao(n.id));
        // Formato antigo: modulos[]
        if (Array.isArray(pa.modulos)) return NAV.filter(n => verModulo(n.id));
      }
    }
    // Sem perfil configurado — sem acesso (admin deve atribuir perfil)
    return [];
  }, [perfilUsuario, data?.perfisAcesso]);

  // Redireciona para a primeira página permitida quando o perfil carrega
  useEffect(() => {
    if (!navFiltrado.length) return;
    const permitidas = navFiltrado.map(n => n.id);
    if (!permitidas.includes(pagina)) setPagina(permitidas[0]);
  }, [navFiltrado]);

  // Menu mobile (hamburger) e badges por módulo
  const [menuAberto, setMenuAberto] = useState(false);
  const paginaAtual = navFiltrado.find(n => n.id === pagina) || navFiltrado[0] || { emoji: '📋', label: 'Kzero' };
  const badgesModulos = useMemo(() => {
    const hoje = new Date().toISOString().split('T')[0];
    const ags = (data?.agendamentos || []).filter(a => a.data === hoje).length;
    const fluxo = data?.fluxoCaixa || [];
    const pendentesCat = fluxo.filter(f => !f.categoria || f.categoria === 'A Classificar').length;
    return { agenda: ags, financeiro: pendentesCat };
  }, [data?.agendamentos, data?.fluxoCaixa]);

  // Profissional do usuário logado (para filtro nos Documentos)
  const profissionalAtual = perfilUsuario?.profissional || '';
  const isAdmin = perfilUsuario?.papel === 'admin';

  // Permissões granulares do usuário logado
  const permissoesUsuario = useMemo(() => {
    if (isAdmin) return null; // admin não tem restrições
    const pa = (data?.perfisAcesso || []).find(p => String(p.id) === String(perfilUsuario?.perfil_acesso_id));
    return pa?.permissoes || null;
  }, [isAdmin, perfilUsuario, data?.perfisAcesso]);

  if (carregandoSessao || (sessao && (carregandoDados || carregandoPerfil))) {
    return (
      <div className="loading-screen">
        <img src={logoKzeroColor} alt="Kzero" style={{ height: 60 }} />
        <p style={{ color: '#5f5874', marginTop: 12 }}>Carregando...</p>
      </div>
    );
  }

  if (!sessao) return <Login />;

  // Chegou pelo link de redefinição/boas-vindas: só entra no sistema depois de definir a senha.
  if (telaRecovery) {
    return <RedefinirSenha onConcluido={() => {
      try { sessionStorage.removeItem(CHAVE_RECOVERY); } catch {}
      setTelaRecovery(false);
    }} />;
  }

  const d = data || backupData.dados;

  // Email do usuário logado (Supabase Auth) — usado pelo EmpresaProvider para
  // filtrar a lista de empresas visíveis e liberar UI de super-admin.
  const emailLogado = (perfilUsuario?.email || sessao?.user?.email || '').trim();

  // Banner ISO 27001 A.9.4.2 — aviso de inatividade
  const BannerInatividade = avisoInatividade ? (
    <div style={{ position:'fixed', top:0, left:0, right:0, zIndex:9999, background:'#b45309', color:'#fff', textAlign:'center', padding:'10px 16px', fontSize:13, fontWeight:600 }}>
      ⚠️ Sua sessão será encerrada em 2 minutos por inatividade. Mova o mouse ou pressione uma tecla para continuar.
    </div>
  ) : null;

  // A página só aparece se o perfil libera o módulo. Antes a Agenda (página
  // inicial) ficava visível até o perfil carregar e redirecionar: o aluno via
  // a agenda inteira piscar a cada F5 — e, sem perfil de acesso, ficava nela.
  const permitidas = new Set(navFiltrado.map(n => n.id));
  const show = (id) => ({ style: { display: pagina === id && permitidas.has(id) ? undefined : 'none' } });

  const renderPaginas = () => (
    <>
      <div {...show('painel')}><Painel data={d} setConfigEstagiaria={set('configEstagiaria')} setFluxoCaixa={set('fluxoCaixa')} setFluxoCaixaPorEmpresa={set('fluxoCaixaPorEmpresa')} isAdmin={isAdmin} /></div>
      <div {...show('agenda')}><Calendario agendamentos={d.agendamentos} setAgendamentos={set('agendamentos')} clientes={d.clientes} profissionais={profissionais} permissoesUsuario={permissoesUsuario} isAdmin={isAdmin} anotacoes={d.anotacoes} setAnotacoes={set('anotacoes')} anotacoesPorEmpresa={d.anotacoesPorEmpresa} setAnotacoesPorEmpresa={set('anotacoesPorEmpresa')} /></div>
      <div {...show('pessoas')}><Pessoas clientes={d.clientes} setClientes={set('clientes')} perfisAcesso={d.perfisAcesso||[]} setPerfisAcesso={set('perfisAcesso')} isAdmin={isAdmin} onAplicarPerfis={recarregarSistema} /></div>
      <div {...show('financeiro')}><FinanceiroAvancado fluxoCaixa={d.fluxoCaixa} setFluxoCaixa={set('fluxoCaixa')} fluxoCaixaPorEmpresa={d.fluxoCaixaPorEmpresa} setFluxoCaixaPorEmpresa={set('fluxoCaixaPorEmpresa')} contasReceber={d.contasReceber || []} setContasReceber={set('contasReceber')} contasReceberPorEmpresa={d.contasReceberPorEmpresa} setContasReceberPorEmpresa={set('contasReceberPorEmpresa')} categorias={d.fin_categorias} setCategorias={set('fin_categorias')} categoriasPorEmpresa={d.fin_categoriasPorEmpresa} setCategoriasPorEmpresa={set('fin_categoriasPorEmpresa')} recorrencias={d.fin_recorrencias || []} setRecorrencias={set('fin_recorrencias')} recorrenciasPorEmpresa={d.fin_recorrenciasPorEmpresa} setRecorrenciasPorEmpresa={set('fin_recorrenciasPorEmpresa')} quotas={d.fin_quotas || []} setQuotas={set('fin_quotas')} quotasPorEmpresa={d.fin_quotasPorEmpresa} setQuotasPorEmpresa={set('fin_quotasPorEmpresa')} acertosDiv={d.fin_acertos || {}} setAcertosDiv={set('fin_acertos')} acertosDivPorEmpresa={d.fin_acertosPorEmpresa} setAcertosDivPorEmpresa={set('fin_acertosPorEmpresa')} agendamentos={d.agendamentos} clientes={d.clientes} reembolsos={d.reembolsos || []} reembolsosPorEmpresa={d.reembolsosPorEmpresa} contabilConfig={d.contabilConfig || {}} contasPagar={d.fin_contasPagar || []} setContasPagar={set('fin_contasPagar')} contasPagarPorEmpresa={d.fin_contasPagarPorEmpresa} setContasPagarPorEmpresa={set('fin_contasPagarPorEmpresa')} mensalidadesPorEmpresa={d.fin_mensalidadesPorEmpresa} setMensalidadesPorEmpresa={set('fin_mensalidadesPorEmpresa')} orcamentosKzsPorEmpresa={d.orcamentosKzsPorEmpresa} setOrcamentosKzsPorEmpresa={set('orcamentosKzsPorEmpresa')} catalogoPrecosKzsPorEmpresa={d.catalogoPrecosKzsPorEmpresa} setCatalogoPrecosKzsPorEmpresa={set('catalogoPrecosKzsPorEmpresa')} itensPadraoOrcKzsPorEmpresa={d.itensPadraoOrcKzsPorEmpresa} setItensPadraoOrcKzsPorEmpresa={set('itensPadraoOrcKzsPorEmpresa')} /></div>
      <div {...show('documentos')}><Documentos anotacoes={d.anotacoes} setAnotacoes={set('anotacoes')} anotacoesPorEmpresa={d.anotacoesPorEmpresa} setAnotacoesPorEmpresa={set('anotacoesPorEmpresa')} reembolsos={d.reembolsos} setReembolsos={set('reembolsos')} reembolsosPorEmpresa={d.reembolsosPorEmpresa} setReembolsosPorEmpresa={set('reembolsosPorEmpresa')} atas={d.atas} setAtas={set('atas')} atasPorEmpresa={d.atasPorEmpresa} setAtasPorEmpresa={set('atasPorEmpresa')} propostas={d.propostas || []} setPropostas={set('propostas')} propostasPorEmpresa={d.propostasPorEmpresa} setPropostasPorEmpresa={set('propostasPorEmpresa')} auditorias={d.auditorias || []} setAuditorias={set('auditorias')} auditoriasPorEmpresa={d.auditoriasPorEmpresa} setAuditoriasPorEmpresa={set('auditoriasPorEmpresa')} bancoClausulas={d.bancoClausulas || []} setBancoClausulas={set('bancoClausulas')} bancoClausulasPorEmpresa={d.bancoClausulasPorEmpresa} setBancoClausulasPorEmpresa={set('bancoClausulasPorEmpresa')} notasDebito={d.notasDebito || []} setNotasDebito={set('notasDebito')} notasDebitoPorEmpresa={d.notasDebitoPorEmpresa} setNotasDebitoPorEmpresa={set('notasDebitoPorEmpresa')} clientes={d.clientes} profissionais={profissionais} profissionalAtual={profissionalAtual} isAdmin={isAdmin} /></div>
      <div {...show('prospeccao')}><Prospeccao oportunidades={d.oportunidades || []} setOportunidades={set('oportunidades')} clientes={d.clientes} usuarios={todosUsuarios} profissionalAtual={profissionalAtual} isAdmin={isAdmin} propostas={d.propostas || []} setPropostas={set('propostas')} propostasPorEmpresa={d.propostasPorEmpresa} setPropostasPorEmpresa={set('propostasPorEmpresa')} orcamentosKzsPorEmpresa={d.orcamentosKzsPorEmpresa} /></div>
      <div {...show('licitacoes')}><Licitacoes profissionalAtual={profissionalAtual} isAdmin={isAdmin} /></div>
      <div {...show('relatorios')}><Relatorios data={d} logoKzero={logoKzeroColor} setFluxoCaixa={set('fluxoCaixa')} setFluxoCaixaPorEmpresa={set('fluxoCaixaPorEmpresa')} setContabilConfigPorEmpresa={set('contabilConfigPorEmpresa')} isAdmin={isAdmin} /></div>
      <div {...show('ensino')}><Ensino usuarioAtual={perfilUsuario} isAdmin={isAdmin} usuarios={todosUsuarios} agendamentos={d.agendamentos} setAgendamentos={set('agendamentos')} dadosTr={d.tr_dados || {}} setDadosTr={set('tr_dados')} trilhas={d.trilhas||[]} setTrilhas={set('trilhas')} /></div>
      <div {...show('marketing')}><Marketing marketing={d.marketing || {}} setMarketing={set('marketing')} endomarketing={d.endomarketing || {}} setEndomarketing={set('endomarketing')} /></div>
      <div {...show('diagnosticoNR1')}><DiagnosticoNR1 dadosNR1={d.dadosNR1||{}} setDadosNR1={set('dadosNR1')} usuarioAtual={perfilUsuario} isAdmin={isAdmin} usuarios={todosUsuarios} perfisAcesso={d.perfisAcesso||[]} /></div>
      <div {...show('backup')}><Backup data={d} onRestore={(d) => salvar(d)} /></div>
    </>
  );

  const nomeUsuario = perfilUsuario?.nome || sessao?.user?.email || 'Usuário';
  const mostrarWebmail =
    (perfilUsuario?.email || sessao?.user?.email || '').toLowerCase().includes('kzero') ||
    (perfilUsuario?.empresa || '').toLowerCase().includes('kzero');
  const nomePortal = perfilUsuario?.papel === 'aluno' || window.location.hostname.startsWith('aluno.')
    ? 'Portal do Aluno' : 'Portal do Consultor';

  return (
    <EmpresaProvider emailUsuario={emailLogado}>
    <div className="app-dark">
      {BannerInatividade}
      <Toaster />
      {/* Modal Alterar Senha */}
      {modalSenha && <ModalAlterarSenha onFechar={() => setModalSenha(false)} perfilUsuario={perfilUsuario} sessao={sessao} />}

      {/* Menu lateral (computador). No celular vira a gaveta do MobileMenu. */}
      <aside className="sidebar sidebar-fixa" aria-label="Menu">
        <div className="sidebar-topo">
          <div className="sidebar-marca">
            <img src={logoKzeroWhite} alt="Kzero" className="sidebar-logo" />
            <small>{nomePortal}</small>
          </div>
        </div>
        <MenuLateral itens={navFiltrado} atual={pagina} onSelect={setPagina} badges={badgesModulos} />
      </aside>

      <div className="area">
        <header className="topbar">
          <button className="topbar-menu" onClick={() => setMenuAberto(true)} aria-label="Abrir menu">☰</button>
          <h1 className="topbar-titulo"><span aria-hidden="true">{paginaAtual.emoji}</span> {paginaAtual.label}</h1>
          <div className="topbar-actions">
            {sincronizando && <span className="topbar-salvando">☁️ Salvando...</span>}
            {mostrarWebmail && (
              <a href="https://titan.hostgator.com.br/mail/" target="_blank" rel="noopener noreferrer" className="topbar-btn topbar-hide-mobile">
                ✉️ <span>Webmail</span>
              </a>
            )}
            <a href="https://www.kzerosystem.com.br/login" target="_blank" rel="noopener noreferrer" className="topbar-btn topbar-hide-mobile">
              🔗 <span>KzeroSystem</span>
            </a>
            {deferredPrompt && (
              <button className="topbar-btn topbar-btn-destaque" onClick={instalarApp}>
                📲 <span className="topbar-btn-text">Instalar</span>
              </button>
            )}
            <button onClick={() => setModalSenha(true)} className="topbar-user-btn" title="Meu perfil e senha">
              <span className="avatar">{nomeUsuario.trim().charAt(0).toUpperCase()}</span>
              <span className="topbar-user-name">{nomeUsuario}</span>
            </button>
            <button className="topbar-btn topbar-sair" onClick={handleLogout} aria-label="Sair">
              <LogOut size={15} /> <span className="topbar-btn-text">Sair</span>
            </button>
          </div>
        </header>

        {/* Banner iOS — instrução de instalação */}
        {mostrarBannerIOS && (
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', background:'linear-gradient(90deg,#3b0764,#6d28d9)', color:'white', fontSize:13 }}>
            <span style={{ flex:1 }}>📲 Para instalar: toque em <strong>Compartilhar</strong> (ícone ⬆️) e depois <strong>"Adicionar à Tela de Início"</strong></span>
            <button onClick={() => { setMostrarBannerIOS(false); localStorage.setItem('pwa-banner-dismissed','1'); }} style={{ background:'rgba(255,255,255,0.2)', border:'none', color:'white', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:12, whiteSpace:'nowrap' }}>Fechar</button>
          </div>
        )}

        {/* Conteúdo */}
        <main className="main-dark">
          {navFiltrado.length === 0 && (
            <div style={{ maxWidth: 480, margin: '15vh auto 0', textAlign: 'center', color: '#5f5874', fontSize: 15, lineHeight: 1.6 }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🔒</div>
              <b style={{ color: '#1c1530' }}>Seu acesso ainda não foi liberado.</b><br />
              Fale com a Kzero Consultoria para liberar os seus cursos.
            </div>
          )}
          {renderPaginas()}
        </main>
      </div>

      <MobileMenu
        open={menuAberto}
        onClose={() => setMenuAberto(false)}
        items={navFiltrado}
        currentId={pagina}
        onSelect={setPagina}
        badges={badgesModulos}
        userName={nomeUsuario}
        userEmail={sessao?.user?.email || ''}
        logo={logoKzeroWhite}
        portal={nomePortal}
        mostrarWebmail={mostrarWebmail}
        onAccount={() => setModalSenha(true)}
        onLogout={handleLogout}
      />
    </div>
    </EmpresaProvider>
  );
}

const MESES_NOMES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

// Grupos DRE disponíveis para mapeamento
// Metadados dos grupos DRE — usados na aba Configurações
//
// NOTA FASE 2: os antigos consts DADOS_REFERENCIA_2024, DADOS_REFERENCIA_2025,
// RECEITAS_PGDAS_PADRAO, PLANO_CONTAS_PADRAO e REGRAS_CLASSIFICACAO_PADRAO
// foram movidos para JSONs por empresa em `src/data/empresas/<slug>/`:
//   - plano-contas.json
//   - regras-classificacao.json
//   - receitas-pgdas.json
//   - dados-referencia.json  (com chaves "2024" e "2025")
//   - abertura-2026.json
// Esses arquivos são consumidos pelo hook `useEmpresaContabil()`, que resolve
// o dataset da empresa ativa via `useEmpresa()`. Não edite as constantes
// antigas aqui — altere os JSONs diretamente.

// ── Helpers: Plano de Contas → Grupos DRE ────────────────────────────────
const MAPEAMENTO_PADRAO_CONTAS = {
  'Simples Nacional':           '5.5.01 — Simples Nacional (DAS)',
  'Tributos e Contribuições':   '5.5.01 — Simples Nacional (DAS)',
  'ISS':                        '5.5.01 — Simples Nacional (DAS)',
  'PIS':                        '5.5.01 — Simples Nacional (DAS)',
  'COFINS':                     '5.5.01 — Simples Nacional (DAS)',
  'IRRF':                       '5.5.01 — Simples Nacional (DAS)',
  'Pró-labore':                 '5.2.01 — Pró-labore',
  'Pro-labore':                 '5.2.01 — Pró-labore',
  'Salário':                    '5.2.03 — Salários e Ordenados',
  'INSS':                       '5.2.02 — INSS sobre Pró-labore',
  'FGTS':                       '5.2.04 — FGTS',
  'Contabilidade':              '5.3.01 — Serviços de Contabilidade',
  'Serviços PJ':                '5.3.01 — Serviços de Contabilidade',
  'Software':                   '5.3.02 — Software e Assinaturas',
  'Assinatura':                 '5.3.02 — Software e Assinaturas',
  'Aluguel':                    '5.3.03 — Aluguel',
  'Internet':                   '5.3.04 — Internet e Telefone',
  'Telefone':                   '5.3.04 — Internet e Telefone',
  'Material de Escritório':     '5.3.05 — Material de Escritório',
  'Viagens':                    '5.3.06 — Viagens e Deslocamentos',
  'Marketing':                  '5.4.01 — Marketing e Publicidade',
  'Publicidade':                '5.4.01 — Marketing e Publicidade',
  'Tarifas Bancárias':          '5.6.01 — Tarifas Bancárias',
  'Juros Pagos':                '5.6.02 — Juros e Multas Pagos',
  'Multas':                     '5.6.02 — Juros e Multas Pagos',
  'IOF':                        '5.6.03 — IOF',
  'Rendimentos':                '4.2.01 — Rendimentos de Aplicações',
  'Receitas Financeiras':       '4.2.01 — Rendimentos de Aplicações',
  'Dividendos':                 '6.1.01 — Dividendos Distribuídos',
  'Distribuição de Lucros':     '6.1.01 — Dividendos Distribuídos',
};

function contaParaGrupoDRE(codigoConta) {
  const c = (codigoConta || '').split(/ [—-] /)[0].trim();
  if (c.startsWith('4.2')) return 'Resultado Financeiro (receita)';
  if (c.startsWith('4'))   return 'Receita Bruta';
  if (c.startsWith('5.1')) return 'Custo dos Serviços Prestados';
  if (c.startsWith('5.2')) return 'Despesas com Pessoal';
  if (c.startsWith('5.3')) return 'Despesas Administrativas';
  if (c.startsWith('5.4')) return 'Despesas Comerciais';
  if (c.startsWith('5.5')) return 'Impostos sobre a Receita';
  if (c.startsWith('5.6')) return 'Resultado Financeiro (despesa)';
  if (c.startsWith('6'))   return 'Distribuição de Lucros';
  return 'Outras Despesas Operacionais';
}

// ── 5º Dia Útil do mês seguinte ──────────────────────────────────────────
// Eventos na agenda = data de prestação do serviço (competência)
// Recebimento efetivo = 5º dia útil do mês subsequente
function quintoDiaUtil(dataEvento) {
  if (!dataEvento) return null;
  const d = new Date(dataEvento + 'T12:00:00');
  // Primeiro dia do mês seguinte
  const primeiro = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  let diasUteis = 0;
  let atual = new Date(primeiro);
  while (diasUteis < 5) {
    const ds = atual.getDay(); // 0=Dom, 6=Sab
    if (ds !== 0 && ds !== 6) diasUteis++;
    if (diasUteis < 5) atual.setDate(atual.getDate() + 1);
  }
  return atual.toISOString().slice(0, 10);
}

function resolverGrupo(cat, mapeamentoContas, mapeamento) {
  const conta = (mapeamentoContas || {})[cat];
  if (conta) return contaParaGrupoDRE(conta);
  return (mapeamento || {})[cat] || null;
}

function codigoConta(cat, mapeamentoContas) {
  const full = (mapeamentoContas || {})[cat] || '';
  return full.split(/ [—-] /)[0].trim();
}

const DRE_GRUPOS_META = [
  {
    grupo: 'Impostos sobre a Receita',
    secao: 'Deduções da Receita Bruta',
    cor: '#f59e0b',
    exemplos: 'ISS, PIS, COFINS, IRRF sobre NF',
    dica: 'Tributos que incidem diretamente sobre o faturamento. Reduzem a Receita Bruta para chegar à Receita Líquida.',
  },
  {
    grupo: 'Custo dos Serviços Prestados',
    secao: 'Custo (CSP)',
    cor: '#a78bfa',
    exemplos: 'Terceiros contratados por projeto, ferramentas diretas do serviço',
    dica: 'Gastos diretamente ligados à execução do serviço vendido. Diferente de despesa operacional: sem este custo, o serviço não seria entregue.',
  },
  {
    grupo: 'Despesas com Pessoal',
    secao: 'Despesas Operacionais',
    cor: '#ef4444',
    exemplos: 'Salários, pró-labore, INSS patronal, FGTS, férias, 13º',
    dica: 'Toda remuneração de funcionários e sócios-administradores. Pró-labore sempre entra aqui, não em Divisão de Lucros.',
  },
  {
    grupo: 'Despesas Administrativas',
    secao: 'Despesas Operacionais',
    cor: '#ef4444',
    exemplos: 'Aluguel, internet, telefone, software, contabilidade, material de escritório',
    dica: 'Custos fixos para manter a empresa funcionando, independentemente do volume de serviços.',
  },
  {
    grupo: 'Despesas Comerciais',
    secao: 'Despesas Operacionais',
    cor: '#ef4444',
    exemplos: 'Marketing, publicidade, comissões de vendas, eventos, brindes',
    dica: 'Gastos voltados à captação e retenção de clientes.',
  },
  {
    grupo: 'Outras Despesas Operacionais',
    secao: 'Despesas Operacionais',
    cor: '#6e7681',
    exemplos: 'Depreciação, multas operacionais, doações, perdas diversas',
    dica: 'Despesas que não se encaixam nos grupos anteriores, mas fazem parte da atividade operacional.',
  },
  {
    grupo: 'Resultado Financeiro (despesa)',
    secao: 'Resultado Financeiro',
    cor: '#fb923c',
    exemplos: 'Juros pagos, IOF, tarifas bancárias, multa por atraso',
    dica: 'Custos financeiros. Entram após o EBIT para calcular o LAIR.',
  },
  {
    grupo: 'Resultado Financeiro (receita)',
    secao: 'Resultado Financeiro',
    cor: '#10b981',
    exemplos: 'Rendimentos de aplicações, juros recebidos',
    dica: 'Receitas de origem financeira (não operacional). Somam ao EBIT para calcular o LAIR.',
  },
  {
    grupo: 'Distribuição de Lucros',
    secao: 'Destinação do Resultado (pós-DRE)',
    cor: '#60a5fa',
    exemplos: 'Dividendos, lucros distribuídos aos sócios',
    dica: '⚠️ NÃO é despesa do DRE. Ocorre após o Resultado Líquido, na Destinação do Resultado / DMPL. Registre aqui para controle de caixa, mas não afeta o lucro calculado.',
  },
];

// ── Painel de Gerenciamento de Empresas (Fase 2) ────────────────────────
// Visível apenas para super-admin (consultoriakzero@gmail.com) na aba Configurações.
// Mostra as empresas cadastradas, permite gerar um config.json para nova empresa
// (texto pronto para commit) e mostra instruções de como adicionar ao repositório.
function GerenciarEmpresasPanel() {
  const { empresasTodas, empresaAtiva } = useEmpresa();
  const [mostrarForm, setMostrarForm] = React.useState(false);
  const [novoConfig, setNovoConfig] = React.useState({
    slug: '', nomeRazaoSocial: '', nomeFantasia: '', cnpj: '',
    tipo: 'propria', ownerEmail: '', visivelPara: '',
    titularNome: '', titularCPF: '',
    contadorNome: 'Bianca Florão Fleck Cardoso', contadorCRC: 'RS-105749/O', contadorCPF: '037.360.540-41',
    cidadeAssinatura: 'Canoas', nomeDemonstracoes: '', nomeMarca: '',
  });
  const [jsonGerado, setJsonGerado] = React.useState(null);

  const cardStyle = { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 12, padding: '1.25rem', marginBottom: '1rem' };
  const inp = { background: '#ffffff', color: '#1c1530', border: '1px solid #e5e0ef', borderRadius: 6, padding: '8px 10px', fontSize: 13, width: '100%' };
  const label = { fontSize: 11, color: '#5f5874', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4, display: 'block' };

  const gerar = () => {
    const visLista = (novoConfig.visivelPara || '').split(/[,\n]/).map(s => s.trim().toLowerCase()).filter(Boolean);
    const slug = (novoConfig.slug || '').toLowerCase().replace(/[^a-z0-9\-]/g, '-');
    if (!slug) { alert('Informe o slug da empresa (ex: "acme-ltda").'); return; }
    if (empresasTodas.some(e => e.slug === slug)) { alert('Slug já existe. Escolha outro.'); return; }
    if (!novoConfig.nomeRazaoSocial) { alert('Informe a Razão Social.'); return; }
    if (!novoConfig.ownerEmail) { alert('Informe o e-mail do responsável.'); return; }

    const cfg = {
      slug,
      nomeRazaoSocial: novoConfig.nomeRazaoSocial,
      nomeFantasia: novoConfig.nomeFantasia || novoConfig.nomeRazaoSocial,
      cnpj: novoConfig.cnpj || '',
      tipo: novoConfig.tipo,
      ownerEmail: novoConfig.ownerEmail.toLowerCase(),
      visivelPara: visLista,
      exerciciosEncerrados: [],
      titularNome: novoConfig.titularNome || novoConfig.nomeRazaoSocial,
      titularCPF: novoConfig.titularCPF || '',
      contadorNome: novoConfig.contadorNome || '',
      contadorCRC: novoConfig.contadorCRC || '',
      contadorCPF: novoConfig.contadorCPF || '',
      cidadeAssinatura: novoConfig.cidadeAssinatura || 'Canoas',
      nomeDemonstracoes: novoConfig.nomeDemonstracoes || novoConfig.nomeRazaoSocial,
      nomeMarca: (novoConfig.nomeMarca || novoConfig.nomeFantasia || novoConfig.nomeRazaoSocial).toUpperCase(),
    };
    setJsonGerado({ slug, cfg });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={cardStyle}>
        <h3 style={{ color: '#1c1530', margin: '0 0 6px 0', fontSize: 18 }}>🏢 Gerenciar Empresas</h3>
        <p style={{ fontSize: 12, color: '#5f5874', margin: 0 }}>
          Super-admin. Cada empresa vive em <code>src/data/empresas/&lt;slug&gt;/</code> com seus próprios JSONs
          (config, plano de contas, regras, abertura, lançamentos). Para cadastrar uma nova, gere o JSON abaixo,
          crie a pasta no repo e registre o config em <code>src/contexts/EmpresaContext.jsx</code> +
          <code>src/hooks/useEmpresaContabil.js</code>.
        </p>
      </div>

      <div style={cardStyle}>
        <div style={{ fontSize: 13, color: '#1c1530', fontWeight: 600, marginBottom: 10 }}>Empresas cadastradas ({empresasTodas.length})</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#ffffff' }}>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: '#5f5874', fontWeight: 600, borderBottom: '1px solid #e5e0ef' }}>Slug</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: '#5f5874', fontWeight: 600, borderBottom: '1px solid #e5e0ef' }}>Nome</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: '#5f5874', fontWeight: 600, borderBottom: '1px solid #e5e0ef' }}>CNPJ</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: '#5f5874', fontWeight: 600, borderBottom: '1px solid #e5e0ef' }}>Tipo</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: '#5f5874', fontWeight: 600, borderBottom: '1px solid #e5e0ef' }}>Owner</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: '#5f5874', fontWeight: 600, borderBottom: '1px solid #e5e0ef' }}>Visível para</th>
            </tr>
          </thead>
          <tbody>
            {empresasTodas.map(e => (
              <tr key={e.slug} style={{ background: e.slug === empresaAtiva?.slug ? 'rgba(124,58,237,0.08)' : 'transparent' }}>
                <td style={{ padding: '8px 10px', color: '#1c1530', borderBottom: '1px solid #eeeaf5', fontFamily: 'monospace' }}>{e.slug}</td>
                <td style={{ padding: '8px 10px', color: '#1c1530', borderBottom: '1px solid #eeeaf5' }}>{e.nomeFantasia || e.nomeRazaoSocial}</td>
                <td style={{ padding: '8px 10px', color: '#5f5874', borderBottom: '1px solid #eeeaf5', fontFamily: 'monospace' }}>{e.cnpj || '—'}</td>
                <td style={{ padding: '8px 10px', color: '#6d28d9', borderBottom: '1px solid #eeeaf5' }}>{e.tipo}</td>
                <td style={{ padding: '8px 10px', color: '#5f5874', borderBottom: '1px solid #eeeaf5' }}>{e.ownerEmail || '—'}</td>
                <td style={{ padding: '8px 10px', color: '#5f5874', borderBottom: '1px solid #eeeaf5' }}>
                  {Array.isArray(e.visivelPara) && e.visivelPara.length ? e.visivelPara.join(', ') : (e.tipo === 'cliente' ? '(só owner)' : '(todos)')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 13, color: '#1c1530', fontWeight: 600 }}>➕ Cadastrar nova empresa</div>
          <button onClick={() => setMostrarForm(v => !v)} style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {mostrarForm ? 'Fechar' : 'Abrir formulário'}
          </button>
        </div>
        {mostrarForm && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.75rem' }}>
            <div>
              <label style={label}>Slug (pasta)</label>
              <input style={inp} placeholder="acme-ltda" value={novoConfig.slug} onChange={e => setNovoConfig({ ...novoConfig, slug: e.target.value })} />
            </div>
            <div>
              <label style={label}>Razão Social</label>
              <input style={inp} value={novoConfig.nomeRazaoSocial} onChange={e => setNovoConfig({ ...novoConfig, nomeRazaoSocial: e.target.value })} />
            </div>
            <div>
              <label style={label}>Nome Fantasia</label>
              <input style={inp} value={novoConfig.nomeFantasia} onChange={e => setNovoConfig({ ...novoConfig, nomeFantasia: e.target.value })} />
            </div>
            <div>
              <label style={label}>CNPJ</label>
              <input style={inp} placeholder="00.000.000/0000-00" value={novoConfig.cnpj} onChange={e => setNovoConfig({ ...novoConfig, cnpj: e.target.value })} />
            </div>
            <div>
              <label style={label}>Tipo</label>
              <select style={inp} value={novoConfig.tipo} onChange={e => setNovoConfig({ ...novoConfig, tipo: e.target.value })}>
                <option value="propria">Própria</option>
                <option value="cliente">Cliente</option>
              </select>
            </div>
            <div>
              <label style={label}>Owner email</label>
              <input style={inp} placeholder="contato@empresa.com" value={novoConfig.ownerEmail} onChange={e => setNovoConfig({ ...novoConfig, ownerEmail: e.target.value })} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={label}>Visível para (vazio = todos; cliente ignora isso)</label>
              <textarea style={{ ...inp, minHeight: 60, fontFamily: 'monospace' }} placeholder="um email por linha ou separado por vírgula" value={novoConfig.visivelPara} onChange={e => setNovoConfig({ ...novoConfig, visivelPara: e.target.value })} />
            </div>
            <div>
              <label style={label}>Titular (nome)</label>
              <input style={inp} value={novoConfig.titularNome} onChange={e => setNovoConfig({ ...novoConfig, titularNome: e.target.value })} />
            </div>
            <div>
              <label style={label}>Titular CPF</label>
              <input style={inp} value={novoConfig.titularCPF} onChange={e => setNovoConfig({ ...novoConfig, titularCPF: e.target.value })} />
            </div>
            <div>
              <label style={label}>Contadora (nome)</label>
              <input style={inp} value={novoConfig.contadorNome} onChange={e => setNovoConfig({ ...novoConfig, contadorNome: e.target.value })} />
            </div>
            <div>
              <label style={label}>CRC</label>
              <input style={inp} value={novoConfig.contadorCRC} onChange={e => setNovoConfig({ ...novoConfig, contadorCRC: e.target.value })} />
            </div>
            <div>
              <label style={label}>Contadora CPF</label>
              <input style={inp} value={novoConfig.contadorCPF} onChange={e => setNovoConfig({ ...novoConfig, contadorCPF: e.target.value })} />
            </div>
            <div>
              <label style={label}>Cidade (assinatura)</label>
              <input style={inp} value={novoConfig.cidadeAssinatura} onChange={e => setNovoConfig({ ...novoConfig, cidadeAssinatura: e.target.value })} />
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
              <button onClick={gerar} style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                ✅ Gerar JSON
              </button>
            </div>
          </div>
        )}
      </div>

      {jsonGerado && (
        <div style={cardStyle}>
          <div style={{ fontSize: 13, color: '#1c1530', fontWeight: 600, marginBottom: 8 }}>
            📄 JSON gerado para <code>src/data/empresas/{jsonGerado.slug}/config.json</code>
          </div>
          <div style={{ fontSize: 12, color: '#5f5874', marginBottom: 8 }}>
            Para ativar a empresa:
            <ol style={{ margin: '6px 0 0 18px', padding: 0 }}>
              <li>Crie a pasta <code>src/data/empresas/{jsonGerado.slug}/</code> no repositório.</li>
              <li>Salve o JSON abaixo como <code>config.json</code> nessa pasta.</li>
              <li>Clone <code>plano-contas.json</code> e <code>regras-classificacao.json</code> da Kzero (ou use vazios).</li>
              <li>Crie <code>receitas-pgdas.json</code> <code>{'{}'}</code>, <code>dados-referencia.json</code> <code>{'{}'}</code>, <code>abertura-&lt;ano&gt;.json</code> <code>{'[]'}</code> e <code>lanc-&lt;ano&gt;.json</code> <code>{'[]'}</code>.</li>
              <li>Adicione o import em <code>EmpresaContext.jsx</code> e <code>useEmpresaContabil.js</code> seguindo o padrão já existente.</li>
              <li>Commit + push → Vercel deploya automaticamente.</li>
            </ol>
          </div>
          <textarea
            readOnly
            value={JSON.stringify(jsonGerado.cfg, null, 2)}
            style={{ width: '100%', minHeight: 280, background: '#ffffff', color: '#1c1530', border: '1px solid #e5e0ef', borderRadius: 6, padding: '10px', fontFamily: 'monospace', fontSize: 12 }}
          />
          <button
            onClick={() => { navigator.clipboard.writeText(JSON.stringify(jsonGerado.cfg, null, 2)); alert('JSON copiado.'); }}
            style={{ marginTop: 8, background: '#f7f5fb', color: '#1c1530', border: '1px solid #e5e0ef', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            📋 Copiar para área de transferência
          </button>
        </div>
      )}
    </div>
  );
}

function Relatorios({ data = {}, logoKzero, setFluxoCaixa: setFluxoCaixaLegado, setFluxoCaixaPorEmpresa, setContabilConfigPorEmpresa, isAdmin }) {
  const agendamentos    = data.agendamentos    || [];

  // ── Dados contábeis da empresa ativa (Fase 2/3/4 multi-empresa) ──
  const { empresaAtiva, isSuperAdmin } = useEmpresa();
  const empresaContabil = useEmpresaContabil();
  const slug = empresaAtiva?.slug || 'kzero-consultoria';
  // Lançamentos gravados no banco (migração 028), a partir da data de corte da empresa.
  // Mesmo slug que o useEmpresaContabil usa para os JSONs (sem o fallback acima),
  // para que JSON e tabela sejam sempre da mesma empresa.
  const { lancamentosDb, inicioLancamentos, recarregar: recarregarLancamentosDb } = useContabilLancamentos(empresaAtiva?.slug);

  // fluxoCaixa namespaced por empresa (Fase 4).
  // Migração lazy: Kzero Consultoria herda data.fluxoCaixa legado se ainda não
  // gravou; KzeroSystem (e qualquer outra empresa nova) começa vazia.
  const fluxoLegado = data.fluxoCaixa;
  const mapaFluxo = data.fluxoCaixaPorEmpresa;
  const fluxoCaixa = React.useMemo(() => {
    if (mapaFluxo && mapaFluxo[slug] !== undefined) return mapaFluxo[slug];
    if (slug === 'kzero-consultoria' && fluxoLegado) return fluxoLegado;
    return EMPTY_FLUXO_CAIXA;
  }, [mapaFluxo, slug, fluxoLegado]);

  const setFluxoCaixa = (valorOuFn) => {
    setFluxoCaixaPorEmpresa(prevMapa => {
      const mapa = prevMapa || {};
      const fluxoAtual = mapa[slug] !== undefined
        ? mapa[slug]
        : (slug === 'kzero-consultoria' ? (data.fluxoCaixa || []) : []);
      const novoFluxo = typeof valorOuFn === 'function' ? valorOuFn(fluxoAtual) : valorOuFn;
      return { ...mapa, [slug]: novoFluxo };
    });
  };

  // contabilConfig namespaced por empresa.
  // Migração lazy: a primeira vez que cada empresa é lida/gravada, o estado é
  // construído a partir do contabilConfig legado (apenas Kzero Consultoria,
  // que é a empresa "histórica") ou de objeto vazio (qualquer outra empresa).
  // O contabilConfig legado nunca é apagado — fica como fonte de verdade até
  // a empresa ter seu próprio contabilConfigPorEmpresa[slug] persistido.
  const cfgLegado = data.contabilConfig;
  const mapaConfig = data.contabilConfigPorEmpresa;
  const contabilConfig = React.useMemo(() => {
    if (mapaConfig && mapaConfig[slug] !== undefined) return mapaConfig[slug];
    if (slug === 'kzero-consultoria' && cfgLegado) return cfgLegado;
    return EMPTY_CONTABIL_CONFIG;
  }, [mapaConfig, slug, cfgLegado]);

  // Setter wrapper: encapsula em contabilConfigPorEmpresa[slug] mantendo a API
  // original setContabilConfig(valor | (prev) => novo). Os ~30 call sites do
  // componente Relatorios continuam usando setContabilConfig sem alteração.
  const setContabilConfig = (valorOuFn) => {
    setContabilConfigPorEmpresa(prevMapa => {
      const mapa = prevMapa || {};
      const cfgAtual = mapa[slug] !== undefined
        ? mapa[slug]
        : (slug === 'kzero-consultoria' ? (data.contabilConfig || {}) : {});
      const novoCfg = typeof valorOuFn === 'function' ? valorOuFn(cfgAtual) : valorOuFn;
      return { ...mapa, [slug]: novoCfg };
    });
  };
  // Estabilizar referências para não invalidar useMemos a cada render.
  // O próprio hook já retorna referências estáveis (são JSON imports estáticos),
  // mas o operador `||` criaria objetos novos a cada render.
  const PLANO_CONTAS_PADRAO         = empresaContabil.planoContas;
  const REGRAS_CLASSIFICACAO_PADRAO = empresaContabil.regrasClassif;
  const RECEITAS_PGDAS_PADRAO       = empresaContabil.receitasPgdas;
  const dadosReferencia             = empresaContabil.dadosReferencia;
  const DADOS_REFERENCIA_2024 = React.useMemo(() => (dadosReferencia && dadosReferencia['2024']) || {}, [dadosReferencia]);
  const DADOS_REFERENCIA_2025 = React.useMemo(() => (dadosReferencia && dadosReferencia['2025']) || {}, [dadosReferencia]);
  // Abertura consumida pelo JSON da empresa (ano 2026). Fallback vazio para empresas sem abertura registrada.
  const aberturaPorAnoEmp      = empresaContabil.aberturaPorAno;
  const lancamentosPorAnoEmp   = empresaContabil.lancamentosPorAno;

  const hoje = new Date();
  const [aba, setAba] = React.useState('lancamentos');

  // ── Filtros de período compartilhados ──
  const [filMes, setFilMes] = React.useState(String(hoje.getMonth() + 1).padStart(2, '0'));
  const [filAno, setFilAno] = React.useState(String(hoje.getFullYear()));

  // Validações por conta e fechamentos (contabil_validacoes / contabil_fechamentos).
  // Instância única: a aba Balancete e o assistente Fechamento do mês leem e gravam
  // pelo mesmo hook, para não haver duas verdades.
  const fechamentoCtx = useContabilFechamento(empresaAtiva?.slug, `${filAno}-${filMes}`);

  // ── Configurações ──
  const [dicaAberta, setDicaAberta] = React.useState(null);

  // ── Livro Razão ──
  const [razaoConta, setRazaoConta] = React.useState('');

  // ── DRE: Mensal ou Acumulado no Ano ──
  const [periodoAcum, setPeriodoAcum] = React.useState(false);

  // ── Balancete: Mensal ou Anual (consolidado do exercício) ──
  const [balanceteAnual, setBalanceteAnual] = React.useState(false);

  // ── Lançamentos Contábeis ──
  const LANC_VAZIO = { data: new Date().toISOString().slice(0,10), historico: '', contaDebito: '', contaCredito: '', valor: '', documento: '', centroCusto: '' };
  const [lancForm, setLancForm] = React.useState(LANC_VAZIO);
  const [showLancForm, setShowLancForm] = React.useState(false);
  const [editandoLancId, setEditandoLancId] = React.useState(null);
  const [editLancForm, setEditLancForm] = React.useState({});
  const [lancFiltro, setLancFiltro] = React.useState('');
  const [lancOrdem, setLancOrdem] = React.useState({ col: 'data', dir: 'asc' });
  const [contaExpandida, setContaExpandida] = React.useState(null); // drill-down balancete
  const [avisoBalancete, setAvisoBalancete] = React.useState(null); // falha ao gravar validação
  const [editandoLancManual, setEditandoLancManual] = React.useState(null); // { idx, form }
  const [editandoRbt12, setEditandoRbt12] = React.useState(false);
  const [rbt12Input, setRbt12Input] = React.useState('');
  // ── Apuração Tributária: competência independente ──
  const [apuracaoMes, setApuracaoMes] = React.useState(String(hoje.getMonth() + 1).padStart(2, '0'));
  const [apuracaoAno, setApuracaoAno] = React.useState(String(hoje.getFullYear()));
  const [novaRegraForm, setNovaRegraForm] = React.useState({ padrao: '', contaContabil: '' });

  // ── Pró-labore ──
  const empresaSlugAtual = empresaAtiva?.slug || '';
  const proLaboreVazio = React.useMemo(() => {
    const agora = new Date();
    const enderecoSalvo = contabilConfig.enderecoEmpresaCampos || {};
    return {
      empresaRazaoSocial: empresaAtiva?.nomeRazaoSocial || '',
      empresaCNPJ:        empresaAtiva?.cnpj || '',
      empresaLogradouro:  enderecoSalvo.logradouro  || '',
      empresaNumero:      enderecoSalvo.numero      || '',
      empresaComplemento: enderecoSalvo.complemento || '',
      empresaBairro:      enderecoSalvo.bairro      || '',
      empresaCidade:      enderecoSalvo.cidade      || '',
      empresaUF:          enderecoSalvo.uf          || '',
      empresaCEP:         enderecoSalvo.cep         || '',
      socioNome:          empresaAtiva?.titularNome || '',
      socioCPF:           empresaAtiva?.titularCPF || '',
      socioFuncao:        'Sócio Administrador',
      competencia:        `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}`,
      dataPagamento:      agora.toISOString().slice(0, 10),
      cidadeAssinatura:   empresaAtiva?.cidadeAssinatura || 'Canoas',
      valorBruto:         '',
      inss:               '',
      irrf:               '',
      outros:             '',
      valorLiquido:       '',
      observacoes:        '',
    };
    // Dep apenas no slug — evita loop de reset ao trocar campos do próprio form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaSlugAtual]);
  const [proLaboreForm, setProLaboreForm] = React.useState(proLaboreVazio);
  const [cnpjBuscando, setCnpjBuscando] = React.useState(false);
  const [cnpjErro, setCnpjErro] = React.useState('');
  // Ao TROCAR de empresa (slug muda), reinicializa o form com os dados da nova empresa.
  // Não reage a mudanças de contabilConfig nem a re-renders — só à troca real de empresa.
  const slugAnterior = React.useRef(empresaSlugAtual);
  React.useEffect(() => {
    if (slugAnterior.current !== empresaSlugAtual) {
      slugAnterior.current = empresaSlugAtual;
      setProLaboreForm(proLaboreVazio);
    }
  }, [empresaSlugAtual, proLaboreVazio]);

  // Consulta CNPJ via proxy same-origin /api/consulta-cnpj (que chama BrasilAPI no server).
  // Same-origin evita bloqueios de CSP no navegador. Preenche razão social + campos de endereço.
  const buscarCnpj = React.useCallback(async (cnpjRaw) => {
    const digitos = String(cnpjRaw || '').replace(/\D/g, '');
    if (digitos.length !== 14) {
      setCnpjErro('CNPJ precisa ter 14 dígitos.');
      return;
    }
    setCnpjBuscando(true);
    setCnpjErro('');
    try {
      const resp = await fetch(`/api/consulta-cnpj?cnpj=${digitos}`);
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(d?.error || `Falha na consulta (HTTP ${resp.status}).`);
      }
      setProLaboreForm(f => ({
        ...f,
        empresaCNPJ:        d.cnpj         || f.empresaCNPJ,
        empresaRazaoSocial: d.razao_social || f.empresaRazaoSocial,
        empresaLogradouro:  d.logradouro   || f.empresaLogradouro  || '',
        empresaNumero:      d.numero       || f.empresaNumero      || '',
        empresaComplemento: d.complemento  || f.empresaComplemento || '',
        empresaBairro:      d.bairro       || f.empresaBairro      || '',
        empresaCidade:      d.cidade       || f.empresaCidade      || '',
        empresaUF:          d.uf           || f.empresaUF          || '',
        empresaCEP:         d.cep          || f.empresaCEP         || '',
      }));
    } catch (e) {
      setCnpjErro(e.message || 'Erro ao consultar CNPJ.');
    } finally {
      setCnpjBuscando(false);
    }
  }, []);
  const [dreSubView, setDreSubView] = React.useState('dre');
  const [configSubView, setConfigSubView] = React.useState('config');
  const [showReclass, setShowReclass] = React.useState(false);
  const [reclassForm, setReclassForm] = React.useState({ contaOrigem: '', contaDestino: '', mesInicio: '', mesFim: '' });

  // ── Plano de Contas ──
  const [novaContaForm, setNovaContaForm] = React.useState({ codigo: '', nome: '', tipo: 'D', nivel: 4 });
  const [showNovaContaForm, setShowNovaContaForm] = React.useState(false);
  const [editandoContaCodigo, setEditandoContaCodigo] = React.useState(null);
  const [editContaForm, setEditContaForm] = React.useState({});

  // ── Validação de movimentação ──
  const [editandoId, setEditandoId] = React.useState(null);
  const [editForm, setEditForm] = React.useState({});

  // Lançamentos contábeis: fonte única = JSONs da empresa ativa (Fase 2).
  // Cada empresa tem sua pasta em src/data/empresas/<slug>/ com:
  //   - lanc-<ano>.json (ou lanc-q1-2026.json etc.)
  //   - abertura-<ano>.json
  // NÃO grava no Supabase. NÃO usa useEffect. Calcula no render. Zero duplicatas.
  const ABERTURA_2026 = React.useMemo(() => {
    return aberturaPorAnoEmp['2026'] || [];
  }, [aberturaPorAnoEmp]);

  // Exercícios fiscais com encerramento gerado automaticamente em 31/12 (zera 4.x/5.x/6.x
  // contra 3.2.01 Lucros Acumulados). Lista vazia quando o encerramento já foi PERSISTIDO
  // nos arquivos JSON — é o caso de 2025 a partir de 2026-04-20 (ver scripts/aplicar-encerramento-2025.mjs).
  const EXERCICIOS_ENCERRADOS = React.useMemo(() => [], []);

  const lancamentosContabeis = React.useMemo(() => {
    // Ordem cronológica: para cada ano, primeiro a abertura, depois os lançamentos.
    // Isso replica exatamente o comportamento Fase 1: [lanc2025, ABERTURA_2026, lancQ1_2026].
    const anosLanc = Object.keys(lancamentosPorAnoEmp).sort();
    const anosAbertura = Object.keys(aberturaPorAnoEmp).sort();
    const todosAnos = [...new Set([...anosLanc, ...anosAbertura])].sort();
    const base = [];
    todosAnos.forEach(ano => {
      if (aberturaPorAnoEmp[ano]) base.push(...aberturaPorAnoEmp[ano]);
      if (lancamentosPorAnoEmp[ano]) base.push(...lancamentosPorAnoEmp[ano]);
    });
    // Lançamentos do banco entram depois dos JSONs. Só os de data >= corte: antes
    // dela o período é dos arquivos (o gatilho já recusa; aqui é defesa extra).
    if (inicioLancamentos && lancamentosDb.length) {
      base.push(...lancamentosDb.filter(l => (l.data || '') >= inicioLancamentos));
    }
    const encerramentos = [];
    EXERCICIOS_ENCERRADOS.forEach(ano => {
      const saldos = {};
      base.filter(l => (l.data || '').startsWith(ano + '-')).forEach(l => {
        const val = parseFloat(l.valor || 0);
        const parse = (s) => {
          const parts = (s || '').split(/ [—-] /);
          return { cod: (parts[0] || '').trim(), nome: (parts[1] || '').trim() };
        };
        const d = parse(l.contaDebito);
        const c = parse(l.contaCredito);
        if (/^[456]\./.test(d.cod)) {
          if (!saldos[d.cod]) saldos[d.cod] = { nome: d.nome, saldo: 0 };
          saldos[d.cod].saldo += val;
        }
        if (/^[456]\./.test(c.cod)) {
          if (!saldos[c.cod]) saldos[c.cod] = { nome: c.nome, saldo: 0 };
          saldos[c.cod].saldo -= val;
        }
      });
      let idx = 0;
      Object.entries(saldos).forEach(([cod, { nome, saldo }]) => {
        if (Math.abs(saldo) < 0.01) return;
        idx++;
        const contaCompleta = `${cod} — ${nome}`;
        if (saldo > 0) {
          // Conta com saldo devedor (despesa/distribuição): encerra creditando
          encerramentos.push({
            id: `enc-${ano}-${idx}`, data: `${ano}-12-31`,
            historico: `Encerramento do exercício ${ano} — ${cod} ${nome}`,
            contaDebito: '3.2.01 — Lucros Acumulados',
            contaCredito: contaCompleta,
            valor: saldo, origem: `encerramento-${ano}`,
          });
        } else {
          // Conta com saldo credor (receita): encerra debitando
          encerramentos.push({
            id: `enc-${ano}-${idx}`, data: `${ano}-12-31`,
            historico: `Encerramento do exercício ${ano} — ${cod} ${nome}`,
            contaDebito: contaCompleta,
            contaCredito: '3.2.01 — Lucros Acumulados',
            valor: -saldo, origem: `encerramento-${ano}`,
          });
        }
      });
    });
    return [...base, ...encerramentos];
  }, [lancamentosPorAnoEmp, aberturaPorAnoEmp, EXERCICIOS_ENCERRADOS, lancamentosDb, inicioLancamentos]);

  // Contabilidade NÃO mexe no fluxoCaixa — são módulos independentes

  const validarMovimentacao = (id, status) => {
    if (!setFluxoCaixa) return;
    setFluxoCaixa(prev => prev.map(f => String(f.id) === String(id) ? { ...f, status } : f));
  };

  const abrirEdicao = (f) => {
    setEditandoId(f.id);
    setEditForm({ descricao: f.descricao || '', categoria: f.categoria || '', valor: f.valor || '', tipo: (f.tipo || 'DESPESA').toUpperCase(), data: f.data || '', observacoes: f.observacoes || '' });
  };

  const salvarEdicao = (id) => {
    if (!setFluxoCaixa) return;
    setFluxoCaixa(prev => prev.map(f => String(f.id) === String(id) ? { ...f, ...editForm } : f));
    setEditandoId(null);
  };

  const STATUS_BADGE = {
    validado:  { label: 'Validado',  bg: 'rgba(16,185,129,0.15)', color: '#10b981', border: 'rgba(16,185,129,0.4)' },
    rejeitado: { label: 'Rejeitado', bg: 'rgba(239,68,68,0.15)',  color: '#ef4444', border: 'rgba(239,68,68,0.4)' },
    pendente:  { label: 'Pendente',  bg: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: 'rgba(245,158,11,0.4)' },
    previsto:  { label: 'Previsto',  bg: 'rgba(96,165,250,0.15)', color: '#1d4ed8', border: 'rgba(96,165,250,0.4)' },
  };

  const fluxoFiltradoPeriodo = React.useMemo(() => {
    return fluxoCaixa.filter(f => {
      if (!f.data) return true;
      const [ano, mes] = f.data.split('-');
      return mes === filMes && ano === filAno;
    });
  }, [fluxoCaixa, filMes, filAno]);

  // ── Balancete — agrupado por Conta Contábil (NBC TG 26) ──
  // A regra mora em utils/contabil/balancete.js; o assistente Fechamento do mês usa
  // a mesma função (sempre no mensal), para as duas telas mostrarem os mesmos números.
  const planoBalancete = contabilConfig.planoContas || PLANO_CONTAS_PADRAO;
  const balancete = React.useMemo(() => calcularBalancete({
    lancamentos: lancamentosContabeis, plano: planoBalancete, filAno, filMes, anual: balanceteAnual,
  }), [filMes, filAno, balanceteAnual, planoBalancete, lancamentosContabeis]);
  const balanceteMensal = React.useMemo(() => (balanceteAnual
    ? calcularBalancete({ lancamentos: lancamentosContabeis, plano: planoBalancete, filAno, filMes, anual: false })
    : balancete), [balanceteAnual, balancete, lancamentosContabeis, planoBalancete, filAno, filMes]);

  // ── DRE (Lei 6.404/76 / NBC TG 26) — Somente lançamentos contábeis ──
  const dre = React.useMemo(() => {
    // DRE ignora lançamentos automáticos de encerramento (que zeram 4/5/6 contra 3.2.01)
    const lancPeriodo = lancamentosContabeis.filter(l => {
      const [ano, mes] = (l.data || '').split('-');
      if ((l.origem || '').startsWith('encerramento-')) return false;
      return periodoAcum ? ano === filAno : (mes === filMes && ano === filAno);
    });

    let receitaBruta = 0;
    let receitasFinanceiras = 0;
    let impostosTribs = 0;
    let resultadoFinanceiroDespesas = 0;
    let cspValor = 0;
    let distribuicaoLucros = 0;
    const despOpGrupos = {
      'Despesas com Pessoal':    0,
      'Despesas Administrativas':0,
      'Despesas Comerciais':     0,
    };
    let outrasDesp = 0;

    lancPeriodo.forEach(l => {
      const val = parseFloat(l.valor || 0);
      const codD = ((l.contaDebito  || '').split(/ [—-] /)[0]).trim();
      const codC = ((l.contaCredito || '').split(/ [—-] /)[0]).trim();
      // Receita Bruta (4.1)
      if (codC.startsWith('4.1')) receitaBruta += val;
      // Receita Financeira (4.2)
      if (codC.startsWith('4.2')) receitasFinanceiras += val;
      // Deduções/Impostos (5.5)
      if (codD.startsWith('5.5')) impostosTribs += val;
      // CSP (5.1)
      else if (codD.startsWith('5.1')) cspValor += val;
      // Despesas com Pessoal (5.2)
      else if (codD.startsWith('5.2')) despOpGrupos['Despesas com Pessoal'] += val;
      // Despesas Administrativas (5.3)
      else if (codD.startsWith('5.3')) despOpGrupos['Despesas Administrativas'] += val;
      // Despesas Comerciais (5.4)
      else if (codD.startsWith('5.4')) despOpGrupos['Despesas Comerciais'] += val;
      // Despesas Financeiras (5.6)
      else if (codD.startsWith('5.6')) resultadoFinanceiroDespesas += val;
      // Outras despesas (5.x não capturado acima)
      else if (codD.startsWith('5.')) outrasDesp += val;
      // Distribuição (6.x)
      if (codD.startsWith('6.')) distribuicaoLucros += val;
      // Estorno: débito em receita = redução
      if (codD.startsWith('4.')) receitaBruta -= val;
    });
    despOpGrupos['Outras Despesas Operacionais'] = outrasDesp;

    const deducoesReceita = impostosTribs;
    const receitaLiquida = receitaBruta - deducoesReceita;
    const csp = cspValor;
    const totalDespOp = Object.values(despOpGrupos).reduce((s, v) => s + v, 0);
    const ebit = receitaLiquida - csp - totalDespOp;
    const resultFinanceiro = receitasFinanceiras - resultadoFinanceiroDespesas;
    const lair = ebit + resultFinanceiro;
    const resultadoLiquido = lair;

    return { receitaBruta, deducoesReceita, receitaLiquida, csp, lucroBruto: receitaLiquida - csp, despOpGrupos, totalDespOp, ebit, resultFinanceiro, lair, resultadoLiquido, distribuicaoLucros };
  }, [filMes, filAno, periodoAcum, contabilConfig, lancamentosContabeis]);

  // ── DRE acumulada do EXERCÍCIO até o mês filtrado — usada nas exportações oficiais
  // (Balancete/Demonstrações Contábeis em PDF). Independe do toggle Mensal/Ano Todo da
  // aba interativa: a DRE em qualquer mês mostra o acumulado de janeiro até o mês emitido
  // (ex: balancete de novembro/2025 → DRE acumulada jan-nov/2025). No balancete anual
  // ou no balancete de dezembro, ambos abrangem o exercício inteiro.
  const dreExercicio = React.useMemo(() => {
    const filMesInt = parseInt(filMes, 10);
    const lancPeriodo = lancamentosContabeis.filter(l => {
      const [ano, mes] = (l.data || '').split('-');
      if ((l.origem || '').startsWith('encerramento-')) return false;
      if (ano !== filAno) return false;
      if (balanceteAnual) return true;
      return parseInt(mes, 10) <= filMesInt;
    });

    let receitaBruta = 0;
    let receitasFinanceiras = 0;
    let impostosTribs = 0;
    let resultadoFinanceiroDespesas = 0;
    let cspValor = 0;
    let distribuicaoLucros = 0;
    const despOpGrupos = {
      'Despesas com Pessoal':    0,
      'Despesas Administrativas':0,
      'Despesas Comerciais':     0,
    };
    let outrasDesp = 0;

    lancPeriodo.forEach(l => {
      const val = parseFloat(l.valor || 0);
      const codD = ((l.contaDebito  || '').split(/ [—-] /)[0]).trim();
      const codC = ((l.contaCredito || '').split(/ [—-] /)[0]).trim();
      if (codC.startsWith('4.1')) receitaBruta += val;
      if (codC.startsWith('4.2')) receitasFinanceiras += val;
      if (codD.startsWith('5.5')) impostosTribs += val;
      else if (codD.startsWith('5.1')) cspValor += val;
      else if (codD.startsWith('5.2')) despOpGrupos['Despesas com Pessoal'] += val;
      else if (codD.startsWith('5.3')) despOpGrupos['Despesas Administrativas'] += val;
      else if (codD.startsWith('5.4')) despOpGrupos['Despesas Comerciais'] += val;
      else if (codD.startsWith('5.6')) resultadoFinanceiroDespesas += val;
      else if (codD.startsWith('5.')) outrasDesp += val;
      if (codD.startsWith('6.')) distribuicaoLucros += val;
      if (codD.startsWith('4.')) receitaBruta -= val;
    });
    despOpGrupos['Outras Despesas Operacionais'] = outrasDesp;

    const deducoesReceita = impostosTribs;
    const receitaLiquida = receitaBruta - deducoesReceita;
    const csp = cspValor;
    const totalDespOp = Object.values(despOpGrupos).reduce((s, v) => s + v, 0);
    const ebit = receitaLiquida - csp - totalDespOp;
    const resultFinanceiro = receitasFinanceiras - resultadoFinanceiroDespesas;
    const lair = ebit + resultFinanceiro;
    const resultadoLiquido = lair;

    return { receitaBruta, deducoesReceita, receitaLiquida, csp, lucroBruto: receitaLiquida - csp, despOpGrupos, totalDespOp, ebit, resultFinanceiro, lair, resultadoLiquido, distribuicaoLucros };
  }, [filAno, filMes, balanceteAnual, contabilConfig, lancamentosContabeis]);

  // ── Livro Razão — por conta contábil ──
  const livroRazaoCategorias = React.useMemo(() => {
    const mapeamentoContas = contabilConfig.mapeamentoContas || {};
    const contas = new Set();
    // Somente lançamentos contábeis (manuais + extrato-auto)
    lancamentosContabeis.forEach(l => {
      const [ano] = (l.data || '').split('-');
      if (ano !== filAno) return;
      if (l.contaDebito)  contas.add(l.contaDebito);
      if (l.contaCredito) contas.add(l.contaCredito);
    });
    return [...contas].sort();
  }, [contabilConfig, filAno, lancamentosContabeis]);

  // ── DLPA do Ano — somente lançamentos contábeis ──
  const dlpaAno = React.useMemo(() => {
    // Fase 2: empresas sem histórico caem em objeto vazio (saldos = 0).
    const dlpaRef = Number(filAno) >= 2026
      ? (contabilConfig.dlpa2025 || DADOS_REFERENCIA_2025.dlpa || {})
      : (contabilConfig.dlpa2024 || DADOS_REFERENCIA_2024.dlpa || {});
    const saldoInicial = Number(filAno) > 2024 ? (dlpaRef.saldoFinal || 0) : (dlpaRef.saldoInicial || 0);
    // DLPA ignora encerramentos automáticos (zerariam o lucro líquido)
    const lancAno = lancamentosContabeis.filter(l => {
      const [ano] = (l.data || '').split('-');
      if ((l.origem || '').startsWith('encerramento-')) return false;
      return ano === filAno;
    });
    let recBrutaAno = 0, impostosAno = 0, cspAno = 0, despOpAno = 0, recFinAno = 0, distribuicaoAno = 0, despFinAno = 0;
    lancAno.forEach(l => {
      const val = parseFloat(l.valor || 0);
      const codD = ((l.contaDebito||'').split(/ [—-] /)[0]).trim();
      const codC = ((l.contaCredito||'').split(/ [—-] /)[0]).trim();
      if (codC.startsWith('4.1')) recBrutaAno += val;
      if (codC.startsWith('4.2')) recFinAno += val;
      if (codD.startsWith('5.5')) impostosAno += val;
      else if (codD.startsWith('5.1')) cspAno += val;
      else if (codD.startsWith('5.6')) despFinAno += val;
      else if (codD.startsWith('5.')) despOpAno += val;
      if (codD.startsWith('6.')) distribuicaoAno += val;
      // Estorno: débito em receita = redução (paridade com DRE — necessário para reclassif intra-receita não inflar a base).
      if (codD.startsWith('4.1')) recBrutaAno -= val;
      if (codD.startsWith('4.2')) recFinAno -= val;
    });
    const lucroLiquidoAno = recBrutaAno - impostosAno - cspAno - despOpAno + (recFinAno - despFinAno);
    const saldoFinal = saldoInicial + lucroLiquidoAno - distribuicaoAno;
    return { saldoInicial, lucroLiquidoAno, distribuicaoAno, saldoFinal, recBrutaAno, impostosAno, cspAno, despOpAno, recFinAno };
  }, [filAno, contabilConfig, lancamentosContabeis, DADOS_REFERENCIA_2024, DADOS_REFERENCIA_2025]);

  // Tolera undefined/null/NaN — degradação graciosa em vez de crash em refs comparativos incompletos.
  const fmt = v => `R$ ${(Number.isFinite(v) ? v : 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtN = v => (Number.isFinite(v) ? v : 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Helpers do config da empresa ativa (Fase 2) ──
  // Resolvem nome do titular, CPF, CNPJ, contadora, cidade — usados em PDFs.
  const cfgEmp = empresaContabil.config || {};
  const empNomeDemonstracoes = cfgEmp.nomeDemonstracoes || cfgEmp.nomeRazaoSocial || empresaAtiva?.nomeFantasia || '—';
  const empCnpj              = cfgEmp.cnpj || '—';
  const empTitularNome       = cfgEmp.titularNome || cfgEmp.nomeRazaoSocial || '';
  const empTitularCPF        = cfgEmp.titularCPF || '';
  const empContadorNome      = cfgEmp.contadorNome || '';
  const empContadorCRC       = cfgEmp.contadorCRC || '';
  const empContadorCPF       = cfgEmp.contadorCPF || '';
  const empCidadeAssinatura  = cfgEmp.cidadeAssinatura || 'Canoas';
  const empNomeMarca         = cfgEmp.nomeMarca || (cfgEmp.nomeRazaoSocial || '').toUpperCase();

  // Exportar demonstração — função-base única; chamar com tipo: 'balancete' | 'dre' | 'dlpa' | 'completo'
  const exportarDemonstracoes = (tipo) => {
    const periodo = `${MESES_NOMES[parseInt(filMes,10)-1]}/${filAno}`;
    const dreLinha = (desc, val, bold, indent) => `<tr style="${bold?'font-weight:bold;background:#f8f8f8':''}"><td style="padding-left:${indent||0}px">${desc}</td><td style="text-align:right;white-space:nowrap">${fmt(val)}</td></tr>`;

    const cabecalho = `
      <div style="text-align:center;margin-bottom:20px;border-bottom:2px solid #333;padding-bottom:15px;">
        <div style="font-size:11px;color:#666;margin-bottom:4px;">CNPJ: ${empCnpj}</div>
        <div style="font-size:18px;font-weight:bold;margin-bottom:2px;">${empNomeMarca}</div>
        <div style="font-size:11px;color:#666;">${empTitularNome}</div>
        <div style="font-size:12px;margin-top:8px;color:#333;">Período: ${periodo}</div>
        <div style="font-size:10px;color:#888;margin-top:4px;">Valores expressos em Reais (R$)</div>
      </div>`;

    const rodape = `
      <div style="margin-top:40px;border-top:1px solid #ccc;padding-top:20px;font-size:10px;color:#666;">
        <div style="margin-bottom:15px;">
          <strong>Base Legal:</strong> Demonstrações contábeis elaboradas de acordo com a NBC TG 1002 — Contabilidade para Pequenas Empresas (Microentidades),
          aprovada pela Resolução CFC nº 1.418/2012. Regime tributário: Simples Nacional — Anexo III (Lei Complementar 123/2006).
        </div>
        <div style="display:flex;justify-content:space-between;margin-top:30px;">
          <div style="text-align:center;width:45%;">
            <div style="border-top:1px solid #333;padding-top:8px;margin-top:40px;">
              <strong>${empTitularNome}</strong><br/>
              Titular<br/>
              ${empTitularCPF ? `CPF: ${empTitularCPF}` : ''}
            </div>
          </div>
          <div style="text-align:center;width:45%;">
            <div style="border-top:1px solid #333;padding-top:8px;margin-top:40px;">
              <strong>${empContadorNome}</strong><br/>
              ${empContadorCRC ? `CRC: ${empContadorCRC} — Contadora` : 'Contadora'}<br/>
              ${empContadorCPF ? `CPF: ${empContadorCPF}` : ''}
            </div>
          </div>
        </div>
      </div>`;

    // BALANCETE
    // Construir hierarquia do balancete (estilo Questor)
    const hierarquia = (() => {
      const plano = contabilConfig.planoContas || PLANO_CONTAS_PADRAO;
      const linhasMap = {};
      balancete.linhas.forEach(l => { linhasMap[l.codigo] = l; });

      // Montar árvore: para cada conta analítica, acumular nos pais sintéticos
      const sinteticos = {};
      const acumular = (codigo, deb, cred, saldoAnt, saldoFin) => {
        const partes = codigo.split('.');
        for (let i = 1; i < partes.length; i++) {
          const pai = partes.slice(0, i).join('.');
          if (!sinteticos[pai]) sinteticos[pai] = { debitos: 0, creditos: 0, saldoAnterior: 0, saldoFinal: 0 };
          sinteticos[pai].debitos += deb;
          sinteticos[pai].creditos += cred;
          sinteticos[pai].saldoAnterior += saldoAnt;
          sinteticos[pai].saldoFinal += saldoFin;
        }
      };
      balancete.linhas.forEach(l => acumular(l.codigo, l.debitos, l.creditos, l.saldoAnterior || 0, l.saldoFinal || 0));

      // Combinar sintéticos + analíticos e ordenar
      const todas = [];
      // Adicionar sintéticos do plano
      plano.filter(c => c.nivel <= 3).forEach(c => {
        const s = sinteticos[c.codigo];
        if (s && (Math.abs(s.debitos) > 0.01 || Math.abs(s.creditos) > 0.01 || Math.abs(s.saldoFinal) > 0.01)) {
          todas.push({ codigo: c.codigo, nome: c.nome, tipo: 'S', nivel: c.nivel, ...s });
        }
      });
      // Adicionar analíticas
      balancete.linhas.forEach(l => {
        todas.push({ codigo: l.codigo, nome: l.nome, tipo: 'A', nivel: (l.codigo.split('.').length), debitos: l.debitos, creditos: l.creditos, saldoAnterior: l.saldoAnterior || 0, saldoFinal: l.saldoFinal || 0 });
      });
      return todas.sort((a, b) => a.codigo.localeCompare(b.codigo));
    })();

    const secaoBalancete = `
      <h2 style="font-size:14px;border-bottom:1px solid #333;padding-bottom:6px;margin-top:30px;">Balancete — Societário</h2>
      <div style="font-size:10px;color:#888;margin-bottom:8px;">Período: ${periodo}</div>
      <table style="width:100%;border-collapse:collapse;font-size:10px;margin-top:8px;">
        <thead><tr style="border-bottom:2px solid #333;">
          <th style="text-align:center;padding:5px 3px;width:25px;">S</th>
          <th style="text-align:left;padding:5px 3px;">Classificação</th>
          <th style="text-align:right;padding:5px 3px;">Saldo Ant.</th>
          <th style="text-align:right;padding:5px 3px;">Débito</th>
          <th style="text-align:right;padding:5px 3px;">Crédito</th>
          <th style="text-align:right;padding:5px 3px;">Saldo</th>
        </tr></thead>
        <tbody>
          ${hierarquia.map(l => {
            const isSint = l.tipo === 'S';
            const indent = Math.max(0, (l.codigo.split('.').length - 1)) * 12;
            const fw = isSint ? 'bold' : 'normal';
            const bg = isSint && l.nivel <= 1 ? '#f0f0f0' : 'transparent';
            const bb = isSint ? '1px solid #ccc' : '1px solid #eee';
            const fs = isSint ? '10px' : '10px';
            // Contas redutoras de resultado (despesas 5.x, distribuição 6.x) aparecem com parênteses
            // no balancete — convenção do padrão Domínio/SPED para evidenciar natureza redutora
            const isRedutora = /^[56]\./.test(l.codigo);
            const fmtSaldo = (v) => {
              if (Math.abs(v) < 0.01) return fmtN(0);
              const abs = fmtN(Math.abs(v));
              return isRedutora ? `(${abs})` : fmtN(v);
            };
            return `<tr style="border-bottom:${bb};background:${bg};">
              <td style="text-align:center;padding:3px;font-size:9px;color:#888;">${isSint ? 'S' : ''}</td>
              <td style="padding:3px;padding-left:${indent}px;font-weight:${fw};font-size:${fs};">
                <span style="font-family:monospace;color:#666;margin-right:6px;">${l.codigo}</span>${l.nome}
              </td>
              <td style="text-align:right;padding:3px;font-weight:${fw};">${fmtSaldo(l.saldoAnterior)}</td>
              <td style="text-align:right;padding:3px;font-weight:${fw};">${l.debitos > 0.01 ? fmtN(l.debitos) : fmtN(0)}</td>
              <td style="text-align:right;padding:3px;font-weight:${fw};">${l.creditos > 0.01 ? fmtN(l.creditos) : fmtN(0)}</td>
              <td style="text-align:right;padding:3px;font-weight:${fw};">${fmtSaldo(l.saldoFinal)}</td>
            </tr>`;
          }).join('')}
        </tbody>
        <tfoot>
          <tr style="font-weight:bold;border-top:2px solid #333;background:#eee;">
            <td></td>
            <td style="padding:5px 3px;">TOTAL</td>
            <td></td>
            <td style="text-align:right;padding:5px 3px;">${fmtN(balancete.totalDebitos)}</td>
            <td style="text-align:right;padding:5px 3px;">${fmtN(balancete.totalCreditos)}</td>
            <td></td>
          </tr>
          <tr style="font-weight:bold;background:#fafafa;">
            <td></td>
            <td style="padding:5px 3px;">${balanceteAnual ? `RESULTADO DO EXERCÍCIO ${filAno}` : `RESULTADO ACUMULADO ATÉ ${MESES_NOMES[parseInt(filMes,10)-1]}/${filAno}`}</td>
            <td colspan="3" style="text-align:right;padding:5px 3px;font-size:9px;color:#666;">(Receita Líquida − Custos − Despesas Op. + Res. Financeiro)</td>
            <td style="text-align:right;padding:5px 3px;color:${dreExercicio.resultadoLiquido >= 0 ? '#0a7d3b' : '#b91c1c'};">${dreExercicio.resultadoLiquido >= 0 ? fmtN(dreExercicio.resultadoLiquido) : '('+fmtN(Math.abs(dreExercicio.resultadoLiquido))+')'}</td>
          </tr>
        </tfoot>
      </table>`;

    // DRE — acumulada do exercício até o mês emitido (padrão técnico NBC TG 26, art. 187 Lei 6.404)
    const dreTituloPeriodo = balanceteAnual
      ? `${filAno}`
      : `${MESES_NOMES[parseInt(filMes,10)-1]}/${filAno}`;
    const dreSubtitulo = balanceteAnual
      ? `Acumulado de janeiro a dezembro de ${filAno}`
      : `Acumulado de janeiro a ${MESES_NOMES[parseInt(filMes,10)-1].toLowerCase()} de ${filAno}`;
    const secaoDRE = `
      <div style="page-break-before:always;"></div>
      <h2 style="font-size:14px;border-bottom:1px solid #333;padding-bottom:6px;margin-top:30px;">DEMONSTRAÇÃO DO RESULTADO DO EXERCÍCIO — ${dreTituloPeriodo}</h2>
      <div style="font-size:10px;color:#888;margin-bottom:10px;">Lei 6.404/76 · NBC TG 26 · ${dreSubtitulo}</div>
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        ${dreLinha('RECEITA BRUTA DE SERVIÇOS', dreExercicio.receitaBruta, true)}
        ${dreLinha('(-) Deduções sobre Receita Bruta', -dreExercicio.deducoesReceita, false, 20)}
        ${dreLinha('RECEITA OPERACIONAL LÍQUIDA', dreExercicio.receitaLiquida, true)}
        ${dreExercicio.csp ? dreLinha('(-) Custo dos Serviços Prestados', -dreExercicio.csp, false, 20) : ''}
        ${dreLinha('LUCRO BRUTO', dreExercicio.lucroBruto, true)}
        ${Object.entries(dreExercicio.despOpGrupos).filter(([,v])=>v>0).map(([g,v]) => dreLinha('(-) '+g, -v, false, 20)).join('')}
        ${dreLinha('TOTAL DESPESAS OPERACIONAIS', -dreExercicio.totalDespOp, true)}
        ${dreLinha('RESULTADO OPERACIONAL (EBIT)', dreExercicio.ebit, true)}
        ${dreLinha('Resultado Financeiro Líquido', dreExercicio.resultFinanceiro, false, 20)}
        ${dreLinha('RESULTADO ANTES DO IR/CS (LAIR)', dreExercicio.lair, true)}
        ${dreLinha('RESULTADO LÍQUIDO DO EXERCÍCIO', dreExercicio.resultadoLiquido, true)}
        ${dreExercicio.distribuicaoLucros > 0 ? dreLinha('(-) Distribuição de Lucros', -dreExercicio.distribuicaoLucros, false, 20) : ''}
      </table>`;

    // DLPA
    const secaoDLPA = `
      <h2 style="font-size:14px;border-bottom:1px solid #333;padding-bottom:6px;margin-top:40px;">DEMONSTRAÇÃO DE LUCROS OU PREJUÍZOS ACUMULADOS — Exercício ${filAno}</h2>
      <div style="font-size:10px;color:#888;margin-bottom:10px;">NBC TG 1002 — Seção 6</div>
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <tr><td style="padding:6px 0;">Saldo no início do exercício</td><td style="text-align:right;padding:6px 0;">${fmt(dlpaAno.saldoInicial)}</td></tr>
        <tr><td style="padding:6px 0;">(+) Lucro Líquido do Exercício</td><td style="text-align:right;padding:6px 0;">${fmt(dlpaAno.lucroLiquidoAno)}</td></tr>
        ${dlpaAno.distribuicaoAno > 0 ? `<tr><td style="padding:6px 0;">(-) Distribuição de Lucros/Dividendos</td><td style="text-align:right;padding:6px 0;">${fmt(-dlpaAno.distribuicaoAno)}</td></tr>` : ''}
        <tr style="font-weight:bold;border-top:2px solid #333;background:#f5f5f5;"><td style="padding:8px 0;">SALDO NO FINAL DO EXERCÍCIO</td><td style="text-align:right;padding:8px 0;">${fmt(dlpaAno.saldoFinal)}</td></tr>
      </table>`;

    let html = cabecalho;
    if (tipo === 'balancete' || tipo === 'completo') html += secaoBalancete;
    if (tipo === 'dre' || tipo === 'completo') html += secaoDRE;
    if (tipo === 'dlpa' || tipo === 'completo') html += secaoDLPA;
    html += rodape;

    const titulo = tipo === 'completo' ? `Demonstrações Contábeis — ${periodo}` :
      tipo === 'balancete' ? `Balancete — ${periodo}` :
      tipo === 'dre' ? `DRE — ${periodo}` : `DLPA — ${filAno}`;

    exportarPDF(html, titulo);
  };

  // ── Balancete Analítico (modelo Questor/Domínio) — para validar saldos ──
  const exportarBalanceteQuestor = () => {
    const plano = contabilConfig.planoContas || PLANO_CONTAS_PADRAO;
    const planoIdx = {};
    plano.forEach(c => { planoIdx[c.codigo] = c; });

    // Mapa de analíticas (as linhas já vêm filtradas de balancete.linhas)
    const analiticas = {};
    balancete.linhas.forEach(l => { analiticas[l.codigo] = l; });

    // Acumular nos sintéticos (todos os pais do código)
    const sinteticos = {};
    balancete.linhas.forEach(l => {
      const partes = l.codigo.split('.');
      for (let i = 1; i < partes.length; i++) {
        const pai = partes.slice(0, i).join('.');
        if (!sinteticos[pai]) sinteticos[pai] = { debitos: 0, creditos: 0, saldoAnterior: 0, saldoFinal: 0 };
        sinteticos[pai].debitos      += l.debitos;
        sinteticos[pai].creditos     += l.creditos;
        sinteticos[pai].saldoAnterior += (l.saldoAnterior || 0);
        sinteticos[pai].saldoFinal   += (l.saldoFinal || 0);
      }
    });

    // Montar lista final (sintéticos + analíticos) ordenada por código
    const linhas = [];
    plano.forEach(c => {
      if (c.nivel >= 1 && c.nivel <= 3 && sinteticos[c.codigo]) {
        linhas.push({ codigo: c.codigo, nome: c.nome, nivel: c.nivel, tipo: 'S', ...sinteticos[c.codigo] });
      }
    });
    balancete.linhas.forEach(l => {
      const partes = l.codigo.split('.');
      const nivel = partes.length;
      // Sintéticos 4 (ex: 1.1.1.02) são na verdade analíticos no plano Kzero atual,
      // mas aparecem como sintéticos visuais se tiverem filhos no mesmo código
      linhas.push({ codigo: l.codigo, nome: l.nome, nivel, tipo: 'A', debitos: l.debitos, creditos: l.creditos, saldoAnterior: l.saldoAnterior || 0, saldoFinal: l.saldoFinal || 0 });
    });
    linhas.sort((a, b) => a.codigo.localeCompare(b.codigo));

    const periodoLabel = balanceteAnual ? `Exercício ${filAno} (Jan a Dez)` : `${MESES_NOMES[parseInt(filMes,10)-1]}/${filAno}`;

    const cabecalhoQ = `
      <div style="text-align:center;margin-bottom:20px;border-bottom:2px solid #333;padding-bottom:15px;">
        <div style="font-size:11px;color:#666;margin-bottom:4px;">CNPJ: ${empCnpj}</div>
        <div style="font-size:18px;font-weight:bold;margin-bottom:2px;">${empNomeMarca}</div>
        <div style="font-size:11px;color:#666;">${empTitularNome}</div>
        <div style="font-size:14px;margin-top:10px;color:#333;font-weight:bold;">Balancete Analítico</div>
        <div style="font-size:12px;color:#333;">Período: ${periodoLabel}</div>
        <div style="font-size:10px;color:#888;margin-top:4px;">Valores expressos em Reais (R$)</div>
      </div>`;

    const linhaHtml = (l) => {
      const isSint = l.tipo === 'S';
      const fw = isSint ? 'bold' : 'normal';
      const bg = isSint && l.nivel <= 2 ? '#e8eef7' : (isSint ? '#f3f6fb' : 'transparent');
      const indentPx = (l.nivel - 1) * 12;
      const descPad = isSint ? 'text-transform:uppercase;' : '';
      const fmtCell = (v) => Math.abs(v) < 0.01 ? '0,00' : fmtN(v);
      return `<tr style="background:${bg};border-bottom:1px solid #e5e7eb;">
        <td style="text-align:center;padding:4px 6px;font-size:9px;color:#666;width:30px;">${l.nivel}</td>
        <td style="text-align:center;padding:4px 6px;font-size:9px;color:${isSint ? '#1e40af' : '#6b7280'};font-weight:${fw};width:40px;">${l.tipo}</td>
        <td style="padding:4px 6px;font-family:monospace;font-size:10px;color:#374151;font-weight:${fw};white-space:nowrap;">${l.codigo}</td>
        <td style="padding:4px 6px;padding-left:${indentPx + 6}px;font-size:10px;color:#111;font-weight:${fw};${descPad}">${l.nome}</td>
        <td style="text-align:right;padding:4px 8px;font-family:monospace;font-size:10px;color:#111;font-weight:${fw};">${fmtCell(l.saldoAnterior)}</td>
        <td style="text-align:right;padding:4px 8px;font-family:monospace;font-size:10px;color:#111;font-weight:${fw};">${fmtCell(l.debitos)}</td>
        <td style="text-align:right;padding:4px 8px;font-family:monospace;font-size:10px;color:#111;font-weight:${fw};">${fmtCell(l.creditos)}</td>
        <td style="text-align:right;padding:4px 8px;font-family:monospace;font-size:10px;color:#111;font-weight:${fw};">${fmtCell(l.saldoFinal)}</td>
      </tr>`;
    };

    const tabela = `
      <table style="width:100%;border-collapse:collapse;font-size:10px;">
        <thead>
          <tr style="background:#f9a825;color:#1a1a1a;">
            <th style="text-align:center;padding:6px 4px;font-size:10px;border:1px solid #c88a00;">C.R.</th>
            <th style="text-align:center;padding:6px 4px;font-size:10px;border:1px solid #c88a00;">CX. S/A</th>
            <th style="text-align:left;padding:6px 6px;font-size:10px;border:1px solid #c88a00;">Conta</th>
            <th style="text-align:left;padding:6px 6px;font-size:10px;border:1px solid #c88a00;">Descrição</th>
            <th style="text-align:right;padding:6px 8px;font-size:10px;border:1px solid #c88a00;">Saldo Anterior</th>
            <th style="text-align:right;padding:6px 8px;font-size:10px;border:1px solid #c88a00;">Débito</th>
            <th style="text-align:right;padding:6px 8px;font-size:10px;border:1px solid #c88a00;">Crédito</th>
            <th style="text-align:right;padding:6px 8px;font-size:10px;border:1px solid #c88a00;">Saldo</th>
          </tr>
        </thead>
        <tbody>${linhas.map(linhaHtml).join('')}</tbody>
        <tfoot>
          <tr style="background:#e5e7eb;font-weight:bold;border-top:2px solid #333;">
            <td colspan="4" style="padding:6px 8px;font-size:10px;">TOTAL GERAL</td>
            <td style="text-align:right;padding:6px 8px;font-family:monospace;font-size:10px;">0,00</td>
            <td style="text-align:right;padding:6px 8px;font-family:monospace;font-size:10px;">${fmtN(balancete.totalDebitos)}</td>
            <td style="text-align:right;padding:6px 8px;font-family:monospace;font-size:10px;">${fmtN(balancete.totalCreditos)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>`;

    const html = cabecalhoQ + tabela + rodapeAssinaturas();
    exportarPDF(html, `Balancete Analítico — ${periodoLabel}`);
  };

  // Rodapé com assinaturas (reutilizável) — usa dados do config.json da empresa ativa.
  const rodapeAssinaturas = () => `
    <div style="margin-top:40px;border-top:1px solid #ccc;padding-top:20px;font-size:10px;color:#666;">
      <div style="display:flex;justify-content:space-between;margin-top:30px;">
        <div style="text-align:center;width:45%;">
          <div style="border-top:1px solid #333;padding-top:8px;margin-top:40px;">
            <strong>${empTitularNome}</strong><br/>
            Titular<br/>
            ${empTitularCPF ? `CPF: ${empTitularCPF}` : ''}
          </div>
        </div>
        <div style="text-align:center;width:45%;">
          <div style="border-top:1px solid #333;padding-top:8px;margin-top:40px;">
            <strong>${empContadorNome}</strong><br/>
            ${empContadorCRC ? `CRC: ${empContadorCRC} — Contadora` : 'Contadora'}<br/>
            ${empContadorCPF ? `CPF: ${empContadorCPF}` : ''}
          </div>
        </div>
      </div>
    </div>`;

  // ── Demonstrações Contábeis aplicadas a Microentidades (modelo PomperCloud) ──
  // Destinado a arquivamento na JUCESP. Formato resumido exigido pela NBC TG 1002.
  const exportarDemonstracoesJunta = () => {
    // Para BP e DRE, usar os mesmos dados já calculados
    // BP é de 31/12/filAno
    const lancAno = lancamentosContabeis.filter(l => {
      const [ano] = (l.data || '').split('-');
      return ano === filAno;
    });
    const saldos = {};
    lancAno.forEach(l => {
      const val = parseFloat(l.valor || 0);
      const codD = ((l.contaDebito||'').split(/ [—-] /)[0]).trim();
      const codC = ((l.contaCredito||'').split(/ [—-] /)[0]).trim();
      if (!saldos[codD]) saldos[codD] = 0;
      if (!saldos[codC]) saldos[codC] = 0;
      saldos[codD] += val;
      saldos[codC] -= val;
    });

    // Agrupamento nível 3 (microentidade — NBC TG 1002)
    let disponibilidades = 0, direitosCP = 0, outrosAtivoCP = 0;
    let fornecClientes = 0, obrigFiscaisCP = 0, obrigColab = 0, emprestimosCP = 0, obrigFiscaisLP = 0;
    let capitalSocial = 0, reservasLucros = 0;
    let receitasAno = 0, despesasAno = 0, distribuicaoAno = 0;

    Object.entries(saldos).forEach(([cod, s]) => {
      if (cod.startsWith('1.1.1')) disponibilidades += s;
      else if (cod.startsWith('1.1.2')) direitosCP += s;
      else if (cod.startsWith('1.')) outrosAtivoCP += s;
      else if (cod.startsWith('2.1.1')) fornecClientes += -s;
      else if (cod.startsWith('2.1.2')) obrigFiscaisCP += -s;
      else if (cod.startsWith('2.1.3')) obrigColab += -s;
      else if (cod.startsWith('2.1.4')) emprestimosCP += -s;
      else if (cod.startsWith('2.2')) obrigFiscaisLP += -s;
      else if (cod.startsWith('3.1')) capitalSocial += -s;
      else if (cod.startsWith('3.2')) reservasLucros += -s;
      else if (cod.startsWith('4.')) receitasAno += -s;
      else if (cod.startsWith('5.')) despesasAno += s;
      else if (cod.startsWith('6.')) distribuicaoAno += s;
    });

    // Movimento do ano (ignora encerramento) — separadamente, para a DLPA mostrar dividendos
    let distribuicaoAnoMov = 0;
    lancAno.forEach(l => {
      if ((l.origem || '').startsWith('encerramento-')) return;
      const val = parseFloat(l.valor || 0);
      const codD = ((l.contaDebito||'').split(/ [—-] /)[0]).trim();
      const codC = ((l.contaCredito||'').split(/ [—-] /)[0]).trim();
      if (codD.startsWith('6.')) distribuicaoAnoMov += val;
      if (codC.startsWith('6.')) distribuicaoAnoMov -= val;
    });

    const lucroExercicio = receitasAno - despesasAno - distribuicaoAno;
    const ativoCirc = disponibilidades + direitosCP + outrosAtivoCP;
    const totalAtivo = ativoCirc;
    const passivoCirc = fornecClientes + obrigFiscaisCP + obrigColab + emprestimosCP;
    const passivoNCirc = obrigFiscaisLP;
    const pl = capitalSocial + reservasLucros + lucroExercicio;
    const totalPassivo = passivoCirc + passivoNCirc + pl;

    // DRE anual (reutiliza cálculo do componente dre quando periodoAcum, mas aqui sempre ano cheio)
    // Recalcular sobre lancAno para independência
    let recBruta = 0, deducoes = 0, csp = 0, despAdm = 0, despPessoal = 0, despCom = 0, despFin = 0, recFin = 0;
    lancAno.forEach(l => {
      if ((l.origem || '').startsWith('encerramento-')) return;
      const val = parseFloat(l.valor || 0);
      const codD = ((l.contaDebito||'').split(/ [—-] /)[0]).trim();
      const codC = ((l.contaCredito||'').split(/ [—-] /)[0]).trim();
      if (codC.startsWith('4.1')) recBruta += val;
      if (codD.startsWith('4.1')) recBruta -= val;
      if (codC.startsWith('4.2')) recFin += val;
      if (codD.startsWith('5.1')) csp += val;
      else if (codD.startsWith('5.5')) deducoes += val;
      else if (codD.startsWith('5.2')) despPessoal += val;
      else if (codD.startsWith('5.3')) despAdm += val;
      else if (codD.startsWith('5.4')) despCom += val;
      else if (codD.startsWith('5.6')) despFin += val;
    });
    const recLiquida = recBruta - deducoes;
    const lucroBruto = recLiquida - csp;
    const totalDespOp = despPessoal + despAdm + despCom;
    const resultadoFinanceiro = recFin - despFin;
    const resultadoOperacional = lucroBruto - totalDespOp + resultadoFinanceiro;
    const resultadoLiquido = resultadoOperacional; // microentidade Simples: sem IR/CS

    // DLPA — Fase 2: fallback vazio p/ empresas sem histórico.
    const dlpaRef = Number(filAno) >= 2026
      ? (contabilConfig.dlpa2025 || DADOS_REFERENCIA_2025.dlpa || {})
      : (contabilConfig.dlpa2024 || DADOS_REFERENCIA_2024.dlpa || {});
    const saldoInicialDlpa = Number(filAno) > 2024 ? (dlpaRef.saldoFinal || 0) : (dlpaRef.saldoInicial || 0);
    const saldoFinalDlpa = saldoInicialDlpa + resultadoLiquido - distribuicaoAnoMov;

    // Análise vertical
    const pctBP = v => totalAtivo > 0 ? ((v / totalAtivo) * 100).toFixed(1).replace('.', ',') + '%' : '0,0%';
    const pctDRE = v => recBruta > 0 ? ((v / recBruta) * 100).toFixed(1).replace('.', ',') + '%' : '0,0%';
    const fmtVal = (v, neg) => {
      const abs = fmtN(Math.abs(v));
      return neg || v < 0 ? `(${abs})` : abs;
    };

    const hojeStr = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const horaStr = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    // Cabeçalho estilo PomperCloud — bicolunar
    const cabecalhoPC = `
      <div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:12px;border-bottom:1px solid #ccc;">
        <div>
          <div style="font-size:16px;font-weight:bold;color:#111;">${empNomeDemonstracoes}</div>
          <div style="font-size:12px;color:#555;margin-top:2px;">CNPJ: ${empCnpj}</div>
        </div>
        <div style="text-align:right;font-size:12px;color:#555;line-height:1.5;">
          Kzero Consultoria - Demonstrações Contábeis<br/>
          Gerado em ${hojeStr} ${horaStr}<br/>
          Valores expressos em Reais
        </div>
      </div>
      <div style="margin-top:20px;padding:8px;background:#dbe9d1;text-align:center;font-weight:bold;font-size:14px;color:#1a3a0e;">
        Demonstrações Contábeis aplicadas a Microentidades
      </div>`;

    // Linha do BP: descrição, valor, percentual. Nível controla indent e peso.
    const bpRow = (desc, val, nivel) => {
      const indent = (nivel - 1) * 20;
      const fw = nivel <= 2 ? 'bold' : 'normal';
      const bg = nivel === 1 ? '#f3f4f6' : 'transparent';
      return `<tr style="background:${bg};">
        <td style="padding:7px 10px;padding-left:${10 + indent}px;font-size:12px;color:#111;font-weight:${fw};${nivel === 1 ? 'text-transform:uppercase;' : ''}">${desc}</td>
        <td style="text-align:right;padding:7px 14px;font-size:12px;color:#111;font-weight:${fw};white-space:nowrap;">${fmtN(val)}</td>
        <td style="text-align:right;padding:7px 14px;font-size:12px;color:#111;font-weight:${fw};white-space:nowrap;">${pctBP(val)}</td>
      </tr>`;
    };

    const secaoBP = `
      <div style="margin-top:18px;padding:6px;background:#dbe9d1;text-align:center;font-weight:bold;font-size:12px;color:#1a3a0e;">
        BP - BALANÇO PATRIMONIAL
      </div>
      <table style="width:100%;border-collapse:collapse;margin-top:4px;">
        <thead>
          <tr style="border-bottom:1px solid #999;">
            <th style="text-align:left;padding:8px 10px;font-size:12px;color:#333;">Descrição</th>
            <th style="text-align:right;padding:8px 14px;font-size:12px;color:#333;">Dez/${filAno}</th>
            <th style="text-align:right;padding:8px 14px;font-size:12px;color:#333;width:60px;"></th>
          </tr>
        </thead>
        <tbody>
          ${bpRow('ATIVO', totalAtivo, 1)}
          ${bpRow('ATIVO CIRCULANTE', ativoCirc, 2)}
          ${disponibilidades > 0.01 ? bpRow('DISPONIBILIDADES', disponibilidades, 3) : ''}
          ${direitosCP > 0.01 ? bpRow('DIREITOS REAL. NO CURSO EX. SOCIAL SUBSEQUENTE', direitosCP, 3) : ''}
          ${outrosAtivoCP > 0.01 ? bpRow('OUTROS ATIVOS CIRCULANTES', outrosAtivoCP, 3) : ''}
          ${bpRow('PASSIVO', totalPassivo, 1)}
          ${bpRow('PASSIVO CIRCULANTE', passivoCirc, 2)}
          ${fornecClientes > 0.01 ? bpRow('OBRIGAÇÕES COM FORNECEDORES/CLIENTES', fornecClientes, 3) : ''}
          ${obrigFiscaisCP > 0.01 ? bpRow('OBRIGAÇÕES FISCAIS', obrigFiscaisCP, 3) : ''}
          ${obrigColab > 0.01 ? bpRow('OBRIGAÇÕES COM COLABORADORES', obrigColab, 3) : ''}
          ${emprestimosCP > 0.01 ? bpRow('EMPRÉSTIMOS BANCÁRIOS', emprestimosCP, 3) : ''}
          ${passivoNCirc > 0.01 ? bpRow('PASSIVO NÃO CIRCULANTE', passivoNCirc, 2) : ''}
          ${obrigFiscaisLP > 0.01 ? bpRow('OBRIGAÇÕES FISCAIS', obrigFiscaisLP, 3) : ''}
          ${bpRow('PATRIMÔNIO LÍQUIDO', pl, 2)}
          ${capitalSocial > 0.01 ? bpRow('CAPITAL SOCIAL', capitalSocial, 3) : ''}
          ${Math.abs(reservasLucros + lucroExercicio) > 0.01 ? bpRow('RESERVAS DE LUCROS', reservasLucros + lucroExercicio, 3) : ''}
        </tbody>
      </table>`;

    const secaoDLPAjunta = `
      <div style="margin-top:22px;padding:6px;background:#dbe9d1;text-align:center;font-weight:bold;font-size:12px;color:#1a3a0e;">
        DLPA - Demonstração de Lucros ou Prejuízos Acumulados
      </div>
      <table style="width:100%;border-collapse:collapse;margin-top:4px;">
        <thead>
          <tr style="background:#f3f4f6;">
            <th colspan="2" style="text-align:center;padding:8px 10px;font-size:12px;color:#111;border-bottom:1px solid #999;">${filAno}</th>
          </tr>
        </thead>
        <tbody>
          <tr><td style="padding:7px 10px;font-size:12px;font-weight:bold;">${saldoInicialDlpa >= 0 ? 'Lucros' : 'Prejuízos'} Acumulados no início do exercício - 01/01/${filAno}</td><td style="text-align:right;padding:7px 14px;font-size:12px;font-weight:bold;">${fmtN(Math.abs(saldoInicialDlpa))}</td></tr>
          <tr><td style="padding:7px 10px;font-size:12px;">(+) Lucro Líquido do Exercício</td><td style="text-align:right;padding:7px 14px;font-size:12px;">${fmtN(resultadoLiquido)}</td></tr>
          ${distribuicaoAnoMov > 0.01 ? `<tr><td style="padding:7px 10px;font-size:12px;">(-) Dividendos distribuídos</td><td style="text-align:right;padding:7px 14px;font-size:12px;">${fmtN(distribuicaoAnoMov)}</td></tr>` : ''}
          <tr style="border-top:1px solid #999;"><td style="padding:7px 10px;font-size:12px;font-weight:bold;">${saldoFinalDlpa >= 0 ? 'Lucros' : 'Prejuízos'} Acumulados no final do exercício - 31/12/${filAno}</td><td style="text-align:right;padding:7px 14px;font-size:12px;font-weight:bold;">${fmtN(Math.abs(saldoFinalDlpa))}</td></tr>
        </tbody>
      </table>`;

    const dreRow = (desc, val, nivel, negativo) => {
      const indent = (nivel - 1) * 20;
      const fw = nivel <= 2 ? 'bold' : 'normal';
      return `<tr>
        <td style="padding:7px 10px;padding-left:${10 + indent}px;font-size:12px;color:#111;font-weight:${fw};${nivel === 1 ? 'text-transform:uppercase;' : ''}">${desc}</td>
        <td style="text-align:right;padding:7px 14px;font-size:12px;color:${negativo ? '#991b1b' : '#111'};font-weight:${fw};white-space:nowrap;">${negativo ? '(' + fmtN(Math.abs(val)) + ')' : fmtN(val)}</td>
        <td style="text-align:right;padding:7px 14px;font-size:12px;color:${negativo ? '#991b1b' : '#111'};font-weight:${fw};white-space:nowrap;">${negativo ? '-' + pctDRE(Math.abs(val)) : pctDRE(val)}</td>
      </tr>`;
    };

    const secaoDREjunta = `
      <div style="margin-top:22px;padding:6px;background:#dbe9d1;text-align:center;font-weight:bold;font-size:12px;color:#1a3a0e;">
        DRE - DEMONSTRAÇÃO DO RESULTADO DO PERÍODO
      </div>
      <table style="width:100%;border-collapse:collapse;margin-top:4px;">
        <thead>
          <tr style="border-bottom:1px solid #999;">
            <th style="text-align:left;padding:8px 10px;font-size:12px;color:#333;">Descrição</th>
            <th style="text-align:right;padding:8px 14px;font-size:12px;color:#333;">Jan/${filAno} a Dez/${filAno}</th>
            <th style="text-align:right;padding:8px 14px;font-size:12px;color:#333;width:60px;"></th>
          </tr>
        </thead>
        <tbody>
          ${dreRow('RECEITA BRUTA', recBruta, 1)}
          ${dreRow('RECEITAS DE ATIVIDADES DE PRESTAÇÕES', recBruta, 3)}
          ${deducoes > 0.01 ? dreRow('DEDUÇÕES DA RECEITA BRUTA', deducoes, 1, true) : ''}
          ${deducoes > 0.01 ? dreRow('TRIBUTOS E CONTRIBUIÇÕES', deducoes, 3, true) : ''}
          ${dreRow('RECEITA LÍQUIDA', recLiquida, 1)}
          ${csp > 0.01 ? dreRow('CUSTO DOS SERVIÇOS PRESTADOS', csp, 1, true) : ''}
          ${csp > 0.01 ? dreRow('SERVIÇOS DE TERCEIROS', csp, 3, true) : ''}
          ${dreRow('LUCRO BRUTO', lucroBruto, 1)}
          ${totalDespOp > 0.01 ? dreRow('DESPESAS OPERACIONAIS', totalDespOp, 1, true) : ''}
          ${despPessoal > 0.01 ? dreRow('DESPESAS COM PESSOAL', despPessoal, 3, true) : ''}
          ${despAdm > 0.01 ? dreRow('DESPESAS ADMINISTRATIVAS', despAdm, 3, true) : ''}
          ${despCom > 0.01 ? dreRow('DESPESAS COMERCIAIS', despCom, 3, true) : ''}
          ${Math.abs(resultadoFinanceiro) > 0.01 ? dreRow('RESULTADO FINANCEIRO', Math.abs(resultadoFinanceiro), 3, resultadoFinanceiro < 0) : ''}
          ${dreRow('RESULTADO OPERACIONAL', resultadoOperacional, 1)}
          ${dreRow('RESULTADO ANTES DO IRPJ E DA CSLL', resultadoOperacional, 1)}
          ${dreRow('RESULTADO LÍQUIDO', resultadoLiquido, 1)}
        </tbody>
      </table>
      <div style="text-align:center;font-size:12px;color:#666;margin-top:20px;font-style:italic;">
        O presente documento intitulado "Demonstrações Contábeis aplicadas a Microentidades" continua na próxima página
      </div>`;

    // Página 2: Embasamento legal e assinaturas
    const pagina2 = `
      <div style="page-break-before:always;"></div>
      <div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:12px;border-bottom:1px solid #ccc;">
        <div>
          <div style="font-size:16px;font-weight:bold;color:#111;">${empNomeDemonstracoes}</div>
          <div style="font-size:12px;color:#555;margin-top:2px;">CNPJ: ${empCnpj}</div>
        </div>
        <div style="text-align:right;font-size:12px;color:#555;line-height:1.5;">
          Kzero Consultoria - Demonstrações Contábeis<br/>
          Gerado em ${hojeStr} ${horaStr}<br/>
          Valores expressos em Reais
        </div>
      </div>
      <div style="margin-top:20px;padding:8px;background:#dbe9d1;text-align:center;font-weight:bold;font-size:14px;color:#1a3a0e;">
        Demonstrações Contábeis aplicadas a Microentidades
      </div>
      <div style="margin-top:18px;padding:6px;background:#dbe9d1;text-align:center;font-weight:bold;font-size:12px;color:#1a3a0e;">
        EMBASAMENTO LEGAL E RESPONSABILIDADES
      </div>
      <div style="margin-top:14px;font-size:12px;color:#111;line-height:1.7;text-align:justify;">
        <p>A Escrituração Contábil, bem como as Demonstrações Contábeis apresentadas, foram realizadas em conformidade com a norma contábil aplicável às Microentidades, de acordo com as disposições contidas na NBC TG 1002, publicada pelo Conselho Federal de Contabilidade em 09/12/2021, em decorrência de a empresa ter registrado receita bruta anual de até R$ 4.800.000,00 no exercício anterior.</p>
        <p style="margin-top:10px;">As informações dispostas nas demonstrações têm como objetivo apresentar a posição financeira e o desempenho da sociedade, pautada no regime de competência, estando composta pelo "Balanço Patrimonial", pelas "Demonstrações do Resultado do Exercício" e pela "Demonstração dos Lucros e Prejuízos Acumulados". De modo a atender a exigência contida na norma contábil de regência, informa-se que a atividade operacional da sociedade decorre da exploração do objeto social contido no registro contratual vigente na data do presente encerramento: "Serviços combinados de escritório e apoio administrativo".</p>
        <p style="text-align:center;margin-top:24px;">${empCidadeAssinatura}, 31 de janeiro de ${Number(filAno) + 1}.</p>
      </div>
      <div style="display:flex;justify-content:space-between;margin-top:60px;font-size:12px;color:#111;">
        <div style="width:45%;">
          <div style="border-top:1px solid #333;padding-top:6px;">
            ${empTitularNome}<br/>
            <strong>${empTitularNome}</strong><br/>
            Titular${empTitularCPF ? `, CPF n.º ${empTitularCPF}` : ''}
          </div>
        </div>
        <div style="width:45%;text-align:right;">
          <div style="border-top:1px solid #333;padding-top:6px;">
            ${empContadorNome}<br/>
            <strong>${empContadorNome}</strong><br/>
            Contadora${empContadorCRC ? `, CRC/${empContadorCRC}` : ''}${empContadorCPF ? `, CPF n.º ${empContadorCPF}` : ''}
          </div>
        </div>
      </div>`;

    const html = cabecalhoPC + secaoBP + secaoDLPAjunta + secaoDREjunta + pagina2;
    exportarPDF(html, `Demonstrações Contábeis à Junta — ${filAno}`, { ocultarTitulo: true, ocultarData: true });
  };

  const inputStyle = { background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 8, color: '#1c1530', padding: '8px 12px', fontSize: 13 };
  const labelStyle = { fontSize: 11, color: '#5f5874', fontWeight: 600, textTransform: 'uppercase' };
  const cardStyle  = { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 12, padding: '1.5rem' };

  const menuItems = [
    { id: 'lancamentos',   label: 'Lançamentos',    icon: '📋' },
    { id: 'fechamentoMes', label: 'Fechamento do mês', icon: '✅' },
    { id: 'balancete',    label: 'Balancete',       icon: '⚖️' },
    { id: 'dre',           label: 'Demonstrações',   icon: '📈' },
    { id: 'razao',         label: 'Livro Razão',     icon: '📒' },
    { id: 'proLabore',     label: 'Pró-labore',      icon: '🧾' },
    { id: 'config',        label: 'Configurações',   icon: '⚙️' },
  ];
  const mesAtualNav = String(hoje.getMonth() + 1).padStart(2, '0');
  const anoAtualNav = String(hoje.getFullYear());
  const isPeriodoHistorico = filAno !== anoAtualNav || filMes !== mesAtualNav;

  return (
    <div className="contab-layout" style={{ display: 'flex', minHeight: 'calc(100vh - 140px)' }}>
      {/* Sidebar */}
      <nav className="contab-sidebar" style={{ width: 220, minWidth: 220, background: '#ffffff', borderRight: '1px solid #eeeaf5', display: 'flex', flexDirection: 'column', padding: 0, flexShrink: 0 }}>
        <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #eeeaf5' }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#1c1530', margin: 0 }}>🏛 Contabilidade</h2>
        </div>

        {/* Empresa ativa (multi-empresa) */}
        <div className="contab-empresa" style={{ padding: '12px', borderBottom: '1px solid #eeeaf5' }}>
          <label style={{ fontSize: 10, color: '#5f5874', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Empresa</label>
          <EmpresaSelector />
        </div>

        {/* Período */}
        <div className="contab-period" style={{ padding: '12px', borderBottom: '1px solid #eeeaf5' }}>
          <label style={{ fontSize: 10, color: '#5f5874', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Período</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <select style={{ ...inputStyle, flex: 1, fontSize: 12, padding: '6px 8px' }} value={filMes} onChange={e => setFilMes(e.target.value)}>
              {MESES_NOMES.map((m, i) => <option key={i} value={String(i+1).padStart(2,'0')}>{m}</option>)}
            </select>
            <input type="number" style={{ ...inputStyle, width: 56, fontSize: 12, padding: '6px 8px' }} value={filAno} onChange={e => setFilAno(e.target.value)} />
          </div>
        </div>

        {/* Menu items */}
        <div className="contab-menu" style={{ padding: '8px 0', flex: 1 }}>
          {menuItems.map(item => (
            <button key={item.id} onClick={() => setAba(item.id)} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 16px', margin: '2px 8px',
              background: aba === item.id ? 'rgba(124,58,237,0.15)' : 'transparent',
              border: 'none', borderRadius: 6,
              borderLeft: aba === item.id ? '3px solid #7c3aed' : '3px solid transparent',
              color: aba === item.id ? '#1c1530' : '#5f5874',
              fontSize: 13, fontWeight: aba === item.id ? 700 : 500, cursor: 'pointer',
              textAlign: 'left', width: 'calc(100% - 16px)',
            }}>
              <span style={{ fontSize: 16 }}>{item.icon}</span> {item.label}
            </button>
          ))}
        </div>

        <div style={{ padding: '12px 16px', borderTop: '1px solid #eeeaf5', fontSize: 10, color: '#e5e0ef' }}>
          Kzero CTB v1.0
        </div>
      </nav>

      {/* Content */}
      <div className="contab-content" style={{ flex: 1, padding: '1.25rem', overflowY: 'auto' }}>
        {/* Período mobile (hidden on desktop) */}
        <div className="contab-period-mobile" style={{ display: 'none', gap: 8, marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <EmpresaSelector />
          <select style={{ ...inputStyle, fontSize: 12 }} value={filMes} onChange={e => setFilMes(e.target.value)}>
            {MESES_NOMES.map((m, i) => <option key={i} value={String(i+1).padStart(2,'0')}>{m}</option>)}
          </select>
          <input type="number" style={{ ...inputStyle, width: 70, fontSize: 12 }} value={filAno} onChange={e => setFilAno(e.target.value)} />
        </div>

        {/* Banner período histórico */}
        {isPeriodoHistorico && !['config'].includes(aba) && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.3)', borderRadius: 8, marginBottom: '1rem', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#1d4ed8', fontWeight: 600 }}>📁 {MESES_NOMES[parseInt(filMes,10)-1]}/{filAno}</span>
            <button onClick={() => { setFilMes(mesAtualNav); setFilAno(anoAtualNav); }} style={{ background: 'rgba(96,165,250,0.15)', border: '1px solid rgba(96,165,250,0.4)', borderRadius: 6, color: '#1d4ed8', padding: '4px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Período atual</button>
          </div>
        )}

      {aba === 'config' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '1rem' }}>
          {[
            { id: 'config', label: 'Mapeamento' },
            { id: 'planocontas', label: 'Plano de Contas' },
            { id: 'importar', label: 'Importar' },
            { id: 'dashboard', label: 'Painel' },
            ...(isSuperAdmin ? [{ id: 'empresas', label: 'Empresas' }] : []),
          ].map(sub => (
            <button key={sub.id} onClick={() => setConfigSubView(sub.id)} style={{
              background: configSubView === sub.id ? '#7c3aed' : '#f7f5fb',
              color: configSubView === sub.id ? 'white' : '#5f5874',
              border: `1px solid ${configSubView === sub.id ? '#7c3aed' : '#e5e0ef'}`,
              borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>{sub.label}</button>
          ))}
        </div>
      )}

      {/* ── ABA: EMPRESAS (super-admin) — Fase 2 ── */}
      {aba === 'config' && configSubView === 'empresas' && isSuperAdmin && (
        <GerenciarEmpresasPanel />
      )}

      {/* ── ABA: IMPORTAR ── */}
      {aba === 'config' && configSubView === 'importar' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* Gerenciar importados */}
          {(() => {
            const importados = fluxoCaixa.filter(f => f.origem === 'extrato');
            const pendentes = fluxoCaixa.filter(f => f.categoria === 'A Classificar' || !f.categoria);
            const regras = (contabilConfig.regrasClassificacao?.length ? contabilConfig.regrasClassificacao : REGRAS_CLASSIFICACAO_PADRAO);

            const reclassificarTodos = () => {
              const atual = fluxoCaixa || [];
              let count = 0;
              const novos = atual.map(f => {
                const descLower = (f.descricao || '').toLowerCase();
                const match = regras.find(r => r.padrao && descLower.includes(r.padrao.toLowerCase()));
                if (match?.contaContabil && f.contaContabil !== match.contaContabil) {
                  count++;
                  return { ...f, contaContabil: match.contaContabil };
                }
                return f;
              });
              setFluxoCaixa(novos);
              alert(`✓ ${count} lançamento(s) vinculado(s) a contas contábeis com base em ${regras.length} regras.`);
            };

            return (
              <div style={{ ...cardStyle, border: '1px solid rgba(124,58,237,0.3)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <span style={{ fontSize: 14, color: '#1c1530', fontWeight: 600 }}>🏦 {importados.length} importado(s) · {pendentes.length > 0 ? <span style={{ color: '#ef4444' }}>⚠ {pendentes.length} pendente(s)</span> : <span style={{ color: '#10b981' }}>✅ Todos classificados</span>}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={reclassificarTodos} style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    🔄 Aplicar regras nos lançamentos existentes
                  </button>
                  {importados.length > 0 && (
                    <button onClick={() => {
                      if (window.confirm(`Excluir TODOS os ${importados.length} lançamentos importados + lançamentos contábeis gerados?\n\nIsso NÃO afeta lançamentos manuais.`)) {
                        setFluxoCaixa(prev => (prev || []).filter(f => f.origem !== 'extrato'));
                        setContabilConfig(prev => ({ ...(prev||{}), lancamentos: ((prev||{}).lancamentos || []).filter(l => l.origem !== 'extrato-auto') }));
                        alert(`✓ Importados excluídos (fluxo de caixa + lançamentos contábeis). Reimporte os CSVs abaixo.`);
                      }
                    }} style={{ background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      🗑 Limpar importados
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Importar Lançamentos Contábeis (Excel) */}
          <div style={{ ...cardStyle, border: '1px solid rgba(96,165,250,0.3)' }}>
            <h3 style={{ color: '#1c1530', margin: '0 0 6px 0' }}>📥 Importar Lançamentos Contábeis — Excel</h3>
            <p style={{ fontSize: 12, color: '#5f5874', margin: '0 0 1rem 0' }}>
              Planilha Excel (.xlsx) com colunas: <strong style={{ color: '#2d2640' }}>Data | Histórico | Conta Débito | Conta Crédito | Valor | Documento | Centro de Custo</strong>
              {' '}(as 5 primeiras são obrigatórias; Documento e Centro de Custo são opcionais).
            </p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => {
                const ws = XLSX.utils.aoa_to_sheet([
                  ['Data','Histórico','Conta Débito','Conta Crédito','Valor','Documento','Centro de Custo'],
                  ['2025-01-05','Pagamento de aluguel','5.3.1.01 — Aluguéis','1.1.1.03 — Banco Inter',1500,'NF 001','Adm'],
                  ['2025-01-10','Receita de consultoria','1.1.1.03 — Banco Inter','4.1.1 — Receita de Serviços Prestados',3000,'REC 010',''],
                ]);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Lancamentos');
                XLSX.writeFile(wb, 'modelo_lancamentos.xlsx');
              }} style={{ background: 'rgba(96,165,250,0.15)', color: '#1d4ed8', border: '1px solid rgba(96,165,250,0.4)', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                ⬇ Baixar Modelo Excel
              </button>
              <label style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                📂 Selecionar Arquivo .xlsx
                <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = ev => {
                    try {
                      const wb = XLSX.read(ev.target.result, { type: 'array', cellDates: true });
                      const ws = wb.Sheets[wb.SheetNames[0]];
                      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, dateNF: 'yyyy-mm-dd' });
                      if (rows.length < 2) { alert('Planilha vazia ou sem dados.'); return; }
                      const header = rows[0].map(h => (h || '').toString().toLowerCase().trim());
                      const col = (nomes) => { for (const n of nomes) { const i = header.findIndex(h => h.includes(n)); if (i >= 0) return i; } return -1; };
                      const iData  = col(['data']); const iHist = col(['hist','descriç']); const iDeb = col(['déb','deb','conta d']); const iCred = col(['créd','cred','conta c']); const iValor = col(['valor']); const iDoc = col(['doc']); const iCC = col(['centro','cc']);
                      if (iData < 0 || iHist < 0 || iDeb < 0 || iCred < 0 || iValor < 0) { alert('Colunas obrigatórias não encontradas. Verifique se o cabeçalho tem: Data, Histórico, Conta Débito, Conta Crédito, Valor'); return; }
                      const novos = [];
                      rows.slice(1).forEach((r, ri) => {
                        if (!r[iData] && !r[iValor]) return;
                        let dataVal = (r[iData] || '').toString().trim();
                        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dataVal)) { const [d,m,y] = dataVal.split('/'); dataVal = `${y}-${m}-${d}`; }
                        else if (/^\d{2}\/\d{2}\/\d{2}$/.test(dataVal)) { const [d,m,y] = dataVal.split('/'); dataVal = `20${y}-${m}-${d}`; }
                        const valorStr = (r[iValor] || '0').toString().replace(/[R$\s]/g,'').replace(',','.');
                        const valor = parseFloat(valorStr);
                        if (isNaN(valor) || valor === 0) return;
                        novos.push({ id: Date.now() + ri, data: dataVal, historico: (r[iHist]||'').toString().trim(), contaDebito: (r[iDeb]||'').toString().trim(), contaCredito: (r[iCred]||'').toString().trim(), valor, documento: iDoc >= 0 ? (r[iDoc]||'').toString().trim() : '', centroCusto: iCC >= 0 ? (r[iCC]||'').toString().trim() : '', criadoEm: new Date().toISOString() });
                      });
                      if (novos.length === 0) { alert('Nenhum lançamento válido encontrado na planilha.'); return; }
                      if (window.confirm(`Importar ${novos.length} lançamento(s)? Serão adicionados aos lançamentos contábeis existentes.`)) {
                        setContabilConfig(prev => ({ ...(prev||{}), lancamentos: [...((prev||{}).lancamentos||[]), ...novos] }));
                        alert(`✓ ${novos.length} lançamento(s) importado(s) com sucesso! Acesse a aba 2. Lançamentos para verificar.`);
                      }
                    } catch (err) { alert('Erro ao ler planilha: ' + err.message); }
                  };
                  reader.readAsArrayBuffer(file);
                  e.target.value = '';
                }} />
              </label>
            </div>
            <div style={{ marginTop: '0.75rem', fontSize: 11, color: '#8a849c' }}>
              Dica: a coluna Conta Débito/Crédito aceita só o código (ex: <strong>1.1.2.01</strong>) ou o formato completo <strong>"código — nome"</strong>.
            </div>
          </div>

          {/* Importar Extrato Bancário (CSV) */}
          <div style={{ ...cardStyle, border: '1px solid rgba(16,185,129,0.3)' }}>
            <h3 style={{ color: '#1c1530', margin: '0 0 6px 0' }}>🏦 Importar Extrato Bancário — CSV</h3>
            <p style={{ fontSize: 12, color: '#5f5874', margin: '0 0 0.75rem 0' }}>
              Arquivo CSV exportado do banco. Colunas detectadas automaticamente. Valor positivo = <strong style={{ color: '#10b981' }}>RECEITA</strong> · negativo = <strong style={{ color: '#ef4444' }}>DESPESA</strong>.
            </p>
            <div style={{ background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.15)', borderRadius: 8, padding: '10px 14px', marginBottom: '0.75rem', fontSize: 12, color: '#5f5874' }}>
              <strong style={{ color: '#1d4ed8' }}>Bancos compatíveis:</strong> Banco Inter · Santander PJ · Itaú · Bradesco · Nubank · Sicoob · qualquer CSV com colunas Data, Descrição, Valor
            </div>
            <label style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-block' }}>
              📂 Selecionar Extrato .csv
              <input type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={e => {
                const file = e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = ev => {
                  try {
                    const text = ev.target.result;
                    const sep = text.includes(';') ? ';' : ',';
                    const todasLinhas = text.split(/\r?\n/).filter(l => l.trim() && !l.startsWith('#'));
                    if (todasLinhas.length < 2) { alert('Arquivo vazio ou sem dados.'); return; }

                    // Detecta o banco pelo cabeçalho do CSV
                    const cabecalho = todasLinhas.slice(0, 5).join(' ').toLowerCase();
                    let bancoDetectado = 'Outro';
                    let contaBanco = '1.1.1.01 — Caixa';
                    if (cabecalho.includes('extrato conta corrente') || cabecalho.includes('data lançamento') || cabecalho.includes('data lancamento')) {
                      bancoDetectado = 'Banco Inter';
                      contaBanco = '1.1.1.03 — Banco Inter';
                    } else if (cabecalho.includes('agencia') || cabecalho.includes('cartao master') || cabecalho.includes('santander')) {
                      bancoDetectado = 'Banco Santander';
                      contaBanco = '1.1.1.02 — Banco Santander';
                    }

                    const parseH = h => (h||'').toString().toLowerCase().replace(/['"]/g,'').trim();
                    let headerIdx = 0;
                    for (let hi = 0; hi < Math.min(todasLinhas.length, 10); hi++) {
                      const campos = todasLinhas[hi].split(sep).map(parseH);
                      if (campos.some(c => c.includes('data') || c.includes('date')) && campos.some(c => c.includes('valor') || c.includes('value') || c.includes('amount'))) {
                        headerIdx = hi; break;
                      }
                    }
                    const linhas = todasLinhas.slice(headerIdx);
                    const header = linhas[0].split(sep).map(parseH);
                    const col = (nomes) => { for (const n of nomes) { const i = header.findIndex(h => h.includes(n)); if (i >= 0) return i; } return -1; };
                    const iData = col(['data','date']); const iDesc = col(['descri','hist','memo','lançamento','titulo']); const iValor = col(['valor','value','amount','credito','debito','moviment']);
                    if (iData < 0 || iValor < 0) { alert('Não foi possível identificar colunas de Data e Valor. Verifique se o CSV tem cabeçalho.'); return; }
                    const novos = [];
                    const novosLanc = []; // Lançamentos contábeis (partidas dobradas)
                    const regras = (contabilConfig.regrasClassificacao?.length ? contabilConfig.regrasClassificacao : REGRAS_CLASSIFICACAO_PADRAO);
                    linhas.slice(1).forEach((linha, li) => {
                      const cells = linha.split(sep).map(c => c.replace(/^["']|["']$/g,'').trim());
                      let dataVal = cells[iData] || '';
                      if (/^\d{2}\/\d{2}\/\d{4}$/.test(dataVal)) { const [d,m,y] = dataVal.split('/'); dataVal = `${y}-${m}-${d}`; }
                      else if (/^\d{2}\/\d{2}\/\d{2}$/.test(dataVal)) { const [d,m,y] = dataVal.split('/'); dataVal = `20${y}-${m}-${d}`; }
                      if (!/^\d{4}-\d{2}-\d{2}$/.test(dataVal)) return;
                      const valorStr = (cells[iValor]||'0').replace(/[R$\s.]/g,'').replace(',','.');
                      const valor = parseFloat(valorStr);
                      if (isNaN(valor) || valor === 0) return;
                      const descricao = iDesc >= 0 ? (cells[iDesc]||'Importado') : 'Importado';
                      const descLower = descricao.toLowerCase();
                      const regraMatch = regras.find(r => r.padrao && descLower.includes(r.padrao.toLowerCase()));
                      const contaAuto = regraMatch?.contaContabil || null;
                      const isReceita = valor > 0;
                      const fcId = Date.now() + li;
                      // Lançamento no Fluxo de Caixa
                      novos.push({ id: fcId, data: dataVal, tipo: isReceita ? 'RECEITA' : 'DESPESA', categoria: contaAuto ? contaAuto.replace(/^[\d.]+\s*—?\s*/, '').trim() : 'A Classificar', contaContabil: contaAuto, contaBanco, banco: bancoDetectado, descricao, valor: Math.abs(valor), status: 'pendente', origem: 'extrato' });
                      // Lançamento contábil (partida dobrada) — só se tem conta classificada
                      if (contaAuto) {
                        novosLanc.push({
                          id: fcId + 900000,
                          data: dataVal,
                          historico: descricao,
                          contaDebito: isReceita ? contaBanco : contaAuto,
                          contaCredito: isReceita ? contaAuto : contaBanco,
                          valor: Math.abs(valor),
                          documento: '',
                          centroCusto: bancoDetectado,
                          origem: 'extrato-auto',
                          criadoEm: new Date().toISOString(),
                        });
                      }
                    });
                    if (novos.length === 0) { alert('Nenhuma transação válida encontrada.'); return; }
                    const classificados = novos.filter(n => n.contaContabil).length;
                    if (window.confirm(`${bancoDetectado} detectado.\n\nImportar ${novos.length} transação(ões)?\n✅ ${classificados} com conta contábil\n⚠ ${novos.length - classificados} pendente(s)`)) {
                      // Salva TUDO em um único setData para evitar race condition
                      setFluxoCaixa(prev => {
                        const idsExist = new Set((prev||[]).map(f => f.data+'_'+f.valor+'_'+f.descricao));
                        const semDup = novos.filter(n => !idsExist.has(n.data+'_'+n.valor+'_'+n.descricao));
                        const dupCount = novos.length - semDup.length;
                        if (dupCount > 0) setTimeout(() => alert(`ℹ ${dupCount} duplicata(s) ignorada(s).`), 200);
                        return [...(prev||[]), ...semDup];
                      });
                      // Gravar lançamentos contábeis com delay para evitar sobrescrita
                      setTimeout(() => {
                        if (novosLanc.length > 0) {
                          setContabilConfig(prev => {
                            const lancExist = (prev||{}).lancamentos || [];
                            const idsExistL = new Set(lancExist.map(l => l.data+'_'+l.valor+'_'+l.historico));
                            const semDupL = novosLanc.filter(l => !idsExistL.has(l.data+'_'+l.valor+'_'+l.historico));
                            return { ...(prev||{}), lancamentos: [...lancExist, ...semDupL] };
                          });
                        }
                      }, 500);
                      alert(`✓ ${bancoDetectado} importado!\n\n${classificados} lançamentos com conta contábil (partidas dobradas geradas)\n${novos.length - classificados} pendente(s) de classificação`);
                    }
                  } catch (err) { alert('Erro ao processar extrato: ' + err.message); }
                };
                reader.readAsText(file, 'ISO-8859-1');
                e.target.value = '';
              }} />
            </label>
            <div style={{ marginTop: '0.75rem', fontSize: 11, color: '#8a849c' }}>
              Após importar, classifique as categorias das transações marcadas como <strong>"A Classificar"</strong> no Fluxo de Caixa.
            </div>
          </div>

        </div>
      )}

      {/* ── ABA: DASHBOARD ── */}
      {aba === 'config' && configSubView === 'dashboard' && (() => {
        const hoje2 = new Date();
        const mesAtual = String(hoje2.getMonth() + 1).padStart(2, '0');
        const anoAtual = String(hoje2.getFullYear());

        // KPIs do mês atual
        const agMes = agendamentos.filter(ag => { const [a, m] = (ag.data || '').split('-'); return m === mesAtual && a === anoAtual; });
        const receitaMes = agMes.reduce((s, ag) => s + parseFloat(ag.valor || 0), 0);
        const fluxoMes = fluxoCaixa.filter(f => { const [a, m] = (f.data || '').split('-'); return m === mesAtual && a === anoAtual && f.status !== 'rejeitado'; });
        const despesasMes = fluxoMes.filter(f => (f.tipo||'').toUpperCase() === 'DESPESA').reduce((s, f) => s + parseFloat(f.valor || 0), 0);
        const margemMes = receitaMes > 0 ? ((receitaMes - despesasMes) / receitaMes * 100) : 0;
        // lancamentosContabeis já definido como useMemo
        const pendentes = fluxoCaixa.filter(f => !f.status || f.status === 'pendente').length;
        const naoMapeadas = [...new Set(fluxoCaixa.map(f => f.categoria || '').filter(Boolean))].filter(c => !(contabilConfig.mapeamento || {})[c]).length;

        // FAIXAS DAS (simplificado) para alíquota
        const rbt12dash = (() => {
          let total = 0;
          for (let i = 1; i <= 12; i++) {
            let m = hoje2.getMonth() + 1 - i; let a = hoje2.getFullYear();
            if (m <= 0) { m += 12; a -= 1; }
            const ms = String(m).padStart(2,'0'); const as = String(a);
            total += agendamentos.filter(ag => { const [an,me] = (ag.data||'').split('-'); return me===ms&&an===as; }).reduce((s,ag)=>s+parseFloat(ag.valor||0),0);
          }
          return total;
        })();
        const FAIXAS_D = [{l:180000,a:0.06,d:0},{l:360000,a:0.112,d:9360},{l:720000,a:0.132,d:17640},{l:1800000,a:0.16,d:35640},{l:3600000,a:0.21,d:125640},{l:4800000,a:0.33,d:557640}];
        const faixaDash = FAIXAS_D.find(f => rbt12dash <= f.l) || FAIXAS_D[FAIXAS_D.length-1];
        const aliqEfDash = rbt12dash > 0 ? (rbt12dash * faixaDash.a - faixaDash.d) / rbt12dash : faixaDash.a;
        const dasEstimado = receitaMes * aliqEfDash;

        // Evolução 6 meses
        const chart6 = [];
        for (let i = 5; i >= 0; i--) {
          let m = hoje2.getMonth() + 1 - i; let a = hoje2.getFullYear();
          if (m <= 0) { m += 12; a -= 1; }
          const ms = String(m).padStart(2,'0'); const as = String(a);
          const rec = agendamentos.filter(ag => { const [an,me]=(ag.data||'').split('-'); return me===ms&&an===as; }).reduce((s,ag)=>s+parseFloat(ag.valor||0),0);
          const desp = fluxoCaixa.filter(f => { const [an,me]=(f.data||'').split('-'); return me===ms&&an===as&&f.tipo==='despesa'&&f.status!=='rejeitado'; }).reduce((s,f)=>s+parseFloat(f.valor||0),0);
          chart6.push({ label: MESES_NOMES[m-1].slice(0,3), rec, desp });
        }
        const maxChart = Math.max(...chart6.flatMap(c => [c.rec, c.desp]), 1);

        const kpiCard = (label, valor, cor, sub) => (
          <div style={{ background: '#ffffff', border: `1px solid ${cor}33`, borderRadius: 12, padding: '1.25rem', flex: 1, minWidth: 160 }}>
            <div style={{ fontSize: 11, color: '#5f5874', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: cor, fontFamily: 'monospace' }}>{valor}</div>
            {sub && <div style={{ fontSize: 11, color: '#8a849c', marginTop: 4 }}>{sub}</div>}
          </div>
        );

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* KPIs */}
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {kpiCard('Receita do Mês', fmt(receitaMes), '#10b981', MESES_NOMES[hoje2.getMonth()] + '/' + anoAtual)}
              {kpiCard('Despesas do Mês', fmt(despesasMes), '#ef4444', 'Lançamentos validados')}
              {kpiCard('Margem Líquida', `${margemMes.toFixed(1)}%`, margemMes >= 30 ? '#10b981' : margemMes >= 10 ? '#f59e0b' : '#ef4444', 'Resultado ÷ Receita')}
              {kpiCard('DAS Estimado', fmt(dasEstimado), '#a78bfa', `Alíq. efetiva ${(aliqEfDash*100).toFixed(2)}%`)}
              {kpiCard('Lançamentos Contábeis', lancamentosContabeis.length, '#58a6ff', 'Total registrado')}
            </div>

            {/* Alertas */}
            {(pendentes > 0 || naoMapeadas > 0) && (
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {pendentes > 0 && (
                  <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '10px 16px', fontSize: 13, color: '#f59e0b', display: 'flex', gap: 8, alignItems: 'center' }}>
                    ⚠ <strong>{pendentes}</strong> lançamento(s) pendente(s) de validação
                    <button onClick={() => setAba('lancamentos')} style={{ background: 'rgba(245,158,11,0.2)', border: 'none', color: '#f59e0b', borderRadius: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Ver</button>
                  </div>
                )}
                {naoMapeadas > 0 && (
                  <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 16px', fontSize: 13, color: '#ef4444', display: 'flex', gap: 8, alignItems: 'center' }}>
                    ⚠ <strong>{naoMapeadas}</strong> categoria(s) não mapeada(s) para o DRE
                    <button onClick={() => setAba('config')} style={{ background: 'rgba(239,68,68,0.2)', border: 'none', color: '#ef4444', borderRadius: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Configurar</button>
                  </div>
                )}
              </div>
            )}

            {/* Gráfico de evolução */}
            <div style={{ ...cardStyle, padding: '1.5rem' }}>
              <div style={{ fontSize: 13, color: '#1c1530', fontWeight: 700, marginBottom: '1.25rem' }}>Evolução — Últimos 6 Meses</div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', height: 140, paddingBottom: 24, position: 'relative' }}>
                {chart6.map((c, i) => (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, height: '100%', justifyContent: 'flex-end' }}>
                    <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', width: '100%', justifyContent: 'center' }}>
                      <div title={`Receita: ${fmt(c.rec)}`} style={{ width: '42%', height: `${Math.max((c.rec / maxChart) * 110, 2)}px`, background: '#10b981', borderRadius: '3px 3px 0 0', transition: 'height 0.3s' }} />
                      <div title={`Despesa: ${fmt(c.desp)}`} style={{ width: '42%', height: `${Math.max((c.desp / maxChart) * 110, 2)}px`, background: '#ef4444', borderRadius: '3px 3px 0 0', transition: 'height 0.3s' }} />
                    </div>
                    <span style={{ fontSize: 10, color: '#5f5874', marginTop: 4 }}>{c.label}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', marginTop: '0.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#5f5874' }}><span style={{ width: 12, height: 12, borderRadius: 3, background: '#10b981', display: 'inline-block' }} />Receita</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#5f5874' }}><span style={{ width: 12, height: 12, borderRadius: 3, background: '#ef4444', display: 'inline-block' }} />Despesas</div>
              </div>
            </div>

            {/* Contas a Receber — 5º Dia Útil */}
            {(() => {
              // Agendamentos do mês anterior cujo 5º DU cai no mês atual ou futuro próximo
              const hoje3 = new Date();
              const mesAtualStr = String(hoje3.getMonth() + 1).padStart(2,'0');
              const anoAtualStr = String(hoje3.getFullYear());
              const proximoMes = new Date(hoje3.getFullYear(), hoje3.getMonth() + 1, 1);
              const proximoMesStr = String(proximoMes.getMonth() + 1).padStart(2,'0');
              const proximoAnoStr = String(proximoMes.getFullYear());

              // Agrupa agendamentos por cliente + mês do evento
              const gruposMap = {};
              agendamentos.forEach(ag => {
                if (!ag.data || !ag.valor) return;
                const [a, m] = ag.data.split('-');
                // Inclui mês atual e próximo mês (eventos cujo recebimento ainda não passou)
                const mesEvento = `${a}-${m}`;
                const recData = quintoDiaUtil(ag.data);
                if (!recData) return;
                if (recData < hoje3.toISOString().slice(0, 10)) return;
                const cliente = ag.cliente || ag.descricao || ag.titulo || 'Serviço';
                const chave = `${mesEvento}||${cliente}`;
                if (!gruposMap[chave]) {
                  gruposMap[chave] = { cliente, mesEvento, dataRecebimento: recData, total: 0, eventos: 0 };
                }
                gruposMap[chave].total += parseFloat(ag.valor || 0);
                gruposMap[chave].eventos += 1;
              });

              const aReceber = Object.values(gruposMap)
                .sort((a, b) => a.dataRecebimento.localeCompare(b.dataRecebimento) || a.cliente.localeCompare(b.cliente));

              if (aReceber.length === 0) return null;

              const totalAReceber = aReceber.reduce((s, g) => s + g.total, 0);

              return (
                <div style={{ ...cardStyle }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#1d4ed8', fontWeight: 700, textTransform: 'uppercase' }}>📅 Contas a Receber — 5º Dia Útil do Mês Subsequente</div>
                      <div style={{ fontSize: 11, color: '#5f5874', marginTop: 2 }}>Agrupado por cliente · Recebimento = 5º DU do mês seguinte ao mês de prestação</div>
                    </div>
                    <div style={{ background: 'rgba(88,166,255,0.1)', border: '1px solid rgba(88,166,255,0.3)', borderRadius: 8, padding: '8px 16px', textAlign: 'center' }}>
                      <div style={{ fontSize: 10, color: '#1d4ed8', fontWeight: 700 }}>TOTAL A RECEBER</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#1d4ed8', fontFamily: 'monospace' }}>{fmt(totalAReceber)}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {aReceber.map((g, i) => {
                      const drec = new Date(g.dataRecebimento + 'T12:00:00');
                      const hoje4 = new Date();
                      const diasRestantes = Math.ceil((drec - hoje4) / 86400000);
                      const vencido = diasRestantes < 0;
                      const ehHoje = diasRestantes === 0;
                      const [mesAno] = [g.mesEvento];
                      const [aMes, mMes] = mesAno.split('-');
                      const labelMes = `${MESES_NOMES[parseInt(mMes,10)-1]}/${aMes}`;
                      return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 130px 100px', gap: '0.75rem', padding: '8px 10px', background: vencido ? 'rgba(239,68,68,0.05)' : ehHoje ? 'rgba(16,185,129,0.05)' : '#f7f5fb', borderRadius: 8, border: `1px solid ${vencido ? 'rgba(239,68,68,0.2)' : ehHoje ? 'rgba(16,185,129,0.2)' : '#eeeaf5'}`, alignItems: 'center' }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 11, color: '#5f5874' }}>Recebimento</div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: vencido ? '#ef4444' : ehHoje ? '#10b981' : '#1d4ed8' }}>
                              {drec.toLocaleDateString('pt-BR')}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: 13, color: '#1c1530' }}>{g.cliente}</div>
                            <div style={{ fontSize: 11, color: '#5f5874' }}>Mês de prestação: {labelMes} · {g.eventos} evento{g.eventos > 1 ? 's' : ''}</div>
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#10b981', fontFamily: 'monospace', textAlign: 'right' }}>{fmt(g.total)}</div>
                          <div style={{ textAlign: 'center' }}>
                            <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 10, background: vencido ? 'rgba(239,68,68,0.15)' : ehHoje ? 'rgba(16,185,129,0.15)' : 'rgba(88,166,255,0.1)', color: vencido ? '#ef4444' : ehHoje ? '#10b981' : '#1d4ed8', fontWeight: 600 }}>
                              {vencido ? `${Math.abs(diasRestantes)}d atraso` : ehHoje ? 'Hoje' : `${diasRestantes}d`}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Resumo DRE do mês */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={{ ...cardStyle }}>
                <div style={{ fontSize: 12, color: '#5f5874', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.75rem' }}>DRE Resumida — {MESES_NOMES[hoje2.getMonth()]}/{anoAtual}</div>
                {[
                  { label: 'Receita Bruta', val: receitaMes, cor: '#10b981' },
                  { label: '(−) Despesas', val: despesasMes, cor: '#ef4444' },
                  { label: '= Resultado', val: receitaMes - despesasMes, cor: (receitaMes - despesasMes) >= 0 ? '#10b981' : '#ef4444', bold: true },
                ].map(r => (
                  <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: r.bold ? '1px solid #e5e0ef' : 'none' }}>
                    <span style={{ color: r.bold ? '#1c1530' : '#2d2640', fontSize: 13, fontWeight: r.bold ? 700 : 400 }}>{r.label}</span>
                    <span style={{ color: r.cor, fontSize: 13, fontWeight: r.bold ? 700 : 400, fontFamily: 'monospace' }}>{fmt(r.val)}</span>
                  </div>
                ))}
              </div>
              <div style={{ ...cardStyle }}>
                <div style={{ fontSize: 12, color: '#5f5874', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.75rem' }}>Ações Rápidas</div>
                {[
                  { label: '📋 Novo Lançamento Contábil', acao: () => { setAba('lancamentos'); setShowLancForm(true); }, cor: '#58a6ff' },
                  { label: '📈 Ver DRE do mês', acao: () => setAba('dre'), cor: '#10b981' },
                  { label: '🧮 Apurar DAS', acao: () => { setAba('dre'); setDreSubView('apuracao'); }, cor: '#a78bfa' },
                  { label: '⚙️ Configurar mapeamento', acao: () => setAba('config'), cor: '#f59e0b' },
                ].map(a => (
                  <button key={a.label} onClick={a.acao} style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: `1px solid ${a.cor}44`, borderRadius: 8, color: a.cor, padding: '8px 14px', fontSize: 13, cursor: 'pointer', marginBottom: 8, fontWeight: 600 }}>
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── ABA: LANÇAMENTOS CONTÁBEIS ── */}
      {aba === 'lancamentos' && (() => {
        const lancamentos = lancamentosContabeis;
        const planoContas = contabilConfig.planoContas || PLANO_CONTAS_PADRAO;
        const contasAnaliticas = planoContas.filter(c => c.nivel === 3 || c.nivel === 4);
        const mapeamentoContas = contabilConfig.mapeamentoContas || {};

        // Verifica se o período atual está com balancete fechado
        const chPeriodoLanc = `${filAno}-${filMes}`;
        // Fechado pelo assistente (contabil_fechamentos) ou pela tela antiga do Balancete.
        const periodoFechado = !!(contabilConfig.balancetesFechados || {})[chPeriodoLanc]
          || fechamentoCtx.fechamentos.some(f => f.periodo === chPeriodoLanc);

        // Erro do banco: mostra só as mensagens de regra de negócio dos gatilhos
        // (período fechado, data antes do corte); o resto vira mensagem genérica.
        const msgErroLanc = (err, acao) => {
          const m = (err && err.message) || '';
          if (/está fechado|anterior ao início da escrituração/.test(m)) return m;
          console.error(`[contabilidade] Falha ao ${acao} lançamento.`, err);
          return `Não foi possível ${acao} o lançamento. Tente novamente em instantes.`;
        };

        // Lançamento manual vai para contabil_lancamentos (mig 028). Antes ia para
        // contabilConfig.lancamentos, que esta lista e o Balancete nunca leram:
        // o lançamento era gravado e não aparecia em lugar nenhum.
        const salvarLancamento = async () => {
          if (periodoFechado) { alert('Este período está com balancete fechado. Não é possível incluir lançamentos.'); return; }
          const faltando = [
            !lancForm.data && 'Data', !lancForm.historico && 'Histórico',
            !lancForm.contaDebito && 'Conta Débito', !lancForm.contaCredito && 'Conta Crédito',
            !(parseFloat(lancForm.valor) > 0) && 'Valor',
          ].filter(Boolean);
          if (faltando.length) { alert(`Preencha: ${faltando.join(', ')}.`); return; }
          if (!empresaAtiva?.slug) { alert('Selecione a empresa.'); return; }
          if (inicioLancamentos && lancForm.data < inicioLancamentos) {
            alert(`A escrituração desta empresa no sistema começa em ${inicioLancamentos.split('-').reverse().join('/')}. Lançamentos anteriores são controlados pelos arquivos de lançamento.`);
            return;
          }
          const { error } = await supabase.from('contabil_lancamentos').insert({
            empresa_slug: empresaAtiva.slug,
            data: lancForm.data,
            historico: lancForm.historico.trim(),
            conta_debito: lancForm.contaDebito,
            conta_credito: lancForm.contaCredito,
            valor: Math.round(parseFloat(lancForm.valor) * 100) / 100,
            documento: lancForm.documento?.trim() || null,
            centro_custo: lancForm.centroCusto?.trim() || null,
            origem: 'manual',
          });
          if (error) { alert(msgErroLanc(error, 'registrar')); return; }
          recarregarLancamentosDb();
          // Mostra o mês do lançamento, para ele aparecer na lista logo em seguida.
          const [aLanc, mLanc] = lancForm.data.split('-');
          if (aLanc !== filAno || mLanc !== filMes) { setFilAno(aLanc); setFilMes(mLanc); }
          setLancForm(LANC_VAZIO);
          setShowLancForm(false);
        };

        const salvarEdicaoLanc = async (id) => {
          if (periodoFechado) { alert('Este período está com balancete fechado.'); return; }
          if (!editLancForm.historico || !editLancForm.contaDebito || !editLancForm.contaCredito || !editLancForm.valor) return;
          const lancDb = lancamentos.find(l => l.id === id && l._db);
          if (lancDb) {
            const { error } = await supabase.from('contabil_lancamentos').update({
              data: editLancForm.data,
              historico: editLancForm.historico.trim(),
              conta_debito: editLancForm.contaDebito,
              conta_credito: editLancForm.contaCredito,
              valor: Math.round(parseFloat(editLancForm.valor) * 100) / 100,
              documento: editLancForm.documento?.trim() || null,
              centro_custo: editLancForm.centroCusto?.trim() || null,
            }).eq('id', lancDb._dbId);
            if (error) { alert(msgErroLanc(error, 'alterar')); return; }
            recarregarLancamentosDb();
          } else if (String(id).startsWith('fc_')) {
            // Lançamento do financeiro: salva override
            setContabilConfig(prev => ({
              ...(prev || {}),
              lancamentosOverride: { ...((prev || {}).lancamentosOverride || {}), [id]: { ...editLancForm, valor: parseFloat(editLancForm.valor) } },
            }));
          } else {
            setContabilConfig(prev => ({
              ...(prev || {}),
              lancamentos: ((prev || {}).lancamentos || []).map(l => l.id === id ? { ...l, ...editLancForm, valor: parseFloat(editLancForm.valor) } : l),
            }));
          }
          setEditandoLancId(null);
        };

        const excluirLancamento = async (id) => {
          if (periodoFechado) { alert('Este período está com balancete fechado. Não é possível excluir lançamentos.'); return; }
          const lancDb = lancamentos.find(l => l.id === id && l._db);
          if (lancDb) {
            if (!window.confirm(`Excluir o lançamento "${lancDb.historico}"?`)) return;
            const { error } = await supabase.from('contabil_lancamentos').delete().eq('id', lancDb._dbId);
            if (error) { alert(msgErroLanc(error, 'excluir')); return; }
            recarregarLancamentosDb();
            return;
          }
          setContabilConfig(prev => ({ ...(prev || {}), lancamentos: ((prev || {}).lancamentos || []).filter(l => l.id !== id) }));
        };

        // Converte fluxoCaixa em lançamentos contábeis automáticos (partidas dobradas)
        const CONTA_CAIXA = '1.1.1.01 — Caixa';
        const CONTA_BANCO = '1.1.1.03 — Banco Inter';
        const CONTA_RECEITA_PADRAO = '4.1.1.01 — Prestação de Serviços';
        const CONTA_DESP_PADRAO = '5.3.99 — Outras Despesas Administrativas';

        const lancOverrides = contabilConfig.lancamentosOverride || {};

        // Lançamentos do período — APENAS do JSON (sem fluxoCaixa)
        const toggleOrdem = (col) => setLancOrdem(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }));
        const sortFn = (a, b) => {
          const { col, dir } = lancOrdem;
          let va, vb;
          if (col === 'data') { va = a.data || ''; vb = b.data || ''; }
          else if (col === 'historico') { va = (a.historico || '').toLowerCase(); vb = (b.historico || '').toLowerCase(); }
          else if (col === 'debito') { va = a.contaDebito || ''; vb = b.contaDebito || ''; }
          else if (col === 'credito') { va = a.contaCredito || ''; vb = b.contaCredito || ''; }
          else if (col === 'valor') { va = parseFloat(a.valor || 0); vb = parseFloat(b.valor || 0); return dir === 'asc' ? va - vb : vb - va; }
          else { va = a.data || ''; vb = b.data || ''; }
          const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
          return dir === 'asc' ? cmp : -cmp;
        };
        const lancsFiltrados = lancamentos
          .filter(l => { const [a, m] = (l.data || '').split('-'); return m === filMes && a === filAno; })
          .filter(l => !lancFiltro || (l.historico || '').toLowerCase().includes(lancFiltro.toLowerCase()) || (l.contaDebito || '').includes(lancFiltro) || (l.contaCredito || '').includes(lancFiltro))
          .sort(sortFn);

        const totalDebito  = lancsFiltrados.reduce((s, l) => s + l.valor, 0);
        const totalCredito = lancsFiltrados.reduce((s, l) => s + l.valor, 0);

        const optContaGroup = (tipo, label) => {
          const contas = contasAnaliticas.filter(c => c.tipo === tipo);
          if (!contas.length) return null;
          return <optgroup key={tipo} label={label}>{contas.map(c => <option key={c.codigo} value={`${c.codigo} — ${c.nome}`}>{c.codigo} — {c.nome}</option>)}</optgroup>;
        };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Header + botão novo */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div>
                <h3 style={{ color: '#1c1530', margin: '0 0 4px 0', fontSize: '1rem' }}>📋 Lançamentos Contábeis — Partidas Dobradas</h3>
                <span style={{ fontSize: 11, color: '#5f5874' }}>{MESES_NOMES[parseInt(filMes,10)-1]}/{filAno} · {lancsFiltrados.length} lançamento(s) · Total débito = crédito: {fmt(totalDebito)}</span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                {periodoFechado && (
                  <span style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.4)', borderRadius: 20, padding: '3px 12px', fontSize: 11, color: '#10b981', fontWeight: 700 }}>
                    🔒 PERÍODO FECHADO
                  </span>
                )}
                <input placeholder="Filtrar..." style={{ ...inputStyle, width: 180 }} value={lancFiltro} onChange={e => setLancFiltro(e.target.value)} />
                {!periodoFechado && (
                  <>
                    <button onClick={() => setShowLancForm(s => !s)} style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      {showLancForm ? '✕ Fechar' : '+ Novo Lançamento'}
                    </button>
                    <button onClick={() => setAba('fechamentoMes')} title="Gera os lançamentos a partir do extrato bancário em PDF, OFX ou CSV" style={{ background: '#ffffff', color: '#6d28d9', border: '1px solid #7c3aed', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      📄 Importar extrato (PDF)
                    </button>
                    <button onClick={() => setAba('fechamentoMes')} title="Lança as notas fiscais emitidas em Clientes a Receber, lendo o PDF ou XML" style={{ background: '#ffffff', color: '#6d28d9', border: '1px solid #7c3aed', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      🧾 Notas fiscais
                    </button>
                    <button onClick={() => setShowReclass(s => !s)} style={{ background: showReclass ? '#ef4444' : '#f59e0b', color: showReclass ? 'white' : '#1e1b4b', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      {showReclass ? '✕ Fechar' : '🔀 Reclassificar'}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Formulário novo lançamento */}
            {showLancForm && (
              <div style={{ background: '#ffffff', border: '1px solid #58a6ff', borderRadius: 12, padding: '1.25rem' }}>
                <div style={{ fontSize: 13, color: '#1d4ed8', fontWeight: 700, marginBottom: '1rem' }}>Novo Lançamento Contábil</div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 140px', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={labelStyle}>Data</label>
                    <input type="date" style={inputStyle} value={lancForm.data} onChange={e => setLancForm(p => ({ ...p, data: e.target.value }))} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={labelStyle}>Histórico</label>
                    <input style={inputStyle} placeholder="Descrição do lançamento..." value={lancForm.historico} onChange={e => setLancForm(p => ({ ...p, historico: e.target.value }))} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={labelStyle}>Documento</label>
                    <input style={inputStyle} placeholder="NF, RPA, Recibo..." value={lancForm.documento} onChange={e => setLancForm(p => ({ ...p, documento: e.target.value }))} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={labelStyle}>Valor (R$)</label>
                    <input type="number" step="0.01" min="0" style={inputStyle} value={lancForm.valor} onChange={e => setLancForm(p => ({ ...p, valor: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ ...labelStyle, color: '#ef4444' }}>Conta Débito (D)</label>
                    <select style={{ ...inputStyle, borderColor: '#ef444455' }} value={lancForm.contaDebito} onChange={e => setLancForm(p => ({ ...p, contaDebito: e.target.value }))}>
                      <option value="">— Selecione a conta —</option>
                      {optContaGroup('A', '1 — ATIVO')}
                      {optContaGroup('P', '2 — PASSIVO')}
                      {optContaGroup('PL', '3 — PATRIMÔNIO LÍQUIDO')}
                      {optContaGroup('R', '4 — RECEITAS')}
                      {optContaGroup('D', '5 — CUSTOS E DESPESAS')}
                      {optContaGroup('DR', '6 — DESTINAÇÃO')}
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ ...labelStyle, color: '#10b981' }}>Conta Crédito (C)</label>
                    <select style={{ ...inputStyle, borderColor: '#10b98155' }} value={lancForm.contaCredito} onChange={e => setLancForm(p => ({ ...p, contaCredito: e.target.value }))}>
                      <option value="">— Selecione a conta —</option>
                      {optContaGroup('A', '1 — ATIVO')}
                      {optContaGroup('P', '2 — PASSIVO')}
                      {optContaGroup('PL', '3 — PATRIMÔNIO LÍQUIDO')}
                      {optContaGroup('R', '4 — RECEITAS')}
                      {optContaGroup('D', '5 — CUSTOS E DESPESAS')}
                      {optContaGroup('DR', '6 — DESTINAÇÃO')}
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={labelStyle}>Centro de Custo</label>
                    <input style={inputStyle} placeholder="Ex: Projetos, Administrativo..." value={lancForm.centroCusto} onChange={e => setLancForm(p => ({ ...p, centroCusto: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={() => { setShowLancForm(false); setLancForm(LANC_VAZIO); }} style={{ background: 'transparent', color: '#5f5874', border: '1px solid #e5e0ef', borderRadius: 8, padding: '8px 18px', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
                  <button onClick={salvarLancamento} style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>💾 Registrar Lançamento</button>
                </div>
              </div>
            )}

            {/* Painel Reclassificação em Massa */}
            {showReclass && (
              <div style={{ background: '#ffffff', border: '1px solid #f59e0b', borderRadius: 12, padding: '1.25rem', marginBottom: '1rem' }}>
                <div style={{ fontSize: 13, color: '#f59e0b', fontWeight: 700, marginBottom: '0.75rem' }}>🔀 Reclassificar Lançamentos — Mover de uma conta para outra</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ ...labelStyle, color: '#ef4444' }}>Conta ORIGEM (de onde sai)</label>
                    <select style={{ ...inputStyle, borderColor: '#ef444455' }} value={reclassForm.contaOrigem} onChange={e => setReclassForm(p => ({ ...p, contaOrigem: e.target.value }))}>
                      <option value="">— Selecione —</option>
                      {contasAnaliticas.map(c => <option key={c.codigo} value={`${c.codigo} — ${c.nome}`}>{c.codigo} — {c.nome}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ ...labelStyle, color: '#10b981' }}>Conta DESTINO (para onde vai)</label>
                    <select style={{ ...inputStyle, borderColor: '#10b98155' }} value={reclassForm.contaDestino} onChange={e => setReclassForm(p => ({ ...p, contaDestino: e.target.value }))}>
                      <option value="">— Selecione —</option>
                      {contasAnaliticas.map(c => <option key={c.codigo} value={`${c.codigo} — ${c.nome}`}>{c.codigo} — {c.nome}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.75rem', alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={labelStyle}>Período DE</label>
                    <input type="month" style={inputStyle} value={reclassForm.mesInicio} onChange={e => setReclassForm(p => ({ ...p, mesInicio: e.target.value }))} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={labelStyle}>Período ATÉ</label>
                    <input type="month" style={inputStyle} value={reclassForm.mesFim} onChange={e => setReclassForm(p => ({ ...p, mesFim: e.target.value }))} />
                  </div>
                  <button onClick={() => {
                    if (!reclassForm.contaOrigem || !reclassForm.contaDestino) { alert('Selecione conta origem e destino.'); return; }
                    if (reclassForm.contaOrigem === reclassForm.contaDestino) { alert('Conta origem e destino são iguais.'); return; }
                    const lancAtual = contabilConfig.lancamentos || [];
                    const fluxoAtual = fluxoCaixa || [];
                    let countLanc = 0, countFluxo = 0;
                    const dentroPeriodo = (data) => {
                      if (!reclassForm.mesInicio && !reclassForm.mesFim) return true;
                      const ym = (data || '').substring(0, 7);
                      if (reclassForm.mesInicio && ym < reclassForm.mesInicio) return false;
                      if (reclassForm.mesFim && ym > reclassForm.mesFim) return false;
                      return true;
                    };
                    // Reclassifica lançamentos contábeis
                    const novosLanc = lancAtual.map(l => {
                      if (!dentroPeriodo(l.data)) return l;
                      let changed = false;
                      const nl = { ...l };
                      if (l.contaDebito === reclassForm.contaOrigem) { nl.contaDebito = reclassForm.contaDestino; changed = true; }
                      if (l.contaCredito === reclassForm.contaOrigem) { nl.contaCredito = reclassForm.contaDestino; changed = true; }
                      if (changed) countLanc++;
                      return nl;
                    });
                    // Reclassifica fluxo de caixa (campo contaContabil)
                    const novosFluxo = fluxoAtual.map(f => {
                      if (!dentroPeriodo(f.data)) return f;
                      if (f.contaContabil === reclassForm.contaOrigem) { countFluxo++; return { ...f, contaContabil: reclassForm.contaDestino }; }
                      return f;
                    });
                    if (countLanc === 0 && countFluxo === 0) { alert('Nenhum lançamento encontrado com a conta origem no período selecionado.'); return; }
                    const periodo = reclassForm.mesInicio || reclassForm.mesFim ? `${reclassForm.mesInicio || '...'} a ${reclassForm.mesFim || '...'}` : 'todos os períodos';
                    if (window.confirm(`Reclassificar:\n\n${countLanc} lançamento(s) contábil(eis)\n${countFluxo} lançamento(s) do fluxo de caixa\n\nDe: ${reclassForm.contaOrigem}\nPara: ${reclassForm.contaDestino}\nPeríodo: ${periodo}`)) {
                      setContabilConfig(prev => ({ ...(prev||{}), lancamentos: novosLanc }));
                      setFluxoCaixa(novosFluxo);
                      alert(`✓ ${countLanc + countFluxo} lançamento(s) reclassificado(s).`);
                      setShowReclass(false);
                      setReclassForm({ contaOrigem: '', contaDestino: '', mesInicio: '', mesFim: '' });
                    }
                  }} style={{ background: '#f59e0b', color: '#1e1b4b', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', height: 38 }}>
                    🔀 Aplicar Reclassificação
                  </button>
                </div>
                <div style={{ fontSize: 11, color: '#8a849c', marginTop: 8 }}>
                  Deixe os campos de período vazios para reclassificar todos os períodos. Afeta lançamentos contábeis e fluxo de caixa.
                </div>
              </div>
            )}

            {/* Tabela de lançamentos */}
            <div style={cardStyle}>
              <div style={{ background: '#f7f5fb', borderRadius: 8, overflowX: 'auto' }}>
                <div style={{ minWidth: 620 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '36px 90px 1fr 1fr 1fr 110px 90px 50px', padding: '8px 12px', background: '#f3f0f9', borderBottom: '1px solid #e5e0ef', gap: '0.5rem' }}>
                  {[
                    { label: 'Nº', col: null },
                    { label: 'Data', col: 'data' },
                    { label: 'Histórico', col: 'historico' },
                    { label: 'Conta Débito', col: 'debito' },
                    { label: 'Conta Crédito', col: 'credito' },
                    { label: 'Valor', col: 'valor' },
                    { label: 'Origem', col: null },
                    { label: 'Rev.', col: null },
                  ].map(h => (
                    <span key={h.label} onClick={h.col ? () => toggleOrdem(h.col) : undefined}
                      style={{ fontSize: 11, color: lancOrdem.col === h.col ? '#6d28d9' : '#5f5874', fontWeight: 700, cursor: h.col ? 'pointer' : 'default', userSelect: 'none' }}>
                      {h.label}{lancOrdem.col === h.col ? (lancOrdem.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </span>
                  ))}
                </div>
                {lancsFiltrados.length === 0 ? (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#5f5874', fontSize: 14 }}>
                    Nenhum lançamento em {MESES_NOMES[parseInt(filMes,10)-1]}/{filAno}.{' '}
                    <button onClick={() => setShowLancForm(true)} style={{ background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontSize: 14, textDecoration: 'underline' }}>Criar o primeiro</button>
                  </div>
                ) : (
                  <>
                    {lancsFiltrados.map((l, i) => {
                      const isFinanceiro = l.origem === 'financeiro';
                      const emEdicao = editandoLancId === l.id;
                      return (
                        <React.Fragment key={l.id}>
                          <div style={{ display: 'grid', gridTemplateColumns: '36px 90px 1fr 1fr 1fr 110px 90px 50px', padding: '9px 12px', borderBottom: emEdicao ? 'none' : '1px solid #eeeaf5', gap: '0.5rem', alignItems: 'center', background: l.reprovado ? 'rgba(239,68,68,0.08)' : isFinanceiro ? 'rgba(16,185,129,0.03)' : emEdicao ? 'rgba(88,166,255,0.06)' : 'transparent' }}>
                            <span style={{ color: '#8a849c', fontSize: 12 }}>{i + 1}</span>
                            <span style={{ color: '#5f5874', fontSize: 12 }}>{l.data ? new Date(l.data+'T00:00').toLocaleDateString('pt-BR') : '—'}</span>
                            <div>
                              <div style={{ color: '#1c1530', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                {l.historico}
                                {isFinanceiro && (
                                  <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 8, background: l.tipo === 'RECEITA' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.12)', color: l.tipo === 'RECEITA' ? '#10b981' : '#dc2626', fontWeight: 700 }}>
                                    {l.tipo === 'RECEITA' ? '↑ Financeiro' : '↓ Financeiro'}
                                  </span>
                                )}
                              </div>
                              {l.documento && <div style={{ color: '#8a849c', fontSize: 11 }}>Doc: {l.documento}</div>}
                              {l.centroCusto && <div style={{ color: '#6d28d9', fontSize: 11 }}>CC: {l.centroCusto}</div>}
                              {isFinanceiro && !mapeamentoContas[l.centroCusto] && (
                                <div style={{ color: '#f59e0b', fontSize: 10 }}>⚠ Categoria sem conta mapeada — use conta padrão</div>
                              )}
                            </div>
                            <span style={{ color: '#ef4444', fontSize: 12 }}>{l.contaDebito}</span>
                            <span style={{ color: '#10b981', fontSize: 12 }}>{l.contaCredito}</span>
                            <span style={{ color: '#1c1530', fontSize: 13, fontFamily: 'monospace', fontWeight: 600 }}>{fmt(l.valor)}</span>
                            <div style={{ display: 'flex', gap: 4, flexDirection: 'column', alignItems: 'center' }}>
                              {periodoFechado ? (
                                <span style={{ fontSize: 10, color: '#10b981' }}>🔒</span>
                              ) : (
                                <>
                                  <button onClick={() => {
                                    if (emEdicao) { setEditandoLancId(null); return; }
                                    setEditandoLancId(l.id);
                                    setEditLancForm({ data: l.data, historico: l.historico, contaDebito: l.contaDebito, contaCredito: l.contaCredito, valor: String(l.valor), documento: l.documento || '', centroCusto: l.centroCusto || '' });
                                  }} style={{ background: emEdicao ? 'rgba(88,166,255,0.15)' : 'transparent', color: '#1d4ed8', border: '1px solid rgba(88,166,255,0.3)', borderRadius: 6, padding: '3px 7px', fontSize: 11, cursor: 'pointer' }}>
                                    {emEdicao ? '✕' : '✏️'}
                                  </button>
                                  {!isFinanceiro && (
                                    <button onClick={() => excluirLancamento(l.id)} style={{ background: 'transparent', color: '#8a849c', border: '1px solid #e5e0ef', borderRadius: 6, padding: '3px 7px', fontSize: 11, cursor: 'pointer' }}>🗑</button>
                                  )}
                                  {isFinanceiro && l.temOverride && (
                                    <span title="Contas editadas manualmente" style={{ fontSize: 9, color: '#f59e0b', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 4, padding: '1px 4px' }}>edit</span>
                                  )}
                                  {isFinanceiro && !l.temOverride && (
                                    <span title="Gerado automaticamente do Financeiro" style={{ fontSize: 9, color: '#8a849c' }}>auto</span>
                                  )}
                                </>
                              )}
                            </div>
                            {/* Coluna Revisão */}
                            <div style={{ textAlign: 'center' }}>
                              <button onClick={() => {
                                setContabilConfig(prev => {
                                  const lancsAtual = (prev||{}).lancamentos || [];
                                  return { ...(prev||{}), lancamentos: lancsAtual.map(lc => lc.id === l.id ? { ...lc, reprovado: !lc.reprovado } : lc) };
                                });
                              }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, opacity: 0.8 }} title={l.reprovado ? 'Clique para aprovar' : 'Clique para reprovar'}>
                                {l.reprovado ? '❌' : '✅'}
                              </button>
                            </div>
                          </div>
                          {/* Formulário de edição inline */}
                          {emEdicao && (
                            <div style={{ background: '#ffffff', border: '1px solid #58a6ff', borderRadius: '0 0 8px 8px', padding: '1rem', marginBottom: 4, borderTop: 'none' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 140px', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <label style={labelStyle}>Data</label>
                                  <input type="date" style={inputStyle} value={editLancForm.data} onChange={e => setEditLancForm(p => ({ ...p, data: e.target.value }))} />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <label style={labelStyle}>Histórico</label>
                                  <input style={inputStyle} value={editLancForm.historico} onChange={e => setEditLancForm(p => ({ ...p, historico: e.target.value }))} />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <label style={labelStyle}>Documento</label>
                                  <input style={inputStyle} value={editLancForm.documento} onChange={e => setEditLancForm(p => ({ ...p, documento: e.target.value }))} />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <label style={labelStyle}>Valor (R$)</label>
                                  <input type="number" step="0.01" style={inputStyle} value={editLancForm.valor} onChange={e => setEditLancForm(p => ({ ...p, valor: e.target.value }))} />
                                </div>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <label style={{ ...labelStyle, color: '#ef4444' }}>Conta Débito (D)</label>
                                  <select style={{ ...inputStyle, borderColor: '#ef444455' }} value={editLancForm.contaDebito} onChange={e => setEditLancForm(p => ({ ...p, contaDebito: e.target.value }))}>
                                    <option value="">— Selecione —</option>
                                    {contasAnaliticas.map(c => <option key={c.codigo} value={`${c.codigo} — ${c.nome}`}>{c.codigo} — {c.nome}</option>)}
                                  </select>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <label style={{ ...labelStyle, color: '#10b981' }}>Conta Crédito (C)</label>
                                  <select style={{ ...inputStyle, borderColor: '#10b98155' }} value={editLancForm.contaCredito} onChange={e => setEditLancForm(p => ({ ...p, contaCredito: e.target.value }))}>
                                    <option value="">— Selecione —</option>
                                    {contasAnaliticas.map(c => <option key={c.codigo} value={`${c.codigo} — ${c.nome}`}>{c.codigo} — {c.nome}</option>)}
                                  </select>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <label style={labelStyle}>Centro de Custo</label>
                                  <input style={inputStyle} value={editLancForm.centroCusto} onChange={e => setEditLancForm(p => ({ ...p, centroCusto: e.target.value }))} />
                                </div>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                <button onClick={() => setEditandoLancId(null)} style={{ background: 'transparent', color: '#5f5874', border: '1px solid #e5e0ef', borderRadius: 8, padding: '7px 16px', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
                                <button onClick={() => salvarEdicaoLanc(l.id)} style={{ background: '#58a6ff', color: 'white', border: 'none', borderRadius: 8, padding: '7px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>💾 Salvar</button>
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                    <div style={{ display: 'grid', gridTemplateColumns: '36px 90px 1fr 1fr 1fr 110px 90px', padding: '10px 12px', background: '#ffffff', gap: '0.5rem', borderTop: '2px solid #e5e0ef' }}>
                      <span></span><span></span>
                      <span style={{ color: '#1c1530', fontWeight: 700, fontSize: 13 }}>TOTAIS · {lancsFiltrados.filter(l => l.origem === 'financeiro').length} do financeiro + {lancsFiltrados.filter(l => !l.origem).length} manuais</span>
                      <span style={{ color: '#ef4444', fontSize: 12 }}>D: {fmt(totalDebito)}</span>
                      <span style={{ color: '#10b981', fontSize: 12 }}>C: {fmt(totalCredito)}</span>
                      <span style={{ color: '#1c1530', fontWeight: 700, fontFamily: 'monospace' }}>{fmt(totalDebito)}</span>
                      <span></span>
                    </div>
                  </>
                )}
                </div>
              </div>
              <div style={{ marginTop: '0.75rem', fontSize: 11, color: '#8a849c' }}>
                Princípio das Partidas Dobradas: todo lançamento tem igual valor a Débito e a Crédito. Total D = Total C = {fmt(totalDebito)}.
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── ABA: DRE (Lei 6.404/76 / NBC TG 26) ── */}
      {aba === 'dre' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '1rem' }}>
          {[
            { id: 'dre', label: 'DRE' },
            { id: 'dlpa', label: 'DLPA' },
            { id: 'bp', label: 'Balanço Patrimonial' },
            { id: 'apuracao', label: 'Apuração DAS' },
          ].map(sub => (
            <button key={sub.id} onClick={() => setDreSubView(sub.id)} style={{
              background: dreSubView === sub.id ? '#7c3aed' : '#f7f5fb',
              color: dreSubView === sub.id ? 'white' : '#5f5874',
              border: `1px solid ${dreSubView === sub.id ? '#7c3aed' : '#e5e0ef'}`,
              borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>{sub.label}</button>
          ))}
        </div>
      )}

      {aba === 'dre' && dreSubView === 'dre' && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              <h3 style={{ color: '#1c1530', margin: '0 0 4px 0' }}>DRE — Demonstração do Resultado do Exercício</h3>
              <span style={{ fontSize: 11, color: '#5f5874' }}>Lei 6.404/76 · NBC TG 26 · {periodoAcum ? `Exercício ${filAno} (Jan–Dez)` : `Período: ${MESES_NOMES[parseInt(filMes,10)-1]}/${filAno}`}</span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ display: 'flex', background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 8, overflow: 'hidden' }}>
                <button onClick={() => setPeriodoAcum(false)} style={{ background: !periodoAcum ? '#7c3aed' : 'transparent', color: !periodoAcum ? 'white' : '#5f5874', border: 'none', padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Mensal</button>
                <button onClick={() => setPeriodoAcum(true)}  style={{ background: periodoAcum  ? '#7c3aed' : 'transparent', color: periodoAcum  ? 'white' : '#5f5874', border: 'none', padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Ano Todo</button>
              </div>
              <button onClick={() => exportarDemonstracoes('dre')} style={{ background: 'rgba(96,165,250,0.15)', color: '#1d4ed8', border: '1px solid rgba(96,165,250,0.4)', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>📄 PDF</button>
            <button onClick={() => {
              const periodo = `${MESES_NOMES[parseInt(filMes,10)-1]}/${filAno}`;
              const despRows = Object.entries(dre.despOpGrupos).map(([g,v]) =>
                `<tr><td style="padding-left:24px">${g}</td><td style="text-align:right;color:red">(${fmt(v)})</td></tr>`).join('');
              const html = `<html><head><meta charset="utf-8"><style>
                body{font-family:Arial,sans-serif;padding:2rem;color:#222;font-size:13px}
                h1{color:#7c3aed;font-size:1.3rem}
                table{width:100%;border-collapse:collapse;margin-top:1rem}
                th{background:#7c3aed;color:white;padding:8px;border:1px solid #ccc;text-align:left}
                td{padding:7px 8px;border:1px solid #e0e0e0}
                .tot{font-weight:700;background:#f0f4ff;border-top:2px solid #7c3aed}
                .result{font-weight:700;font-size:1.05em;background:#e8f5e9;border-top:2px solid #4caf50}
                .neg{background:#fff3f3}
                .sub{color:#555;padding-left:24px}
              </style></head><body>
                <h1>DRE — ${periodo}</h1>
                <p style="font-size:11px;color:#888">Elaborado conforme Lei 6.404/76 e NBC TG 26</p>
                <table>
                  <thead><tr><th>Conta</th><th style="text-align:right;width:180px">Valor (R$)</th></tr></thead>
                  <tbody>
                    <tr><td><strong>1. Receita Bruta de Serviços</strong></td><td style="text-align:right;color:green">${fmt(dre.receitaBruta)}</td></tr>
                    <tr><td class="sub">(-) Impostos sobre a Receita (ISS, PIS, COFINS)</td><td style="text-align:right;color:red">(${fmt(dre.deducoesReceita)})</td></tr>
                    <tr class="tot"><td>2. Receita Líquida de Serviços</td><td style="text-align:right;color:green">${fmt(dre.receitaLiquida)}</td></tr>
                    <tr><td class="sub">(-) Custo dos Serviços Prestados (CSP)</td><td style="text-align:right;color:red">(${fmt(dre.csp)})</td></tr>
                    <tr class="tot"><td>3. Lucro Bruto</td><td style="text-align:right;color:${dre.lucroBruto>=0?'green':'red'}">${fmt(dre.lucroBruto)}</td></tr>
                    <tr><td><strong>(-) Despesas Operacionais</strong></td><td></td></tr>
                    ${despRows}
                    <tr class="tot"><td>Total Despesas Operacionais</td><td style="text-align:right;color:red">(${fmt(dre.totalDespOp)})</td></tr>
                    <tr class="tot"><td>4. Resultado Operacional (EBIT)</td><td style="text-align:right;color:${dre.ebit>=0?'green':'red'}">${fmt(dre.ebit)}</td></tr>
                    <tr><td class="sub">(+/-) Resultado Financeiro</td><td style="text-align:right;color:${dre.resultFinanceiro>=0?'green':'red'}">${fmt(dre.resultFinanceiro)}</td></tr>
                    <tr class="tot"><td>5. LAIR — Lucro Antes do IR e CSLL</td><td style="text-align:right;color:${dre.lair>=0?'green':'red'}">${fmt(dre.lair)}</td></tr>
                    <tr class="${dre.resultadoLiquido>=0?'result':'neg tot'}"><td>6. Resultado Líquido do Período</td><td style="text-align:right;color:${dre.resultadoLiquido>=0?'green':'red'}">${fmt(dre.resultadoLiquido)}</td></tr>
                    ${dre.distribuicaoLucros > 0 ? `<tr style="background:#eff6ff"><td style="color:#1d4ed8;padding-left:24px">(-) Distribuição de Lucros aos Sócios</td><td style="text-align:right;color:#1d4ed8">(${fmt(dre.distribuicaoLucros)})</td></tr><tr class="tot" style="background:#dbeafe"><td style="color:#1d4ed8">= Lucros Retidos no Período</td><td style="text-align:right;color:#1d4ed8">${fmt(dre.resultadoLiquido - dre.distribuicaoLucros)}</td></tr>` : ''}
                  </tbody>
                </table>
                <p style="font-size:11px;color:#888;margin-top:1.5rem">Gerado em ${new Date().toLocaleDateString('pt-BR')} · Kzero Consultoria</p>
              </body></html>`;
              const j = window.open('','_blank'); j.document.write(html); j.document.close(); j.print();
            }} style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Exportar PDF
            </button>
            </div>
          </div>

          {/* Linha DRE helper */}
          {(() => {
            // Fase 2: empresas sem dados de referência retornam objeto vazio — mostraComp evita crash.
            const refAnterior = Number(filAno) >= 2026
              ? (contabilConfig.dre2025 || DADOS_REFERENCIA_2025.dre || {})
              : (contabilConfig.dre2024 || DADOS_REFERENCIA_2024.dre || {});
            const ref2024 = refAnterior;
            const mostraComp = !!(refAnterior && Object.keys(refAnterior).length > 0);

            const GRID = mostraComp ? '1fr 160px 140px' : '1fr 160px';
            const linha = (label, valorAtual, valor2024, cor, corRef, indent, bold, borderTop) => (
              <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: '0.5rem', alignItems: 'center', padding: `${borderTop ? '10px' : '6px'} 0`, paddingLeft: indent ? 16 : 0, borderTop: borderTop ? '1px solid #e5e0ef' : 'none' }}>
                <span style={{ color: bold ? '#1c1530' : '#2d2640', fontWeight: bold ? 700 : 400, fontSize: bold ? 14 : 13 }}>{label}</span>
                <span style={{ color: cor, fontWeight: bold ? 700 : 400, fontSize: bold ? 14 : 13, textAlign: 'right' }}>{valorAtual}</span>
                {mostraComp && <span style={{ color: corRef || '#8a849c', fontWeight: 400, fontSize: 12, textAlign: 'right', opacity: 0.75 }}>{valor2024}</span>}
              </div>
            );

            return (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Cabeçalho colunas */}
                <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: '0.5rem', padding: '6px 0', marginBottom: 4, borderBottom: '1px solid #e5e0ef' }}>
                  <span style={{ fontSize: 11, color: '#5f5874' }}>Conta</span>
                  <span style={{ fontSize: 11, color: '#1d4ed8', fontWeight: 700, textAlign: 'right' }}>{MESES_NOMES[parseInt(filMes,10)-1]}/{filAno}</span>
                  {mostraComp && <span style={{ fontSize: 11, color: '#5f5874', textAlign: 'right' }}>2024 (SPED)</span>}
                </div>

                {/* Receitas */}
                <div style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 8, padding: '1rem', marginBottom: '0.5rem' }}>
                  <div style={{ color: '#10b981', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', marginBottom: 8 }}>Receitas</div>
                  {linha('1. Receita Bruta de Serviços', fmt(dre.receitaBruta), fmt(ref2024.receitaBruta), '#10b981', '#5f5874', false, false, false)}
                  {(dre.deducoesReceita > 0 || ref2024.deducoesReceita > 0) && linha('(-) Impostos sobre a Receita', `(${fmt(dre.deducoesReceita)})`, `(${fmt(ref2024.deducoesReceita)})`, '#ef4444', '#5f5874', true, false, false)}
                  {linha('2. Receita Líquida de Serviços', fmt(dre.receitaLiquida), fmt(ref2024.receitaLiquida), '#10b981', '#5f5874', false, true, true)}
                  {linha('(-) Custo dos Serviços Prestados (CSP)', `(${fmt(dre.csp)})`, `(${fmt(ref2024.csp)})`, '#ef4444', '#5f5874', true, false, false)}
                  {linha('3. Lucro Bruto', fmt(dre.lucroBruto), fmt(ref2024.lucroBruto), dre.lucroBruto >= 0 ? '#10b981' : '#ef4444', '#5f5874', false, true, true)}
                </div>

                {/* Despesas operacionais */}
                <div style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '1rem', marginBottom: '0.5rem' }}>
                  <div style={{ color: '#ef4444', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', marginBottom: 8 }}>(-) Despesas Operacionais</div>
                  {Object.entries(dre.despOpGrupos).map(([g, v]) => {
                    const v2024 = ref2024.despOpGrupos?.[g] ?? 0;
                    return (
                      <div key={g} style={{ display: 'grid', gridTemplateColumns: GRID, gap: '0.5rem', padding: '5px 0', paddingLeft: 12 }}>
                        <span style={{ color: '#2d2640', fontSize: 13 }}>{g}</span>
                        <span style={{ color: '#ef4444', fontSize: 13, textAlign: 'right' }}>({fmt(v)})</span>
                        {mostraComp && <span style={{ color: '#8a849c', fontSize: 12, textAlign: 'right', opacity: 0.75 }}>({fmt(v2024)})</span>}
                      </div>
                    );
                  })}
                  {linha('Total Despesas Operacionais', `(${fmt(dre.totalDespOp)})`, `(${fmt(ref2024.totalDespOp)})`, '#ef4444', '#5f5874', false, true, true)}
                </div>

                {/* Resultado */}
                <div style={{ background: '#f7f5fb', borderRadius: 8, padding: '1rem', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {linha('4. Resultado Operacional (EBIT)', fmt(dre.ebit), fmt(ref2024.ebit), dre.ebit >= 0 ? '#10b981' : '#ef4444', '#5f5874', false, true, false)}
                  {linha('(+/-) Resultado Financeiro Líquido', fmt(dre.resultFinanceiro), fmt(ref2024.resultFinanceiro), dre.resultFinanceiro >= 0 ? '#10b981' : '#ef4444', '#5f5874', true, false, false)}
                  {linha('5. LAIR — Lucro Antes do IR e CSLL', fmt(dre.lair), fmt(ref2024.lair), dre.lair >= 0 ? '#10b981' : '#ef4444', '#5f5874', false, true, true)}
                  <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: '0.5rem', background: dre.resultadoLiquido >= 0 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', borderRadius: 6, padding: '10px 12px', marginTop: 4 }}>
                    <span style={{ color: '#1c1530', fontWeight: 700, fontSize: 15 }}>6. Resultado Líquido do Período</span>
                    <span style={{ color: dre.resultadoLiquido >= 0 ? '#10b981' : '#ef4444', fontWeight: 700, fontSize: 15, textAlign: 'right' }}>{fmt(dre.resultadoLiquido)}</span>
                    {mostraComp && <span style={{ color: '#5f5874', fontSize: 13, textAlign: 'right', alignSelf: 'center' }}>{fmt(ref2024.resultadoLiquido)}</span>}
                  </div>
                  {dre.distribuicaoLucros > 0 && (
                    <div style={{ marginTop: '0.5rem', background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.3)', borderRadius: 8, padding: '0.75rem 1rem' }}>
                      <div style={{ fontSize: 11, color: '#1d4ed8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Destinação do Resultado (pós-DRE)</div>
                      {linha('(-) Distribuição de Lucros aos Sócios', `(${fmt(dre.distribuicaoLucros)})`, `(${fmt(ref2024.distribuicaoLucros)})`, '#60a5fa', '#5f5874', true, false, false)}
                      {linha('= Lucros Retidos no Período', fmt(dre.resultadoLiquido - dre.distribuicaoLucros), fmt(ref2024.lucrosRetidos ?? ((ref2024.resultadoLiquido || 0) - (ref2024.distribuicaoLucros || 0))), (dre.resultadoLiquido - dre.distribuicaoLucros) >= 0 ? '#60a5fa' : '#ef4444', '#5f5874', false, true, true)}
                    </div>
                  )}
                </div>
                {!mostraComp && (
                  <div style={{ marginTop: '0.75rem', fontSize: 12, color: '#5f5874', textAlign: 'center', padding: '8px', background: '#ffffff', borderRadius: 8, border: '1px solid #eeeaf5' }}>
                    💡 Vá em <strong style={{ color: '#1c1530' }}>⚙️ Configurações</strong> e importe os dados do SPED 2024 para ver a coluna comparativa.
                  </div>
                )}
              </div>
            );
          })()}
          <div style={{ marginTop: '0.75rem', fontSize: 11, color: '#8a849c', textAlign: 'right' }}>
            Elaborado conforme Lei 6.404/76 e NBC TG 26 · Regime de Competência (data do evento) · Recebimento = 5º DU do mês seguinte · Entidade: {empNomeDemonstracoes} · CNPJ {empCnpj}
          </div>
        </div>
      )}

      {/* ── ABA: BALANCETE (NBC TG) ── */}
      {/* ── ABA: FECHAMENTO DO MÊS (assistente; etapa 1 = extratos) ── */}
      {aba === 'fechamentoMes' && (
        <FechamentoMes
          empresaSlug={empresaAtiva?.slug}
          empresaCnpj={(empresaContabil.config || {}).cnpj || empresaAtiva?.cnpj || ''}
          filMes={filMes}
          filAno={filAno}
          mesesNomes={MESES_NOMES}
          planoContas={contabilConfig.planoContas || PLANO_CONTAS_PADRAO}
          regrasJson={contabilConfig.regrasClassificacao?.length ? contabilConfig.regrasClassificacao : REGRAS_CLASSIFICACAO_PADRAO}
          inicioLancamentos={inicioLancamentos}
          lancamentosContabeis={lancamentosContabeis}
          balancetesFechados={contabilConfig.balancetesFechados}
          balancete={balanceteMensal}
          fechamento={fechamentoCtx}
          validacoesAntigas={contabilConfig.validacoesBalancete}
          isAdmin={isAdmin}
          onRemoverFechamentoAntigo={(per) => setContabilConfig(prev => {
            const f = { ...((prev || {}).balancetesFechados || {}) };
            delete f[per];
            return { ...(prev || {}), balancetesFechados: f };
          })}
          onImportado={recarregarLancamentosDb}
          cardStyle={cardStyle}
          inputStyle={inputStyle}
          labelStyle={labelStyle}
        />
      )}

      {aba === 'balancete' && (
        <div style={cardStyle}>
          {(() => {
            const chPeriodo = `${filAno}-${filMes}`;
            // Fechamento: a fonte é contabil_fechamentos (assistente Fechamento do mês).
            // Os fechamentos antigos (contabilConfig.balancetesFechados) continuam valendo
            // como histórico até serem reabertos pelo assistente; nada novo grava ali.
            const fechadosDb = {};
            fechamentoCtx.fechamentos.forEach(f => {
              fechadosDb[f.periodo] = { ...(f.snapshot || {}), fechadoEm: f.fechado_em || (f.snapshot || {}).fechadoEm };
            });
            const fechados = { ...(contabilConfig.balancetesFechados || {}), ...fechadosDb };
            const estaFechado = !!fechados[chPeriodo];
            const dadosFechado = fechados[chPeriodo] || null;

            // Validações obrigatórias antes do fechamento
            const errosFechamento = (() => {
              const erros = [];
              // 1. Sem lançamentos
              if (balancete.linhas.length === 0)
                erros.push({ tipo: 'bloqueio', msg: 'Nenhum lançamento no período.' });
              // 2. Categorias não mapeadas (conta 9.9.9)
              const naoClass = balancete.linhas.find(l => l.codigo === '9.9.9');
              if (naoClass)
                erros.push({ tipo: 'bloqueio', msg: `Existem lançamentos sem conta contábil (9.9.9 — A Classificar, ${fmt(naoClass.debitos + naoClass.creditos)}). Classifique-os em Fechamento do mês → Classificar.` });
              // 3. Débito ≠ Crédito (desequilíbrio nas partidas dobradas)
              const diff = Math.abs(balancete.totalDebitos - balancete.totalCreditos);
              if (diff > 0.01)
                erros.push({ tipo: 'bloqueio', msg: `Total Débito ≠ Total Crédito (diferença de ${fmt(diff)}). Verifique os lançamentos contábeis.` });
              // 4. Alertas não críticos (não bloqueiam, apenas avisam)
              const ativoNegativo = balancete.linhas.filter(l => l.tipo === 'A' && (l.saldoFinal || 0) < -0.01);
              if (ativoNegativo.length > 0)
                erros.push({ tipo: 'aviso', msg: `${ativoNegativo.length} conta(s) de Ativo com saldo negativo: ${ativoNegativo.map(l => l.codigo).join(', ')}` });
              const passivoNegativo = balancete.linhas.filter(l => l.tipo === 'P' && (l.saldoFinal || 0) < -0.01);
              if (passivoNegativo.length > 0)
                erros.push({ tipo: 'aviso', msg: `${passivoNegativo.length} conta(s) de Passivo com saldo negativo: ${passivoNegativo.map(l => l.codigo).join(', ')}` });
              return erros;
            })();
            // Fechar e reabrir agora só pelo assistente Fechamento do mês (que exige todas
            // as contas validadas e nada em A Classificar, e registra a justificativa da
            // reabertura). Os botões daqui levam para lá.
            const irParaFechamento = () => setAba('fechamentoMes');

            return (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <h3 style={{ color: '#1c1530', margin: '0 0 4px 0' }}>Balancete de Verificação — por Conta Contábil</h3>
                      {estaFechado && (
                        <>
                          <span style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.4)', borderRadius: 20, padding: '3px 12px', fontSize: 11, color: '#10b981', fontWeight: 700 }}>
                            FECHADO
                          </span>
                          <button onClick={irParaFechamento} title="A reabertura é feita no Fechamento do mês, com justificativa" style={{ background: 'transparent', border: '1px solid #f59e0b', borderRadius: 8, color: '#f59e0b', padding: '3px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                            Reabrir no Fechamento do mês
                          </button>
                        </>
                      )}
                    </div>
                    <span style={{ fontSize: 11, color: '#5f5874' }}>
                      Período: {balanceteAnual ? `Exercício ${filAno} (Jan–Dez)` : `${MESES_NOMES[parseInt(filMes,10)-1]}/${filAno}`} · Agrupado pelo Plano de Contas
                      {estaFechado && dadosFechado && !balanceteAnual && (
                        <span style={{ color: '#10b981', marginLeft: 8 }}>
                          · Fechado em {new Date(dadosFechado.fechadoEm).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'inline-flex', background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, overflow: 'hidden' }}>
                      <button onClick={() => setBalanceteAnual(false)} style={{ background: !balanceteAnual ? '#7c3aed' : 'transparent', color: !balanceteAnual ? 'white' : '#5f5874', border: 'none', padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Mensal</button>
                      <button onClick={() => setBalanceteAnual(true)}  style={{ background: balanceteAnual  ? '#7c3aed' : 'transparent', color: balanceteAnual  ? 'white' : '#5f5874', border: 'none', padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Anual</button>
                    </div>
                    <button onClick={() => exportarDemonstracoes('balancete')} style={{ background: 'rgba(96,165,250,0.15)', color: '#1d4ed8', border: '1px solid rgba(96,165,250,0.4)', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      📄 Balancete PDF
                    </button>
                    <button onClick={exportarBalanceteQuestor} style={{ background: 'rgba(249,168,37,0.15)', color: '#f9a825', border: '1px solid rgba(249,168,37,0.4)', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }} title="Balancete analítico estilo Questor/Domínio — para validar saldos com o contador">
                      📊 Balancete Analítico
                    </button>
                    <button onClick={exportarDemonstracoesJunta} style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.4)', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }} title="Demonstrações Contábeis aplicadas a Microentidades (NBC TG 1002) — para arquivamento na Junta Comercial">
                      🏛️ Demonstrações à Junta
                    </button>
                    <button onClick={() => exportarDemonstracoes('completo')} style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      📋 Demonstrações Completas
                    </button>
                    {!estaFechado && (
                      <button onClick={irParaFechamento} title="O fechamento é feito no assistente Fechamento do mês" style={{ background: 'rgba(16,185,129,0.15)', color: '#047857', border: '1px solid rgba(16,185,129,0.4)', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        Fechar no Fechamento do mês
                      </button>
                    )}
                    <button onClick={() => {
                      const linhasExib = estaFechado ? dadosFechado.linhas : balancete.linhas;
                      const totDeb = estaFechado ? dadosFechado.totalDebitos : balancete.totalDebitos;
                      const totCred = estaFechado ? dadosFechado.totalCreditos : balancete.totalCreditos;
                      const rows = linhasExib.map(l => { const sF = l.saldoFinal != null ? l.saldoFinal : ((l.saldoAnterior||0)+l.creditos-l.debitos); return `<tr><td style="font-family:monospace">${l.codigo}</td><td>${l.nome}</td><td style="text-align:right;color:blue">${fmt(l.saldoAnterior||0)}</td><td style="text-align:right;color:red">${fmt(l.debitos||0)}</td><td style="text-align:right;color:green">${fmt(l.creditos||0)}</td><td style="text-align:right;font-weight:700;color:${sF>=0?'green':'red'}">${fmt(sF)}</td></tr>`; }).join('');
                      const html = `<html><head><meta charset="utf-8"><style>body{font-family:Arial;padding:2rem;font-size:13px}table{width:100%;border-collapse:collapse}th{background:#7c3aed;color:white;padding:8px;border:1px solid #ccc}td{padding:7px 8px;border:1px solid #e0e0e0}.tot{font-weight:700;background:#f0f4ff}</style></head><body><h2>Balancete de Verificação — ${MESES_NOMES[parseInt(filMes,10)-1]}/${filAno}${estaFechado?' (FECHADO)':''}</h2><p style="font-size:11px;color:#666">NBC TG 26 / Lei 6.404/76</p><table><thead><tr><th>Código</th><th>Conta</th><th style="text-align:right">Saldo Anterior</th><th style="text-align:right">Déb. Período</th><th style="text-align:right">Créd. Período</th><th style="text-align:right">Saldo Final</th></tr></thead><tbody>${rows}<tr class="tot"><td colspan="2">TOTAIS</td><td></td><td style="text-align:right;color:red">${fmt(totDeb)}</td><td style="text-align:right;color:green">${fmt(totCred)}</td><td style="text-align:right">${fmt(totCred-totDeb)}</td></tr></tbody></table></body></html>`;
                      const w = window.open('','_blank'); w.document.write(html); w.document.close(); w.print();
                    }} style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      Exportar PDF
                    </button>
                  </div>
                </div>

                {/* Painel de validação pré-fechamento */}
                {!estaFechado && errosFechamento.length > 0 && (
                  <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {errosFechamento.map((e, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', borderRadius: 8, background: e.tipo === 'bloqueio' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)', border: `1px solid ${e.tipo === 'bloqueio' ? 'rgba(239,68,68,0.35)' : 'rgba(245,158,11,0.35)'}` }}>
                        <span style={{ fontSize: 15, flexShrink: 0 }}>{e.tipo === 'bloqueio' ? '⛔' : '⚠️'}</span>
                        <span style={{ fontSize: 12, color: e.tipo === 'bloqueio' ? '#ef4444' : '#f59e0b', lineHeight: 1.5 }}>{e.msg}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Lista de períodos fechados */}
                {Object.keys(fechados).length > 0 && (
                  <div style={{ marginBottom: '1rem', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: '#5f5874' }}>Períodos fechados:</span>
                    {Object.keys(fechados).sort().map(p => {
                      const [a, m] = p.split('-');
                      return (
                        <span key={p} style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 12, padding: '2px 10px', fontSize: 11, color: '#10b981', cursor: 'pointer', fontWeight: p === chPeriodo ? 700 : 400 }}
                          onClick={() => { setFilAno(a); setFilMes(m); }}>
                          {MESES_NOMES[parseInt(m,10)-1]}/{a}
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Tabela do Balancete com flags de validação */}
                {(() => {
                  const linhasExib = estaFechado ? dadosFechado.linhas : balancete.linhas;
                  const totDeb = estaFechado ? dadosFechado.totalDebitos : balancete.totalDebitos;
                  const totCred = estaFechado ? dadosFechado.totalCreditos : balancete.totalCreditos;
                  const chPer = chPeriodo;

                  // Validação por conta: fonte única em contabil_validacoes (mesma do assistente
                  // Fechamento do mês). contabilConfig.validacoesBalancete não é mais gravado;
                  // o assistente oferece trazer o que estava lá ("Trazer validações anteriores").
                  // A situação é sempre calculada sobre o balancete MENSAL do período.
                  const validacoesDb = new Map(fechamentoCtx.validacoes.map(v => [v.conta_codigo, v]));
                  const mensalPorCodigo = new Map(balanceteMensal.linhas.map(m => [m.codigo, m]));
                  const situacaoDe = (codigo) => {
                    const lm = mensalPorCodigo.get(codigo);
                    if (!lm || codigo === '9.9.9') return 'pendente';
                    return situacaoValidacao(lm, validacoesDb.get(codigo), (balanceteMensal.lancamentosPorConta || {})[codigo] || []);
                  };
                  const STATUS_DA_SITUACAO = { validada: 'validado', desatualizada: 'desatualizado', pendente: 'pendente' };
                  const validacoes = {};
                  linhasExib.forEach(l => { validacoes[`${chPer}||${l.codigo}`] = STATUS_DA_SITUACAO[situacaoDe(l.codigo)]; });
                  const podeValidarAqui = !balanceteAnual && !fechamentoCtx.carregando;

                  const toggleValidacao = async (codigo, status) => {
                    const lm = mensalPorCodigo.get(codigo);
                    if (!lm || codigo === '9.9.9' || !podeValidarAqui) return;
                    const obs = (validacoesDb.get(codigo) || {}).observacao || '';
                    setAvisoBalancete(null);
                    const r = status === 'validado'
                      ? await fechamentoCtx.validar(codigo, { observacao: obs, totais: totaisDaLinha(lm) })
                      : await fechamentoCtx.desfazer(codigo, { observacao: obs });
                    if (!r.ok) setAvisoBalancete('Não foi possível gravar a validação da conta. Tente novamente.');
                  };

                  // Checagens automáticas por linha (mesma função do assistente)
                  const flagAuto = flagConta;

                  if (linhasExib.length === 0) return (
                    <div style={{ textAlign: 'center', color: '#5f5874', padding: '2rem', fontSize: 14 }}>
                      Nenhum lançamento no período. Configure o mapeamento em ⚙️ Configurações.
                    </div>
                  );

                  const contValid = linhasExib.filter(l => validacoes[`${chPer}||${l.codigo}`] === 'validado').length;
                  const contNaoValid = linhasExib.length - contValid;
                  const contDesatualizadas = linhasExib.filter(l => validacoes[`${chPer}||${l.codigo}`] === 'desatualizado').length;

                  return (
                    <>
                      {/* Resumo de validação */}
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, color: '#5f5874' }}>Validação das contas:</span>
                        <span style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 12, padding: '3px 12px', fontSize: 12, color: '#10b981', fontWeight: 600 }}>
                          ✓ {contValid} validadas
                        </span>
                        <span style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 12, padding: '3px 12px', fontSize: 12, color: '#f59e0b', fontWeight: 600 }}>
                          ⏳ {contNaoValid} pendentes
                        </span>
                        {contDesatualizadas > 0 && (
                          <span style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 12, padding: '3px 12px', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>
                            {contDesatualizadas} alterada(s) após a validação
                          </span>
                        )}
                        {balanceteAnual && (
                          <span style={{ fontSize: 11, color: '#5f5874' }}>A validação é feita no balancete mensal.</span>
                        )}
                        {avisoBalancete && (
                          <span role="alert" style={{ fontSize: 12, color: '#b91c1c' }}>{avisoBalancete}</span>
                        )}
                        {linhasExib.some(l => flagAuto(l)?.tipo === 'erro') && (
                          <span style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 12, padding: '3px 12px', fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                            ✗ {linhasExib.filter(l => flagAuto(l)?.tipo === 'erro').length} erro{linhasExib.filter(l => flagAuto(l)?.tipo === 'erro').length>1?'s':''}
                          </span>
                        )}
                      </div>

                      <div style={{ background: '#f7f5fb', borderRadius: 8, overflowX: 'auto' }}>
                        <div style={{ minWidth: 700 }}>
                        {/* Cabeçalho — NBC TG 26: Saldo Anterior | Movimentos | Saldo Final */}
                        <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 110px 110px 110px 110px 100px 50px', padding: '8px 12px', background: '#f3f0f9', borderBottom: '1px solid #e5e0ef', gap: '0.5rem' }}>
                          {['Código', 'Conta Contábil', 'Saldo Ant.', 'Déb. Período', 'Créd. Período', 'Saldo Final', 'Validação', 'Rev.'].map(h => (
                            <span key={h} style={{ fontSize: 11, color: '#5f5874', fontWeight: 700 }}>{h}</span>
                          ))}
                        </div>
                        {linhasExib.map(l => {
                          const saldoAnt = l.saldoAnterior || 0;
                          const saldoFinal = l.saldoFinal != null ? l.saldoFinal : saldoAnt;
                          const TIPO_COR2 = { A: '#10b981', P: '#ef4444', PL: '#a78bfa', R: '#10b981', D: '#f87171', DR: '#60a5fa' };
                          const isNaoMapeado = l.codigo === '9.9.9';
                          const flag = flagAuto(l);
                          const vk = `${chPer}||${l.codigo}`;
                          const vStatus = validacoes[vk] || 'pendente';
                          const isReprovado = (contabilConfig.contasReprovadas || {})[chPer+'||'+l.codigo];
                          const bgRow = isReprovado ? 'rgba(239,68,68,0.1)' : isNaoMapeado ? 'rgba(239,68,68,0.05)' : flag?.tipo === 'alerta' ? 'rgba(245,158,11,0.03)' : vStatus === 'validado' ? 'rgba(16,185,129,0.03)' : 'transparent';
                          const isExpanded = contaExpandida === l.codigo;
                          const lancsDrillDown = (balancete.lancamentosPorConta || {})[l.codigo] || [];
                          return (
                            <React.Fragment key={l.codigo}>
                              {/* Linha da conta — clicável para drill-down */}
                              <div onClick={() => setContaExpandida(isExpanded ? null : l.codigo)}
                                style={{ display: 'grid', gridTemplateColumns: '100px 1fr 110px 110px 110px 110px 100px 50px', padding: '9px 12px', borderBottom: isExpanded ? 'none' : '1px solid #eeeaf5', gap: '0.5rem', alignItems: 'center', background: isExpanded ? 'rgba(124,58,237,0.08)' : bgRow, cursor: 'pointer' }}>
                                <span style={{ color: TIPO_COR2[l.tipo] || '#5f5874', fontSize: 12, fontFamily: 'monospace', fontWeight: 600 }}>{l.codigo}</span>
                                <span style={{ color: isNaoMapeado ? '#f59e0b' : '#2d2640', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ color: '#8a849c', fontSize: 10 }}>{isExpanded ? '▼' : '▶'}</span>
                                  {l.nome}
                                  {flag && (
                                    <span title={flag.msg} style={{ marginLeft: 4, fontSize: 10, padding: '1px 6px', borderRadius: 8, background: flag.tipo === 'erro' ? 'rgba(239,68,68,0.15)' : flag.tipo === 'alerta' ? 'rgba(245,158,11,0.15)' : 'rgba(96,165,250,0.12)', color: flag.tipo === 'erro' ? '#ef4444' : flag.tipo === 'alerta' ? '#f59e0b' : '#1d4ed8' }}>
                                      {flag.tipo === 'erro' ? '✗' : flag.tipo === 'alerta' ? '⚠' : 'ℹ'} {flag.msg}
                                    </span>
                                  )}
                                </span>
                                <span style={{ color: saldoAnt !== 0 ? '#1d4ed8' : '#8a849c', fontSize: 13, fontFamily: 'monospace' }}>{fmt(saldoAnt)}</span>
                                <span style={{ color: l.debitos > 0 ? '#ef4444' : '#8a849c', fontSize: 13, fontFamily: 'monospace' }}>{fmt(l.debitos)}</span>
                                <span style={{ color: l.creditos > 0 ? '#10b981' : '#8a849c', fontSize: 13, fontFamily: 'monospace' }}>{fmt(l.creditos)}</span>
                                <span style={{ color: saldoFinal >= 0 ? '#10b981' : '#ef4444', fontSize: 13, fontFamily: 'monospace', fontWeight: 600 }}>{fmt(saldoFinal)}</span>
                                {estaFechado || !podeValidarAqui || isNaoMapeado ? (
                                  <span style={{ fontSize: 11, color: vStatus === 'validado' ? '#10b981' : vStatus === 'desatualizado' ? '#b91c1c' : '#5f5874' }} onClick={e => e.stopPropagation()}>
                                    {vStatus === 'validado' ? '✓ Validado' : vStatus === 'desatualizado' ? 'Alterada' : '—'}
                                  </span>
                                ) : (
                                  <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                                    <button onClick={() => toggleValidacao(l.codigo, vStatus === 'validado' ? 'pendente' : 'validado')}
                                      title={vStatus === 'validado' ? 'Desfazer a validação' : vStatus === 'desatualizado' ? 'Alterada após a validação: validar novamente' : 'Marcar como validada'}
                                      style={{ background: vStatus === 'validado' ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.05)', border: `1px solid ${vStatus === 'validado' ? 'rgba(16,185,129,0.4)' : vStatus === 'desatualizado' ? 'rgba(239,68,68,0.4)' : '#e5e0ef'}`, borderRadius: 6, padding: '3px 8px', fontSize: 11, color: vStatus === 'validado' ? '#10b981' : vStatus === 'desatualizado' ? '#b91c1c' : '#5f5874', cursor: 'pointer', fontWeight: 600 }}>
                                      {vStatus === 'validado' ? '✓ OK' : vStatus === 'desatualizado' ? 'Revalidar' : 'Validar'}
                                    </button>
                                  </div>
                                )}
                                {/* Coluna Reprovação */}
                                <div style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                  <button onClick={() => {
                                    // Marca/desmarca reprovado em todas as linhas do balancete dessa conta
                                    setContabilConfig(prev => {
                                      const reprovadas = { ...((prev||{}).contasReprovadas || {}) };
                                      const key = chPer + '||' + l.codigo;
                                      if (reprovadas[key]) delete reprovadas[key]; else reprovadas[key] = true;
                                      return { ...(prev||{}), contasReprovadas: reprovadas };
                                    });
                                  }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}
                                    title={(contabilConfig.contasReprovadas || {})[chPer+'||'+l.codigo] ? 'Clique para aprovar' : 'Clique para reprovar'}>
                                    {(contabilConfig.contasReprovadas || {})[chPer+'||'+l.codigo] ? '❌' : '✅'}
                                  </button>
                                </div>
                              </div>

                              {/* Painel drill-down de lançamentos */}
                              {isExpanded && (
                                <div style={{ background: '#ffffff', borderBottom: '1px solid #e5e0ef', padding: '12px 16px' }}>
                                  <div style={{ fontSize: 12, color: '#5f5874', marginBottom: 8, fontWeight: 600 }}>
                                    Lançamentos de {l.nome} ({l.codigo}) — {MESES_NOMES[parseInt(filMes,10)-1]}/{filAno}
                                    <span style={{ marginLeft: 8, color: '#8a849c', fontWeight: 400 }}>({lancsDrillDown.length} lançamento{lancsDrillDown.length !== 1 ? 's' : ''})</span>
                                  </div>
                                  {lancsDrillDown.length === 0 ? (
                                    <div style={{ color: '#8a849c', fontSize: 12 }}>Nenhum lançamento neste período.</div>
                                  ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                      {/* Header drill-down */}
                                      <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 80px 80px 120px', gap: '0.5rem', padding: '4px 8px', borderBottom: '1px solid #eeeaf5' }}>
                                        {['Data', 'Histórico', 'D/C', 'Valor', 'Origem'].map(h => (
                                          <span key={h} style={{ fontSize: 10, color: '#8a849c', fontWeight: 700 }}>{h}</span>
                                        ))}
                                      </div>
                                      {lancsDrillDown.map((lc, li) => {
                                        const isEditando = editandoLancManual && editandoLancManual.idx === lc.lancIdx && lc.tipo === 'manual';
                                        if (isEditando) {
                                          return (
                                            <div key={li} style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                                                <div>
                                                  <div style={{ fontSize: 10, color: '#5f5874', marginBottom: 3 }}>Data</div>
                                                  <input type="date" value={editandoLancManual.form.data || ''} onChange={e => setEditandoLancManual(prev => ({ ...prev, form: { ...prev.form, data: e.target.value } }))} style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 6, padding: '5px 8px', color: '#1c1530', fontSize: 12, width: '100%' }} />
                                                </div>
                                                <div>
                                                  <div style={{ fontSize: 10, color: '#5f5874', marginBottom: 3 }}>Valor (R$)</div>
                                                  <input type="number" step="0.01" value={editandoLancManual.form.valor || ''} onChange={e => setEditandoLancManual(prev => ({ ...prev, form: { ...prev.form, valor: e.target.value } }))} style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 6, padding: '5px 8px', color: '#1c1530', fontSize: 12, width: '100%' }} />
                                                </div>
                                                <div>
                                                  <div style={{ fontSize: 10, color: '#5f5874', marginBottom: 3 }}>Histórico</div>
                                                  <input type="text" value={editandoLancManual.form.historico || ''} onChange={e => setEditandoLancManual(prev => ({ ...prev, form: { ...prev.form, historico: e.target.value } }))} style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 6, padding: '5px 8px', color: '#1c1530', fontSize: 12, width: '100%' }} />
                                                </div>
                                              </div>
                                              <div style={{ display: 'flex', gap: 6 }}>
                                                <button onClick={() => {
                                                  const novosLanc = [...(contabilConfig.lancamentos || [])];
                                                  novosLanc[lc.lancIdx] = { ...novosLanc[lc.lancIdx], ...editandoLancManual.form };
                                                  setContabilConfig({ ...contabilConfig, lancamentos: novosLanc });
                                                  setEditandoLancManual(null);
                                                }} style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Salvar</button>
                                                <button onClick={() => setEditandoLancManual(null)} style={{ background: 'transparent', color: '#5f5874', border: '1px solid #e5e0ef', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>Cancelar</button>
                                              </div>
                                            </div>
                                          );
                                        }
                                        return (
                                          <div key={li} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 80px 80px 120px', gap: '0.5rem', padding: '5px 8px', borderRadius: 6, alignItems: 'center', background: lc.tipo === 'manual' ? 'rgba(124,58,237,0.05)' : 'transparent' }}>
                                            <span style={{ fontSize: 12, color: '#2d2640', fontFamily: 'monospace' }}>{lc.data}</span>
                                            <span style={{ fontSize: 12, color: '#2d2640' }}>{lc.historico}</span>
                                            <span style={{ fontSize: 12, fontWeight: 700, color: lc.lado === 'D' ? '#ef4444' : '#10b981' }}>{lc.lado === 'D' ? 'Débito' : 'Crédito'}</span>
                                            <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#1c1530' }}>{fmt(lc.valor)}</span>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                              <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 8, background: lc.tipo === 'manual' ? 'rgba(124,58,237,0.15)' : 'rgba(96,165,250,0.1)', color: lc.tipo === 'manual' ? '#6d28d9' : '#1d4ed8' }}>{lc.origem}</span>
                                              {lc.tipo === 'manual' && lc.lancIdx != null && (
                                                <button onClick={() => setEditandoLancManual({ idx: lc.lancIdx, form: { ...(contabilConfig.lancamentos || [])[lc.lancIdx] } })}
                                                  style={{ background: 'transparent', border: '1px solid #e5e0ef', borderRadius: 5, padding: '2px 7px', fontSize: 10, color: '#5f5874', cursor: 'pointer' }}>✏ Editar</button>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                        <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 110px 110px 110px 110px 100px', padding: '10px 12px', background: '#ffffff', gap: '0.5rem', borderTop: '2px solid #e5e0ef' }}>
                          <span></span>
                          <span style={{ color: '#1c1530', fontWeight: 700, fontSize: 13 }}>TOTAIS</span>
                          <span style={{ color: '#8a849c', fontFamily: 'monospace', fontSize: 13 }}>—</span>
                          <span style={{ color: '#ef4444', fontWeight: 700, fontFamily: 'monospace' }}>{fmt(totDeb)}</span>
                          <span style={{ color: '#10b981', fontWeight: 700, fontFamily: 'monospace' }}>{fmt(totCred)}</span>
                          <span style={{ color: (totCred - totDeb) >= 0 ? '#10b981' : '#ef4444', fontWeight: 700, fontFamily: 'monospace' }}>{fmt(totCred - totDeb)}</span>
                          <span></span>
                        </div>
                        </div>
                      </div>
                      <div style={{ marginTop: '0.75rem', fontSize: 11, color: '#8a849c', textAlign: 'right' }}>
                        NBC TG 26 · Clique em uma conta para ver os lançamentos · Código 9.9.9 = A Classificar (classifique em Fechamento do mês)
                      </div>
                    </>
                  );
                })()}

              </>
            );
          })()}

        </div>
      )}

      {/* ── ABA: VALIDAÇÃO ── */}
      {aba === 'lancamentos' && (
        <div style={cardStyle}>
          <h3 style={{ color: '#1c1530', margin: '0 0 1rem 0' }}>Validação de Movimentações</h3>
          {fluxoFiltradoPeriodo.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#5f5874', padding: '2rem', fontSize: 14 }}>
              Nenhuma movimentação no período selecionado.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {/* Resumo de status */}
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                {['pendente','validado','rejeitado'].map(st => {
                  const count = fluxoFiltradoPeriodo.filter(f => (f.status || 'pendente') === st).length;
                  const b = STATUS_BADGE[st] || STATUS_BADGE.pendente;
                  return <span key={st} style={{ background: b.bg, border: `1px solid ${b.border}`, borderRadius: 20, padding: '4px 14px', fontSize: 12, color: b.color }}>{b.label}: {count}</span>;
                })}
              </div>
              {fluxoFiltradoPeriodo.map(f => {
                const status = f.status || 'pendente';
                const b = STATUS_BADGE[status] || STATUS_BADGE.pendente;
                const editando = editandoId === f.id;
                return (
                  <div key={f.id} style={{ background: '#f7f5fb', border: `1px solid ${editando ? '#58a6ff' : b.border}`, borderRadius: 8, padding: '0.875rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {/* Linha principal */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 600, color: '#1c1530', fontSize: 14 }}>{f.descricao || f.categoria || '—'}</span>
                          <span style={{ fontSize: 11, background: b.bg, color: b.color, border: `1px solid ${b.border}`, borderRadius: 10, padding: '2px 8px' }}>{b.label}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#5f5874' }}>
                          {(f.tipo||'').toUpperCase() === 'RECEITA' ? '↑ Receita' : '↓ Despesa'}
                          {f.categoria ? ` · ${f.categoria}` : ''}
                          {f.data ? ` · ${new Date(f.data+'T00:00').toLocaleDateString('pt-BR')}` : ''}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, color: (f.tipo||'').toUpperCase() === 'RECEITA' ? '#10b981' : '#ef4444', fontSize: 15 }}>
                          {(f.tipo||'').toUpperCase() === 'RECEITA' ? '+' : '-'} {fmt(parseFloat(f.valor||0))}
                        </span>
                        {/* Botão editar */}
                        <button
                          onClick={() => editando ? setEditandoId(null) : abrirEdicao(f)}
                          style={{ background: editando ? '#f3f0f9' : 'transparent', color: '#1d4ed8', border: '1px solid #58a6ff', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}
                        >{editando ? '✕ Fechar' : '✏️ Editar'}</button>
                        {isAdmin && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              onClick={() => validarMovimentacao(f.id, 'validado')}
                              style={{ background: status === 'validado' ? '#10b981' : 'transparent', color: status === 'validado' ? 'white' : '#10b981', border: '1px solid #10b981', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                            >✓ Validar</button>
                            <button
                              onClick={() => validarMovimentacao(f.id, 'rejeitado')}
                              style={{ background: status === 'rejeitado' ? '#ef4444' : 'transparent', color: status === 'rejeitado' ? 'white' : '#ef4444', border: '1px solid #ef4444', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                            >✗ Rejeitar</button>
                            {status !== 'pendente' && (
                              <button
                                onClick={() => validarMovimentacao(f.id, 'pendente')}
                                style={{ background: 'transparent', color: '#f59e0b', border: '1px solid #f59e0b', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}
                              >↩</button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Formulário de edição inline */}
                    {editando && (
                      <div style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <div className="form-grid-2" style={{ gap: '0.75rem' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <label style={labelStyle}>Descrição</label>
                            <input
                              style={inputStyle}
                              value={editForm.descricao}
                              onChange={e => setEditForm(p => ({ ...p, descricao: e.target.value }))}
                              placeholder="Descrição do lançamento"
                            />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <label style={labelStyle}>Categoria</label>
                            <input
                              style={inputStyle}
                              value={editForm.categoria}
                              onChange={e => setEditForm(p => ({ ...p, categoria: e.target.value }))}
                              placeholder="Ex: Pessoal, Infraestrutura..."
                            />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <label style={labelStyle}>Tipo</label>
                            <select style={inputStyle} value={editForm.tipo} onChange={e => setEditForm(p => ({ ...p, tipo: e.target.value }))}>
                              <option value="DESPESA">Despesa</option>
                              <option value="RECEITA">Receita</option>
                            </select>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <label style={labelStyle}>Valor (R$)</label>
                            <input
                              type="number"
                              style={inputStyle}
                              value={editForm.valor}
                              onChange={e => setEditForm(p => ({ ...p, valor: e.target.value }))}
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <label style={labelStyle}>Data</label>
                            <input
                              type="date"
                              style={inputStyle}
                              value={editForm.data}
                              onChange={e => setEditForm(p => ({ ...p, data: e.target.value }))}
                            />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <label style={labelStyle}>Observações</label>
                            <input
                              style={inputStyle}
                              value={editForm.observacoes}
                              onChange={e => setEditForm(p => ({ ...p, observacoes: e.target.value }))}
                              placeholder="Opcional"
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button onClick={() => setEditandoId(null)} style={{ background: 'transparent', color: '#5f5874', border: '1px solid #e5e0ef', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
                          <button onClick={() => salvarEdicao(f.id)} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, padding: '7px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Salvar alterações</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── ABA: LIVRO RAZÃO ── */}
      {aba === 'razao' && (() => {
        const categorias = livroRazaoCategorias;
        const contaSel = razaoConta && categorias.includes(razaoConta) ? razaoConta : categorias[0] || '';
        const codSel = contaSel.split(/ [—-] /)[0].trim();

        // Natureza da conta (NBC TG 26): Ativo, Despesa, Distribuição = devedora; Passivo, PL, Receita = credora
        const isDevedora = (cod) => {
          if (!cod) return true;
          if (/^1\./.test(cod)) return true;
          if (/^5\./.test(cod)) return true;
          if (/^6\./.test(cod)) return true;
          if (/^3\.1\.2/.test(cod)) return true;
          if (/^3\.3/.test(cod)) return true;
          if (/^3\.4\.1\.01/.test(cod)) return true;
          return false;
        };
        const devedora = isDevedora(codSel);

        const matchConta = (l) => {
          const codD = (l.contaDebito  || '').split(/ [—-] /)[0].trim();
          const codC = (l.contaCredito || '').split(/ [—-] /)[0].trim();
          return {
            isD: (l.contaDebito === contaSel || codD === codSel),
            isC: (l.contaCredito === contaSel || codC === codSel),
          };
        };

        // Saldo de abertura — segue mesmo padrão do balancete (linhas 1533-1558):
        // Se há lançamento `abertura-${filAno}`, ELE É a fonte autoritária do saldo inicial
        // e o histórico de anos anteriores é ignorado (senão duplica). Caso contrário,
        // soma os movimentos de todos os exercícios anteriores.
        const temAbertura = lancamentosContabeis.some(l => (l.origem || '') === `abertura-${filAno}`);
        let saldoAbertura = 0;
        lancamentosContabeis.forEach(l => {
          const [ano] = (l.data || '').split('-');
          if (!ano) return;
          const ehAbertura = (l.origem || '') === `abertura-${filAno}`;
          if (temAbertura) {
            if (!ehAbertura) return;          // só a abertura conta
          } else {
            if (ano >= filAno) return;        // só exercícios anteriores
          }
          const val = parseFloat(l.valor || 0);
          const { isD, isC } = matchConta(l);
          if (isD) saldoAbertura += devedora ? val : -val;
          if (isC) saldoAbertura += devedora ? -val : val;
        });

        const txns = [];
        lancamentosContabeis
          .filter(l => {
            const [ano] = (l.data || '').split('-');
            // Movimento do exercício, excluindo a abertura (já contabilizada no saldoAbertura).
            return ano === filAno && (l.origem || '') !== `abertura-${filAno}`;
          })
          .sort((a, b) => (a.data || '').localeCompare(b.data || ''))
          .forEach(l => {
            const val = parseFloat(l.valor || 0);
            const { isD, isC } = matchConta(l);
            const ehEncerramento = (l.origem || '').startsWith('encerramento-');
            if (isD) txns.push({ data: l.data, historico: l.historico || '—', debito: val, credito: 0, ehEncerramento });
            if (isC) txns.push({ data: l.data, historico: l.historico || '—', debito: 0, credito: val, ehEncerramento });
          });

        // Saldo acumulado: natureza da conta determina o sinal
        let saldo = saldoAbertura;
        const linhas = txns.map(t => {
          saldo += devedora ? (t.debito - t.credito) : (t.credito - t.debito);
          return { ...t, saldo };
        });
        const totDebito  = linhas.reduce((s, l) => s + l.debito, 0);
        const totCredito = linhas.reduce((s, l) => s + l.credito, 0);

        return (
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div>
                <h3 style={{ color: '#1c1530', margin: '0 0 4px 0' }}>📒 Livro Razão</h3>
                <span style={{ fontSize: 11, color: '#5f5874' }}>Movimentação por conta · Exercício {filAno}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 220 }}>
                <label style={labelStyle}>Conta / Categoria</label>
                <select style={{ ...inputStyle, width: '100%' }} value={contaSel} onChange={e => setRazaoConta(e.target.value)}>
                  {categorias.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 8, padding: '8px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#1d4ed8', fontWeight: 700, textTransform: 'uppercase' }}>Saldo Anterior</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#1d4ed8', fontFamily: 'monospace' }}>{fmt(saldoAbertura)}</div>
                </div>
                <div style={{ background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 8, padding: '8px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#ef4444', fontWeight: 700, textTransform: 'uppercase' }}>Total Débitos</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#ef4444', fontFamily: 'monospace' }}>{fmt(totDebito)}</div>
                </div>
                <div style={{ background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 8, padding: '8px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#10b981', fontWeight: 700, textTransform: 'uppercase' }}>Total Créditos</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#10b981', fontFamily: 'monospace' }}>{fmt(totCredito)}</div>
                </div>
                <div style={{ background: saldo >= 0 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${saldo >= 0 ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}`, borderRadius: 8, padding: '8px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: saldo >= 0 ? '#10b981' : '#ef4444', fontWeight: 700, textTransform: 'uppercase' }}>Saldo Final ({devedora ? 'Devedor' : 'Credor'})</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: saldo >= 0 ? '#10b981' : '#ef4444', fontFamily: 'monospace' }}>{fmt(saldo)}</div>
                </div>
              </div>
            </div>

            <div style={{ background: '#f7f5fb', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 130px 130px 130px', padding: '8px 12px', background: '#f3f0f9', borderBottom: '1px solid #e5e0ef', gap: '0.5rem' }}>
                {['Data', 'Histórico', 'Débito', 'Crédito', 'Saldo'].map(h => (
                  <span key={h} style={{ fontSize: 11, color: '#5f5874', fontWeight: 700 }}>{h}</span>
                ))}
              </div>
              {linhas.length === 0 && Math.abs(saldoAbertura) < 0.01 ? (
                <div style={{ padding: '1.5rem', textAlign: 'center', color: '#5f5874', fontSize: 14 }}>Sem lançamentos para esta conta em {filAno}.</div>
              ) : (
                <>
                  {/* Linha de abertura — saldo trazido do exercício anterior */}
                  <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 130px 130px 130px', padding: '8px 12px', borderBottom: '1px solid #eeeaf5', gap: '0.5rem', alignItems: 'center', background: 'rgba(96,165,250,0.04)' }}>
                    <span style={{ color: '#1d4ed8', fontSize: 12, fontWeight: 600 }}>01/01/{filAno}</span>
                    <span style={{ color: '#1d4ed8', fontSize: 13, fontStyle: 'italic' }}>Saldo anterior (trazido)</span>
                    <span style={{ color: '#8a849c', fontSize: 13, fontFamily: 'monospace' }}>—</span>
                    <span style={{ color: '#8a849c', fontSize: 13, fontFamily: 'monospace' }}>—</span>
                    <span style={{ color: saldoAbertura >= 0 ? '#10b981' : '#ef4444', fontSize: 13, fontFamily: 'monospace', fontWeight: 600 }}>{fmt(saldoAbertura)}</span>
                  </div>
                  {linhas.map((l, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 130px 130px 130px', padding: '8px 12px', borderBottom: '1px solid #eeeaf5', gap: '0.5rem', alignItems: 'center', background: l.ehEncerramento ? 'rgba(245,158,11,0.04)' : 'transparent' }}>
                      <span style={{ color: '#5f5874', fontSize: 12 }}>{l.data ? new Date(l.data + 'T00:00').toLocaleDateString('pt-BR') : '—'}</span>
                      <span style={{ color: '#2d2640', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {l.historico}
                        {l.ehEncerramento && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: 600 }}>Encerramento</span>}
                      </span>
                      <span style={{ color: l.debito > 0 ? '#ef4444' : '#8a849c', fontSize: 13, fontFamily: 'monospace' }}>{l.debito > 0 ? fmt(l.debito) : '—'}</span>
                      <span style={{ color: l.credito > 0 ? '#10b981' : '#8a849c', fontSize: 13, fontFamily: 'monospace' }}>{l.credito > 0 ? fmt(l.credito) : '—'}</span>
                      <span style={{ color: l.saldo >= 0 ? '#10b981' : '#ef4444', fontSize: 13, fontFamily: 'monospace', fontWeight: 600 }}>{fmt(l.saldo)}</span>
                    </div>
                  ))}
                  <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 130px 130px 130px', padding: '10px 12px', background: '#ffffff', gap: '0.5rem', borderTop: '2px solid #e5e0ef' }}>
                    <span></span>
                    <span style={{ color: '#1c1530', fontWeight: 700, fontSize: 13 }}>TOTAIS DO EXERCÍCIO</span>
                    <span style={{ color: '#ef4444', fontWeight: 700, fontSize: 13, fontFamily: 'monospace' }}>{fmt(totDebito)}</span>
                    <span style={{ color: '#10b981', fontWeight: 700, fontSize: 13, fontFamily: 'monospace' }}>{fmt(totCredito)}</span>
                    <span style={{ color: saldo >= 0 ? '#10b981' : '#ef4444', fontWeight: 700, fontSize: 14, fontFamily: 'monospace' }}>{fmt(saldo)}</span>
                  </div>
                </>
              )}
            </div>
            <div style={{ marginTop: '0.75rem', fontSize: 11, color: '#8a849c' }}>
              Natureza da conta: <strong style={{ color: devedora ? '#dc2626' : '#047857' }}>{devedora ? 'devedora (Ativo/Despesa)' : 'credora (Passivo/PL/Receita)'}</strong> ·
              Saldo acumulado = {devedora ? 'Débitos − Créditos' : 'Créditos − Débitos'} (NBC TG 26)
            </div>
          </div>
        );
      })()}

      {/* ── ABA: DLPA ── */}
      {aba === 'dre' && dreSubView === 'dlpa' && (() => {
        const { saldoInicial, lucroLiquidoAno, distribuicaoAno, saldoFinal, recBrutaAno, impostosAno, cspAno, despOpAno, recFinAno } = dlpaAno;

        const dlpaLinha = (label, valor, cor, indent, bold) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: bold ? '10px 0' : '6px 0', paddingLeft: indent ? 20 : 0, borderTop: bold ? '1px solid #e5e0ef' : 'none', alignItems: 'center' }}>
            <span style={{ color: bold ? '#1c1530' : '#2d2640', fontWeight: bold ? 700 : 400, fontSize: bold ? 14 : 13 }}>{label}</span>
            <span style={{ color: cor, fontWeight: bold ? 700 : 400, fontSize: bold ? 14 : 13, fontFamily: 'monospace' }}>{fmt(Math.abs(valor))}</span>
          </div>
        );

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: 8 }}>
                <h3 style={{ color: '#1c1530', margin: '0 0 4px 0' }}>📄 DLPA — Demonstração de Lucros ou Prejuízos Acumulados</h3>
                <button onClick={() => exportarDemonstracoes('dlpa')} style={{ background: 'rgba(96,165,250,0.15)', color: '#1d4ed8', border: '1px solid rgba(96,165,250,0.4)', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>📄 PDF</button>
                <span style={{ fontSize: 11, color: '#5f5874' }}>Exercício: {filAno} · Lei 6.404/76, art. 186 · NBC TG 26</span>
              </div>

              <div style={{ background: '#f7f5fb', borderRadius: 8, padding: '1.25rem', marginBottom: '1rem' }}>
                <div style={{ fontSize: 11, color: '#5f5874', fontWeight: 700, textTransform: 'uppercase', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #e5e0ef' }}>
                  Entidade: {empNomeDemonstracoes} · CNPJ {empCnpj}
                </div>
                {dlpaLinha(`Saldo em 31/12/${Number(filAno) - 1}`, saldoInicial, saldoInicial >= 0 ? '#10b981' : '#ef4444', false, true)}
                {lucroLiquidoAno >= 0
                  ? dlpaLinha(`(+) Lucro Líquido do Exercício ${filAno}`, lucroLiquidoAno, '#10b981', true, false)
                  : dlpaLinha(`(−) Prejuízo do Exercício ${filAno}`, lucroLiquidoAno, '#ef4444', true, false)}
                {distribuicaoAno > 0 && dlpaLinha('(−) Distribuição de Lucros aos Sócios', distribuicaoAno, '#ef4444', true, false)}
                <div style={{ marginTop: 10, padding: '12px 0', borderTop: '2px solid #7c3aed', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#1c1530', fontWeight: 700, fontSize: 15 }}>= Saldo em 31/12/{filAno}</span>
                  <span style={{ color: saldoFinal >= 0 ? '#10b981' : '#ef4444', fontWeight: 700, fontSize: 16, fontFamily: 'monospace' }}>
                    {saldoFinal >= 0 ? '' : '('}{fmt(Math.abs(saldoFinal))}{saldoFinal >= 0 ? '' : ')'}
                  </span>
                </div>
              </div>

              <div style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: '1rem' }}>
                <div style={{ fontSize: 11, color: '#5f5874', fontWeight: 700, textTransform: 'uppercase', marginBottom: 10 }}>Composição do Resultado — {filAno} (DRE Resumida)</div>
                {dlpaLinha('Receita Bruta de Serviços', recBrutaAno, '#10b981', false, false)}
                {impostosAno > 0 && dlpaLinha('(−) Impostos sobre a Receita', impostosAno, '#ef4444', true, false)}
                {cspAno > 0 && dlpaLinha('(−) Custo dos Serviços Prestados', cspAno, '#ef4444', true, false)}
                {dlpaLinha('(−) Despesas Operacionais', despOpAno, '#ef4444', true, false)}
                {recFinAno !== 0 && dlpaLinha('(±) Resultado Financeiro', Math.abs(recFinAno), recFinAno >= 0 ? '#10b981' : '#ef4444', true, false)}
                {dlpaLinha('= Resultado Líquido do Exercício', Math.abs(lucroLiquidoAno), lucroLiquidoAno >= 0 ? '#10b981' : '#ef4444', false, true)}
              </div>

              {!contabilConfig.dlpa2024 && (
                <div style={{ marginTop: '0.75rem', fontSize: 12, color: '#f59e0b', padding: '8px 12px', background: 'rgba(245,158,11,0.08)', borderRadius: 8, border: '1px solid rgba(245,158,11,0.3)' }}>
                  ⚠ Saldo inicial baseado nos dados de referência SPED 2024. Para confirmar, vá em ⚙️ Configurações e importe os dados oficiais.
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── ABA: BALANÇO PATRIMONIAL ── */}
      {aba === 'dre' && dreSubView === 'bp' && (() => {
        // BP calculado diretamente dos lançamentos contábeis (até fim do ano)
        const lancAno = lancamentosContabeis.filter(l => { const [ano] = (l.data || '').split('-'); return ano === filAno; });
        const saldos = {};
        lancAno.forEach(l => {
          const val = parseFloat(l.valor || 0);
          const codD = ((l.contaDebito||'').split(/ [—-] /)[0]).trim();
          const codC = ((l.contaCredito||'').split(/ [—-] /)[0]).trim();
          if (!saldos[codD]) saldos[codD] = 0;
          if (!saldos[codC]) saldos[codC] = 0;
          saldos[codD] += val;  // débito aumenta
          saldos[codC] -= val;  // crédito diminui
        });

        // Agrupar por natureza. Passivo/PL são credores (saldos em `s` são negativos):
        // invertemos com −s para exibir em valor positivo. Ativo mantém sinal real.
        let disponibilidades = 0, adiantamentos = 0, outrosAtivos = 0;
        let fornecedores = 0, obrigFiscaisCP = 0, obrigColab = 0, obrigFiscaisLP = 0;
        let capitalSocial = 0, lucrosAcumulados = 0;
        // Resultado do Exercício em Curso (NBC TG 26 item 54.q / NBC TG 21)
        let receitasAno = 0, despesasAno = 0, distribuicaoAno = 0;

        Object.entries(saldos).forEach(([cod, s]) => {
          if (cod.startsWith('1.1.1')) disponibilidades += s;
          else if (cod.startsWith('1.1.2.02') || cod.startsWith('1.1.2.03')) adiantamentos += s;
          else if (cod.startsWith('1.')) outrosAtivos += s;
          else if (cod.startsWith('2.1.1')) fornecedores += -s;
          else if (cod.startsWith('2.1.2')) obrigFiscaisCP += -s;
          else if (cod.startsWith('2.1.3')) obrigColab += -s;
          else if (cod.startsWith('2.2')) obrigFiscaisLP += -s;
          else if (cod.startsWith('3.1')) capitalSocial += -s;
          else if (cod.startsWith('3.2')) lucrosAcumulados += -s;
          else if (cod.startsWith('4.')) receitasAno += -s;      // receitas: saldo credor
          else if (cod.startsWith('5.')) despesasAno += s;       // despesas: saldo devedor
          else if (cod.startsWith('6.')) distribuicaoAno += s;   // distribuição: saldo devedor
        });

        // Lucro/Prejuízo do Exercício em Curso — aparece no PL enquanto o exercício não foi encerrado.
        // Em exercícios fechados (2025), o encerramento zera 4/5/6 e transfere resultado para 3.2.01,
        // então lucroExercicio vai natural para zero aqui.
        const lucroExercicio = receitasAno - despesasAno - distribuicaoAno;

        const totalAtivo = disponibilidades + adiantamentos + outrosAtivos;
        const totalPassivoCP = fornecedores + obrigFiscaisCP + obrigColab;
        const totalPassivoNCP = obrigFiscaisLP;
        const totalPL = capitalSocial + lucrosAcumulados + lucroExercicio;
        const totalPassivo = totalPassivoCP + totalPassivoNCP + totalPL;

        const bpLinha = (label, valor, cor, indent, bold) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: bold ? '8px 0' : '5px 0', paddingLeft: indent ? 14 : 0, borderTop: bold ? '1px solid #e5e0ef' : 'none', alignItems: 'center' }}>
            <span style={{ color: bold ? '#1c1530' : '#2d2640', fontWeight: bold ? 700 : 400, fontSize: bold ? 13 : 12 }}>{label}</span>
            <span style={{ color: cor || '#1c1530', fontSize: 13, fontFamily: 'monospace', fontWeight: bold ? 700 : 400 }}>{fmt(valor)}</span>
          </div>
        );

        const diff = Math.abs(totalAtivo - totalPassivo);

        return (
          <div style={cardStyle}>
            <div style={{ marginBottom: '1rem' }}>
              <h3 style={{ color: '#1c1530', margin: '0 0 4px 0' }}>
                🏦 Balanço Patrimonial — Exercício {filAno}
              </h3>
              <span style={{ fontSize: 11, color: '#10b981' }}>
                Calculado dos lançamentos contábeis · NBC TG 1002 · Lei 6.404/76
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1rem' }}>
              <div style={{ background: '#f7f5fb', borderRadius: 8, padding: '1rem' }}>
                <div style={{ color: '#10b981', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #e5e0ef' }}>ATIVO</div>
                {bpLinha('Ativo Circulante', disponibilidades + adiantamentos + outrosAtivos, '#10b981', false, true)}
                {bpLinha('Disponibilidades (Caixa/Banco)', disponibilidades, '#2d2640', true, false)}
                {bpLinha('Adiantamentos a Sócios', adiantamentos, '#2d2640', true, false)}
                {outrosAtivos > 0.01 && bpLinha('Outros Ativos Circulantes', outrosAtivos, '#2d2640', true, false)}
                {bpLinha('TOTAL DO ATIVO', totalAtivo, '#10b981', false, true)}
              </div>

              <div style={{ background: '#f7f5fb', borderRadius: 8, padding: '1rem' }}>
                <div style={{ color: '#ef4444', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #e5e0ef' }}>PASSIVO + PATRIMÔNIO LÍQUIDO</div>
                {bpLinha('Passivo Circulante', totalPassivoCP, '#ef4444', false, true)}
                {fornecedores > 0.01 && bpLinha('Fornecedores', fornecedores, '#2d2640', true, false)}
                {obrigFiscaisCP > 0.01 && bpLinha('Obrigações Fiscais (CP)', obrigFiscaisCP, '#2d2640', true, false)}
                {obrigColab > 0.01 && bpLinha('Obrigações com Colaboradores', obrigColab, '#2d2640', true, false)}
                {totalPassivoNCP > 0.01 && bpLinha('Passivo Não Circulante', totalPassivoNCP, '#ef4444', false, true)}
                {obrigFiscaisLP > 0.01 && bpLinha('Obrigações Fiscais (LP)', obrigFiscaisLP, '#2d2640', true, false)}
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e0ef' }}>
                  <div style={{ color: '#6d28d9', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', marginBottom: 8 }}>Patrimônio Líquido</div>
                  {bpLinha('Capital Social', capitalSocial, '#a78bfa', false, false)}
                  {bpLinha('Lucros/Prejuízos Acumulados', lucrosAcumulados, lucrosAcumulados >= 0 ? '#a78bfa' : '#ef4444', false, false)}
                  {Math.abs(lucroExercicio) > 0.01 && bpLinha('Lucro/Prejuízo do Exercício em Curso', lucroExercicio, lucroExercicio >= 0 ? '#a78bfa' : '#ef4444', false, false)}
                  {bpLinha('Total Patrimônio Líquido', totalPL, '#a78bfa', false, true)}
                </div>
                {bpLinha('TOTAL PASSIVO + PL', totalPassivo, '#10b981', false, true)}
              </div>
            </div>

            <div style={{ padding: '10px 14px', background: diff < 1 ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)', borderRadius: 8, border: `1px solid ${diff < 1 ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#2d2640' }}>Verificação de equilíbrio (Ativo = Passivo + PL)</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: diff < 1 ? '#10b981' : '#ef4444' }}>
                {diff < 1 ? '✓ Balancete equilibrado' : `⚠ Diferença: ${fmt(diff)}`}
              </span>
            </div>
          </div>
        );
      })()}

      {/* ── ABA: APURAÇÃO TRIBUTÁRIA ── */}
      {aba === 'dre' && dreSubView === 'apuracao' && (() => {
        const FAIXAS_SN = [
          { limite: 180000,   aliq: 0.06,  ded: 0,      label: 'Até R$ 180.000' },
          { limite: 360000,   aliq: 0.112, ded: 9360,   label: 'R$ 180.001 a R$ 360.000' },
          { limite: 720000,   aliq: 0.132, ded: 17640,  label: 'R$ 360.001 a R$ 720.000' },
          { limite: 1800000,  aliq: 0.16,  ded: 35640,  label: 'R$ 720.001 a R$ 1.800.000' },
          { limite: 3600000,  aliq: 0.21,  ded: 125640, label: 'R$ 1.800.001 a R$ 3.600.000' },
          { limite: 4800000,  aliq: 0.33,  ded: 557640, label: 'R$ 3.600.001 a R$ 4.800.000' },
        ];

        // Receitas históricas do PGDAS (fonte oficial, usadas para RBT12)
        const receitasHistoricas = { ...RECEITAS_PGDAS_PADRAO, ...(contabilConfig.receitasHistoricas || {}) };

        const mesRef = parseInt(apuracaoMes, 10);
        const anoRef = parseInt(apuracaoAno, 10);

        // Categorias de reembolso a excluir da receita tributável
        const catReembolso = ['reembolso', 'reembolsos'];

        const receitaMes = (m, a) => {
          const chave = `${a}-${String(m).padStart(2, '0')}`;
          // Meses passados e já declarados no PGDAS oficial: usar valor
          // histórico como fonte da verdade (immutável no órgão).
          if (receitasHistoricas[chave] != null) return Number(receitasHistoricas[chave]);
          const s = String(m).padStart(2, '0');
          const as = String(a);
          // Mês em curso / futuro: soma APENAS agendamentos (fonte única de
          // receita no Financeiro). Somar `fluxoCaixa` também gerava DOBRA
          // quando o agendamento pago era conciliado no fluxo — bug reportado
          // pela Bianca em 28/07 (mostrava R$ 40.180 em ago/26, sendo o valor
          // real ~R$ 20 mil). Reembolsos ficam FORA porque a Bianca lança
          // reembolsos como fluxo/despesa negativa, não como agendamento.
          const recAgend = agendamentos.filter(ag => {
            const [ano, mes] = (ag.data || '').split('-');
            if (mes !== s || ano !== as) return false;
            if (catReembolso.includes((ag.categoria || '').toLowerCase())) return false;
            return true;
          }).reduce((acc, ag) => acc + parseFloat(ag.valor || 0), 0);
          return recAgend;
        };

        const meses12 = [];
        for (let i = 1; i <= 12; i++) {
          let m = mesRef - i; let a = anoRef;
          if (m <= 0) { m += 12; a -= 1; }
          meses12.push({ m, a });
        }

        const rbt12Calculado = meses12.reduce((s, { m, a }) => s + receitaMes(m, a), 0);
        const chRbt = `${apuracaoAno}-${apuracaoMes}`;
        const rbt12Override = (contabilConfig.rbt12Override || {})[chRbt];
        const rbt12 = rbt12Override != null ? Number(rbt12Override) : rbt12Calculado;
        const receitaAtual = receitaMes(mesRef, anoRef);
        const faixa = FAIXAS_SN.find(f => rbt12 <= f.limite) || FAIXAS_SN[FAIXAS_SN.length - 1];
        const faixaNum = FAIXAS_SN.indexOf(faixa) + 1;
        const aliqEfetiva = rbt12 > 0 ? ((rbt12 * faixa.aliq - faixa.ded) / rbt12) : faixa.aliq;
        const das = receitaAtual * aliqEfetiva;
        const mesesAno = Array.from({ length: mesRef }, (_, i) => i + 1);
        const dasAcum = mesesAno.reduce((s, m) => s + receitaMes(m, anoRef) * aliqEfetiva, 0);

        // Detalhamento por tributo (proporções Anexo III para distribuição)
        const propTributos = { IRPJ: 0.04, CSLL: 0.035, COFINS: 0.1282, PIS: 0.0278, CPP: 0.4340, ISS: 0.335 };

        return (
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div>
                <h3 style={{ color: '#1c1530', margin: '0 0 4px 0' }}>🧮 Apuração Tributária — Simples Nacional</h3>
                <span style={{ fontSize: 11, color: '#5f5874' }}>Anexo III (Serviços Intelectuais / Consultoria) · Competência: {MESES_NOMES[mesRef - 1]}/{apuracaoAno}</span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    const novo = { ...(contabilConfig.rbt12Override || {}) };
                    delete novo[chRbt];
                    setContabilConfig({ ...contabilConfig, rbt12Override: novo });
                    setEditandoRbt12(false);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(88,166,255,0.1)', border: '1px solid rgba(88,166,255,0.3)', borderRadius: 8, color: '#1d4ed8', padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  🔄 Recalcular
                </button>
              </div>
            </div>

            {/* Seletor de competência da apuração */}
            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={labelStyle}>Mês de Competência</label>
                <select value={apuracaoMes} onChange={e => setApuracaoMes(e.target.value)} style={{ ...inputStyle, minWidth: 140 }}>
                  {MESES_NOMES.map((n, i) => <option key={i} value={String(i + 1).padStart(2, '0')}>{n}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={labelStyle}>Ano</label>
                <select value={apuracaoAno} onChange={e => setApuracaoAno(e.target.value)} style={{ ...inputStyle, minWidth: 90 }}>
                  {[2024, 2025, 2026, 2027].map(a => <option key={a} value={String(a)}>{a}</option>)}
                </select>
              </div>
              <div style={{ fontSize: 11, color: '#5f5874', alignSelf: 'center', padding: '6px 12px', background: 'rgba(139,148,158,0.08)', borderRadius: 8 }}>
                Apuração realizada em {MESES_NOMES[mesRef % 12]}/{mesRef === 12 ? anoRef + 1 : anoRef}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
              <div style={{ background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: '#5f5874', marginBottom: 5 }}>Receita Bruta ({MESES_NOMES[mesRef - 1]})</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#10b981', fontFamily: 'monospace' }}>{fmt(receitaAtual)}</div>
                {receitasHistoricas[chRbt] != null && <div style={{ fontSize: 10, color: '#10b981', marginTop: 2 }}>Fonte: PGDAS</div>}
              </div>
              {/* RBT12 — editável */}
              <div style={{ background: '#f7f5fb', border: `1px solid ${rbt12Override != null ? 'rgba(245,158,11,0.5)' : '#e5e0ef'}`, borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                  <span style={{ fontSize: 11, color: '#5f5874' }}>RBT12 (últimos 12 meses)</span>
                  <button onClick={() => { setEditandoRbt12(v => !v); setRbt12Input(String(rbt12Override ?? rbt12Calculado)); }} style={{ background: 'transparent', border: 'none', color: '#1d4ed8', cursor: 'pointer', fontSize: 11, padding: 0 }}>
                    {editandoRbt12 ? '✕' : '✏️'}
                  </button>
                </div>
                {editandoRbt12 ? (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input type="number" step="0.01" min="0" value={rbt12Input} onChange={e => setRbt12Input(e.target.value)}
                      style={{ background: '#ffffff', border: '1px solid #58a6ff', borderRadius: 6, color: '#1c1530', padding: '4px 8px', fontSize: 13, width: '100%', fontFamily: 'monospace' }} />
                    <button onClick={() => {
                      const novoOverride = { ...(contabilConfig.rbt12Override || {}), [chRbt]: parseFloat(rbt12Input) };
                      setContabilConfig({ ...contabilConfig, rbt12Override: novoOverride });
                      setEditandoRbt12(false);
                    }} style={{ background: '#10b981', border: 'none', borderRadius: 6, color: 'white', padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>OK</button>
                    {rbt12Override != null && (
                      <button onClick={() => {
                        const novo = { ...(contabilConfig.rbt12Override || {}) };
                        delete novo[chRbt];
                        setContabilConfig({ ...contabilConfig, rbt12Override: novo });
                        setEditandoRbt12(false);
                      }} style={{ background: 'transparent', border: '1px solid #ef4444', borderRadius: 6, color: '#ef4444', padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}>Limpar</button>
                    )}
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#1d4ed8', fontFamily: 'monospace' }}>{fmt(rbt12)}</div>
                    {rbt12Override != null && <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 2 }}>⚠ Valor manual · Calculado: {fmt(rbt12Calculado)}</div>}
                  </div>
                )}
              </div>
              {[
                { label: 'Faixa Simples Nacional', valor: `${faixaNum}ª Faixa`, cor: '#f59e0b' },
                { label: 'Alíquota Nominal', valor: `${(faixa.aliq * 100).toFixed(2)}%`, cor: '#a78bfa' },
                { label: 'Alíquota Efetiva', valor: `${(aliqEfetiva * 100).toFixed(4)}%`, cor: '#a78bfa' },
                { label: 'DAS a Recolher (mês)', valor: fmt(das), cor: '#ef4444' },
              ].map(item => (
                <div key={item.label} style={{ background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, color: '#5f5874', marginBottom: 5 }}>{item.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: item.cor, fontFamily: 'monospace' }}>{item.valor}</div>
                </div>
              ))}
            </div>

            <div style={{ background: '#f7f5fb', borderRadius: 8, padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: 11, color: '#5f5874', fontWeight: 700, textTransform: 'uppercase', marginBottom: 10 }}>Memória de Cálculo</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#2d2640', fontFamily: 'monospace' }}>
                <div>RBT12 = {fmt(rbt12)}</div>
                <div>Alíquota Nominal = {(faixa.aliq * 100).toFixed(2)}%</div>
                {faixa.ded > 0 && <div>Parcela a Deduzir = {fmt(faixa.ded)}</div>}
                <div>Alíquota Efetiva = ({fmt(rbt12)} × {(faixa.aliq * 100).toFixed(2)}%{faixa.ded > 0 ? ` − ${fmt(faixa.ded)}` : ''}) ÷ {fmt(rbt12)} = <strong style={{ color: '#6d28d9' }}>{(aliqEfetiva * 100).toFixed(4)}%</strong></div>
                <div>DAS = {fmt(receitaAtual)} × {(aliqEfetiva * 100).toFixed(4)}% = <strong style={{ color: '#ef4444' }}>{fmt(das)}</strong></div>
              </div>
            </div>

            {/* Detalhamento RBT12 — receitas dos 12 meses anteriores */}
            <div style={{ background: '#f7f5fb', borderRadius: 8, overflow: 'hidden', marginBottom: '1rem' }}>
              <div style={{ padding: '8px 12px', background: '#f3f0f9', borderBottom: '1px solid #e5e0ef' }}>
                <span style={{ fontSize: 11, color: '#5f5874', fontWeight: 700 }}>Composição RBT12 — Receitas dos 12 meses anteriores</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.5rem', padding: '10px 12px' }}>
                {meses12.slice().reverse().map(({ m, a }) => {
                  const chave = `${a}-${String(m).padStart(2, '0')}`;
                  const val = receitaMes(m, a);
                  const isPgdas = receitasHistoricas[chave] != null;
                  return (
                    <div key={chave} style={{ background: '#ffffff', border: `1px solid ${isPgdas ? 'rgba(16,185,129,0.3)' : '#eeeaf5'}`, borderRadius: 6, padding: '6px 10px' }}>
                      <div style={{ fontSize: 10, color: '#5f5874' }}>{MESES_NOMES[m - 1]}/{a}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#2d2640', fontFamily: 'monospace' }}>{fmt(val)}</div>
                      {isPgdas && <div style={{ fontSize: 9, color: '#10b981' }}>PGDAS</div>}
                    </div>
                  );
                })}
              </div>
              <div style={{ padding: '6px 12px', borderTop: '1px solid #eeeaf5', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11, color: '#5f5874' }}>Total RBT12 (calculado)</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8', fontFamily: 'monospace' }}>{fmt(rbt12Calculado)}</span>
              </div>
            </div>

            <div style={{ background: '#f7f5fb', borderRadius: 8, overflow: 'hidden', marginBottom: '1rem' }}>
              <div style={{ padding: '8px 12px', background: '#f3f0f9', borderBottom: '1px solid #e5e0ef' }}>
                <span style={{ fontSize: 11, color: '#5f5874', fontWeight: 700 }}>Tabela Simples Nacional — Anexo III 2024 (Serviços de Consultoria)</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '32px 1fr 110px 120px 120px', padding: '7px 12px', background: '#f3f0f9', borderBottom: '1px solid #e5e0ef', gap: '0.5rem' }}>
                {['#', 'Faixa RBT12', 'Alíq. Nominal', 'Parcela Ded.', 'Alíq. Efetiva*'].map(h => <span key={h} style={{ fontSize: 11, color: '#5f5874', fontWeight: 700 }}>{h}</span>)}
              </div>
              {FAIXAS_SN.map((f, i) => {
                const ativo = faixaNum === i + 1;
                const ae = rbt12 > 0 && ativo ? ((rbt12 * f.aliq - f.ded) / rbt12 * 100).toFixed(4) + '%' : '—';
                return (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '32px 1fr 110px 120px 120px', padding: '8px 12px', borderBottom: '1px solid #eeeaf5', background: ativo ? 'rgba(124,58,237,0.15)' : 'transparent', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ color: ativo ? '#7c3aed' : '#5f5874', fontWeight: ativo ? 700 : 400, fontSize: 13 }}>{i + 1}ª</span>
                    <span style={{ color: ativo ? '#1c1530' : '#2d2640', fontSize: 12 }}>
                      {f.label}
                      {ativo && <span style={{ marginLeft: 8, fontSize: 10, color: '#7c3aed', background: 'rgba(124,58,237,0.2)', padding: '2px 6px', borderRadius: 10 }}>▶ ATUAL</span>}
                    </span>
                    <span style={{ color: ativo ? '#6d28d9' : '#5f5874', fontSize: 13, fontFamily: 'monospace' }}>{(f.aliq * 100).toFixed(2)}%</span>
                    <span style={{ color: '#5f5874', fontSize: 12, fontFamily: 'monospace' }}>{f.ded > 0 ? fmt(f.ded) : '—'}</span>
                    <span style={{ color: ativo ? '#6d28d9' : '#8a849c', fontSize: 12, fontFamily: 'monospace' }}>{ae}</span>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
              <div style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: '12px 16px' }}>
                <div style={{ fontSize: 11, color: '#5f5874', marginBottom: 4 }}>DAS Acumulado {apuracaoAno} (até {MESES_NOMES[mesRef - 1]})</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#ef4444', fontFamily: 'monospace' }}>{fmt(dasAcum)}</div>
              </div>
              <div style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: '12px 16px' }}>
                <div style={{ fontSize: 11, color: '#5f5874', marginBottom: 4 }}>Receita Bruta Acumulada {apuracaoAno}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981', fontFamily: 'monospace' }}>{fmt(mesesAno.reduce((s, m) => s + receitaMes(m, anoRef), 0))}</div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#8a849c' }}>
              * Alíquota efetiva calculada conforme LC 123/2006. RBT12 = receita dos 12 meses anteriores ao mês de apuração. Reembolsos excluídos da receita tributável. Confirme com sua contabilidade antes de recolher.
            </div>
          </div>
        );
      })()}

      {/* ── ABA: PLANO DE CONTAS ── */}
      {aba === 'config' && configSubView === 'planocontas' && (() => {
        const plano = contabilConfig.planoContas || PLANO_CONTAS_PADRAO;

        const adicionarConta = () => {
          if (!novaContaForm.codigo || !novaContaForm.nome) return;
          const novoConta = { ...novaContaForm, nivel: parseInt(novaContaForm.nivel) };
          setContabilConfig(prev => ({ ...(prev||{}), planoContas: [...plano, novoConta].sort((a,b) => a.codigo.localeCompare(b.codigo)) }));
          setNovaContaForm({ codigo: '', nome: '', tipo: 'D', nivel: 4 });
          setShowNovaContaForm(false);
        };

        const resetarPlano = () => {
          setContabilConfig(prev => { const n = {...(prev||{})}; delete n.planoContas; return n; });
        };

        const excluirConta = (codigo) => {
          setContabilConfig(prev => ({ ...(prev||{}), planoContas: plano.filter(c => c.codigo !== codigo) }));
        };

        const salvarEdicaoConta = (codigoOriginal) => {
          if (!editContaForm.codigo || !editContaForm.nome) return;
          const atualizado = plano.map(c => c.codigo === codigoOriginal ? { ...editContaForm, nivel: parseInt(editContaForm.nivel) } : c);
          setContabilConfig(prev => ({ ...(prev||{}), planoContas: atualizado.sort((a,b) => a.codigo.localeCompare(b.codigo)) }));
          setEditandoContaCodigo(null);
        };

        const TIPO_LABEL = { A: 'Ativo', P: 'Passivo', PL: 'Patrimônio Líquido', R: 'Receita', D: 'Custo/Despesa', DR: 'Destinação' };
        const TIPO_COR   = { A: '#10b981', P: '#ef4444', PL: '#a78bfa', R: '#10b981', D: '#f87171', DR: '#60a5fa' };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div>
                <h3 style={{ color: '#1c1530', margin: '0 0 4px 0' }}>🗂 Plano de Contas</h3>
                <span style={{ fontSize: 11, color: '#5f5874' }}>Estrutura hierárquica · NBC TG 1002 (Microentidade) · {plano.length} contas</span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => setShowNovaContaForm(s => !s)} style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Conta</button>
                {contabilConfig.planoContas && (
                  <button onClick={resetarPlano} style={{ background: 'transparent', color: '#5f5874', border: '1px solid #e5e0ef', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}>↺ Restaurar padrão</button>
                )}
              </div>
            </div>

            {showNovaContaForm && (
              <div style={{ ...cardStyle, border: '1px solid #58a6ff' }}>
                <div style={{ fontSize: 13, color: '#1d4ed8', fontWeight: 700, marginBottom: '0.75rem' }}>Nova Conta Contábil</div>
                <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 140px 100px', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  {[
                    { label: 'Código', field: 'codigo', type: 'text', placeholder: 'Ex: 5.3.07' },
                    { label: 'Nome da Conta', field: 'nome', type: 'text', placeholder: 'Ex: Despesas com Energia' },
                  ].map(f => (
                    <div key={f.field} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label style={labelStyle}>{f.label}</label>
                      <input type={f.type} style={inputStyle} placeholder={f.placeholder} value={novaContaForm[f.field]} onChange={e => setNovaContaForm(p => ({ ...p, [f.field]: e.target.value }))} />
                    </div>
                  ))}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={labelStyle}>Tipo</label>
                    <select style={inputStyle} value={novaContaForm.tipo} onChange={e => setNovaContaForm(p => ({ ...p, tipo: e.target.value }))}>
                      {Object.entries(TIPO_LABEL).map(([k,v]) => <option key={k} value={k}>{k} — {v}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={labelStyle}>Nível</label>
                    <select style={inputStyle} value={novaContaForm.nivel} onChange={e => setNovaContaForm(p => ({ ...p, nivel: e.target.value }))}>
                      {[1,2,3,4].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => setShowNovaContaForm(false)} style={{ background: 'transparent', color: '#5f5874', border: '1px solid #e5e0ef', borderRadius: 8, padding: '7px 16px', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
                  <button onClick={adicionarConta} style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '7px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Salvar Conta</button>
                </div>
              </div>
            )}

            <div style={cardStyle}>
              <div style={{ background: '#f7f5fb', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 110px 60px 72px', padding: '8px 12px', background: '#f3f0f9', borderBottom: '1px solid #e5e0ef', gap: '0.5rem' }}>
                  {['Código', 'Nome da Conta', 'Tipo', 'Nível', ''].map(h => <span key={h} style={{ fontSize: 11, color: '#5f5874', fontWeight: 700 }}>{h}</span>)}
                </div>
                {plano.map((c, i) => {
                  const emEdicao = editandoContaCodigo === c.codigo;
                  return (
                    <React.Fragment key={c.codigo + i}>
                      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 110px 60px 72px', padding: c.nivel === 1 ? '10px 12px' : '7px 12px', borderBottom: emEdicao ? 'none' : '1px solid #eeeaf5', gap: '0.5rem', alignItems: 'center', background: emEdicao ? 'rgba(88,166,255,0.06)' : c.nivel === 1 ? '#eeeaf5' : c.nivel === 2 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                        <span style={{ color: TIPO_COR[c.tipo] || '#1c1530', fontSize: 13, fontFamily: 'monospace', fontWeight: c.nivel <= 2 ? 700 : 400 }}>{c.codigo}</span>
                        <span style={{ color: c.nivel === 1 ? '#1c1530' : c.nivel === 2 ? '#2d2640' : '#5f5874', fontSize: c.nivel === 1 ? 14 : 13, fontWeight: c.nivel <= 2 ? 700 : 400, paddingLeft: (c.nivel - 1) * 16 }}>{c.nome}</span>
                        <span style={{ fontSize: 11, color: TIPO_COR[c.tipo] || '#5f5874', background: (TIPO_COR[c.tipo] || '#5f5874') + '22', borderRadius: 10, padding: '2px 8px', textAlign: 'center' }}>{TIPO_LABEL[c.tipo] || c.tipo}</span>
                        <span style={{ fontSize: 12, color: '#8a849c', textAlign: 'center' }}>{c.nivel}º</span>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => {
                            if (emEdicao) { setEditandoContaCodigo(null); return; }
                            setEditandoContaCodigo(c.codigo);
                            setEditContaForm({ codigo: c.codigo, nome: c.nome, tipo: c.tipo, nivel: String(c.nivel) });
                          }} style={{ background: emEdicao ? 'rgba(88,166,255,0.2)' : 'transparent', color: '#1d4ed8', border: '1px solid rgba(88,166,255,0.3)', borderRadius: 6, padding: '3px 7px', fontSize: 11, cursor: 'pointer' }}>
                            {emEdicao ? '✕' : '✏️'}
                          </button>
                          <button onClick={() => excluirConta(c.codigo)} style={{ background: 'transparent', color: '#8a849c', border: '1px solid #e5e0ef', borderRadius: 6, padding: '3px 7px', fontSize: 11, cursor: 'pointer' }}>🗑</button>
                        </div>
                      </div>
                      {emEdicao && (
                        <div style={{ background: '#ffffff', border: '1px solid #58a6ff', borderRadius: '0 0 8px 8px', padding: '0.75rem 1rem', marginBottom: 4, borderTop: 'none' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 130px 90px', gap: '0.75rem', marginBottom: '0.5rem' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <label style={labelStyle}>Código</label>
                              <input style={inputStyle} value={editContaForm.codigo} onChange={e => setEditContaForm(p => ({ ...p, codigo: e.target.value }))} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <label style={labelStyle}>Nome da Conta</label>
                              <input style={inputStyle} value={editContaForm.nome} onChange={e => setEditContaForm(p => ({ ...p, nome: e.target.value }))} />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <label style={labelStyle}>Tipo</label>
                              <select style={inputStyle} value={editContaForm.tipo} onChange={e => setEditContaForm(p => ({ ...p, tipo: e.target.value }))}>
                                {Object.entries(TIPO_LABEL).map(([k,v]) => <option key={k} value={k}>{k} — {v}</option>)}
                              </select>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <label style={labelStyle}>Nível</label>
                              <select style={inputStyle} value={editContaForm.nivel} onChange={e => setEditContaForm(p => ({ ...p, nivel: e.target.value }))}>
                                {[1,2,3,4].map(n => <option key={n} value={n}>{n}</option>)}
                              </select>
                            </div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button onClick={() => setEditandoContaCodigo(null)} style={{ background: 'transparent', color: '#5f5874', border: '1px solid #e5e0ef', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
                            <button onClick={() => salvarEdicaoConta(c.codigo)} style={{ background: '#58a6ff', color: 'white', border: 'none', borderRadius: 8, padding: '6px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>💾 Salvar</button>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── ABA: CONFIGURAÇÕES ── */}
      {/* Regras de Classificação Automática → Conta Contábil */}
      {aba === 'config' && configSubView === 'config' && (() => {
        const usandoPadrao = !contabilConfig.regrasClassificacao;
        const regras = (contabilConfig.regrasClassificacao?.length ? contabilConfig.regrasClassificacao : REGRAS_CLASSIFICACAO_PADRAO);
        const planoRegras = contabilConfig.planoContas || PLANO_CONTAS_PADRAO;
        const contasAnaliticas = planoRegras.filter(c => c.nivel >= 3).sort((a,b) => a.codigo.localeCompare(b.codigo));
        const addRegra = () => {
          if (!novaRegraForm.padrao.trim() || !novaRegraForm.contaContabil) return;
          setContabilConfig(prev => ({ ...(prev||{}), regrasClassificacao: [...(prev?.regrasClassificacao || REGRAS_CLASSIFICACAO_PADRAO), { padrao: novaRegraForm.padrao.trim(), contaContabil: novaRegraForm.contaContabil }] }));
          setNovaRegraForm({ padrao: '', contaContabil: '' });
        };
        const removeRegra = (idx) => {
          setContabilConfig(prev => ({ ...(prev||{}), regrasClassificacao: (prev?.regrasClassificacao || REGRAS_CLASSIFICACAO_PADRAO).filter((_,i) => i !== idx) }));
        };
        return (
          <div style={{ ...cardStyle, marginBottom: '1.5rem', border: '1px solid rgba(245,158,11,0.3)' }}>
            <div style={{ marginBottom: '1rem' }}>
              <h3 style={{ color: '#1c1530', margin: 0, fontSize: 15 }}>🔀 Regras de Classificação Automática</h3>
              <p style={{ fontSize: 12, color: '#5f5874', margin: '4px 0 0' }}>Quando o histórico bancário contém a palavra-chave, o lançamento é classificado direto na conta contábil.</p>
            </div>
            {/* Formulário nova regra */}
            <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 200px' }}>
                <label style={labelStyle}>Palavra-chave no histórico</label>
                <input style={inputStyle} placeholder="Ex: RECEITA FEDERAL" value={novaRegraForm.padrao} onChange={e => setNovaRegraForm(f => ({ ...f, padrao: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '2 1 300px' }}>
                <label style={labelStyle}>Conta contábil</label>
                <select style={inputStyle} value={novaRegraForm.contaContabil} onChange={e => setNovaRegraForm(f => ({ ...f, contaContabil: e.target.value }))}>
                  <option value="">Selecione a conta...</option>
                  {contasAnaliticas.map(c => <option key={c.codigo} value={`${c.codigo} — ${c.nome}`}>{c.codigo} — {c.nome} ({c.tipo})</option>)}
                </select>
              </div>
              <button onClick={addRegra} disabled={!novaRegraForm.padrao.trim() || !novaRegraForm.contaContabil} style={{ background: novaRegraForm.padrao && novaRegraForm.contaContabil ? '#f59e0b' : '#ebe7f3', color: novaRegraForm.padrao && novaRegraForm.contaContabil ? '#1e1b4b' : '#8a849c', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', alignSelf: 'flex-end' }}>+ Adicionar</button>
            </div>
            {regras.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#8a849c', fontSize: 13 }}>
                Nenhuma regra cadastrada.<br/>
                <span style={{ fontSize: 11 }}>Ex: "RECEITA FEDERAL" → 5.5.01 — Simples Nacional (DAS)</span>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#5f5874', borderBottom: '1px solid #eeeaf5', textTransform: 'uppercase' }}>Palavra-chave</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#5f5874', borderBottom: '1px solid #eeeaf5', textTransform: 'uppercase' }}>Conta Contábil</th>
                    <th style={{ width: 40, borderBottom: '1px solid #eeeaf5' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {regras.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eeeaf5' }}>
                      <td style={{ padding: '8px 10px', fontSize: 13, color: '#1c1530', fontFamily: 'monospace' }}>{r.padrao}</td>
                      <td style={{ padding: '8px 10px', fontSize: 13, color: '#f59e0b', fontWeight: 600 }}>{r.contaContabil || r.categoria}</td>
                      <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                        <button onClick={() => removeRegra(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16 }} title="Remover regra">✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })()}

      {aba === 'config' && configSubView === 'config' && (() => {
        const todasCategorias = [...new Set(fluxoCaixa.map(f => f.categoria || '').filter(Boolean))].sort();
        const mapeamentoContas = contabilConfig.mapeamentoContas || {};
        const mapeamento = contabilConfig.mapeamento || {};
        const planoConf = contabilConfig.planoContas || PLANO_CONTAS_PADRAO;
        const contasAnaliticasConf = planoConf.filter(c => c.nivel >= 3);

        const salvarMapeamento = (cat, contaStr) => {
          if (!setContabilConfig) return;
          setContabilConfig(prev => ({
            ...(prev || {}),
            mapeamentoContas: { ...((prev || {}).mapeamentoContas || {}), [cat]: contaStr },
          }));
        };

        const naoMapeadas = todasCategorias.filter(c => !mapeamentoContas[c] && !mapeamento[c]);

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {/* ── Importar Dados SPED 2024 (mantido aqui para referência histórica) ── */}
            {false && <div style={{ ...cardStyle, border: '1px solid rgba(96,165,250,0.3)' }}>
              <h3 style={{ color: '#1c1530', margin: '0 0 6px 0' }}>📥 Importar Lançamentos Contábeis — Excel</h3>
              <p style={{ fontSize: 12, color: '#5f5874', margin: '0 0 1rem 0' }}>
                Planilha Excel (.xlsx) com colunas: <strong style={{ color: '#2d2640' }}>Data | Histórico | Conta Débito | Conta Crédito | Valor | Documento | Centro de Custo</strong>
                {' '}(as 5 primeiras são obrigatórias; Documento e Centro de Custo são opcionais).
              </p>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button onClick={() => {
                  // Gerar planilha modelo
                  const ws = XLSX.utils.aoa_to_sheet([
                    ['Data','Histórico','Conta Débito','Conta Crédito','Valor','Documento','Centro de Custo'],
                    ['2025-01-05','Pagamento de aluguel','5.3.1.01 — Aluguéis','1.1.1.03 — Banco Inter',1500,'NF 001','Adm'],
                    ['2025-01-10','Receita de consultoria','1.1.1.03 — Banco Inter','4.1.1 — Receita de Serviços Prestados',3000,'REC 010',''],
                  ]);
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, ws, 'Lancamentos');
                  XLSX.writeFile(wb, 'modelo_lancamentos.xlsx');
                }} style={{ background: 'rgba(96,165,250,0.15)', color: '#1d4ed8', border: '1px solid rgba(96,165,250,0.4)', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  ⬇ Baixar Modelo
                </button>
                <label style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  📂 Selecionar Arquivo .xlsx
                  <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = ev => {
                      try {
                        const wb = XLSX.read(ev.target.result, { type: 'array', cellDates: true });
                        const ws = wb.Sheets[wb.SheetNames[0]];
                        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, dateNF: 'yyyy-mm-dd' });
                        if (rows.length < 2) { alert('Planilha vazia ou sem dados.'); return; }
                        // Detectar colunas pela linha de cabeçalho
                        const header = rows[0].map(h => (h || '').toString().toLowerCase().trim());
                        const col = (nomes) => { for (const n of nomes) { const i = header.findIndex(h => h.includes(n)); if (i >= 0) return i; } return -1; };
                        const iData   = col(['data']);
                        const iHist   = col(['hist','descriç']);
                        const iDeb    = col(['déb','deb','conta d']);
                        const iCred   = col(['créd','cred','conta c']);
                        const iValor  = col(['valor']);
                        const iDoc    = col(['doc']);
                        const iCC     = col(['centro','cc']);
                        if (iData < 0 || iHist < 0 || iDeb < 0 || iCred < 0 || iValor < 0) {
                          alert('Colunas obrigatórias não encontradas. Verifique se o cabeçalho da planilha tem: Data, Histórico, Conta Débito, Conta Crédito, Valor');
                          return;
                        }
                        const novos = [];
                        rows.slice(1).forEach((r, ri) => {
                          if (!r[iData] && !r[iValor]) return; // linha vazia
                          let dataVal = (r[iData] || '').toString().trim();
                          // Normalizar data: dd/mm/yyyy → yyyy-mm-dd
                          if (/^\d{2}\/\d{2}\/\d{4}$/.test(dataVal)) {
                            const [d, m, y] = dataVal.split('/');
                            dataVal = `${y}-${m}-${d}`;
                          } else if (/^\d{2}\/\d{2}\/\d{2}$/.test(dataVal)) {
                            const [d, m, y] = dataVal.split('/');
                            dataVal = `20${y}-${m}-${d}`;
                          }
                          const valorStr = (r[iValor] || '0').toString().replace(/[R$\s]/g,'').replace(',','.');
                          const valor = parseFloat(valorStr);
                          if (isNaN(valor) || valor === 0) return;
                          novos.push({
                            id: Date.now() + ri,
                            data: dataVal,
                            historico: (r[iHist] || '').toString().trim(),
                            contaDebito: (r[iDeb] || '').toString().trim(),
                            contaCredito: (r[iCred] || '').toString().trim(),
                            valor,
                            documento: iDoc >= 0 ? (r[iDoc] || '').toString().trim() : '',
                            centroCusto: iCC >= 0 ? (r[iCC] || '').toString().trim() : '',
                            criadoEm: new Date().toISOString(),
                          });
                        });
                        if (novos.length === 0) { alert('Nenhum lançamento válido encontrado na planilha.'); return; }
                        if (window.confirm(`Importar ${novos.length} lançamento(s)? Eles serão adicionados aos lançamentos existentes.`)) {
                          setContabilConfig(prev => ({
                            ...(prev || {}),
                            lancamentos: [...((prev || {}).lancamentos || []), ...novos],
                          }));
                          alert(`✓ ${novos.length} lançamento(s) importado(s) com sucesso!`);
                        }
                      } catch (err) {
                        alert('Erro ao ler planilha: ' + err.message);
                      }
                    };
                    reader.readAsArrayBuffer(file);
                    e.target.value = '';
                  }} />
                </label>
              </div>
              <div style={{ marginTop: '0.75rem', fontSize: 11, color: '#8a849c' }}>
                Dica: a coluna <strong>Conta Débito</strong> e <strong>Conta Crédito</strong> aceitam apenas o código (ex: 1.1.2.01) ou o formato completo "código — nome".
              </div>
            </div>}

            {false && <div style={{ ...cardStyle, border: '1px solid rgba(16,185,129,0.3)' }}>
              <h3 style={{ color: '#1c1530', margin: '0 0 6px 0' }}>🏦 Importar Extrato Bancário — CSV</h3>
              <p style={{ fontSize: 12, color: '#5f5874', margin: '0 0 0.75rem 0' }}>
                Arquivo CSV exportado do banco. Colunas detectadas automaticamente. Linhas com valor positivo = <strong style={{ color: '#10b981' }}>RECEITA</strong>, negativo = <strong style={{ color: '#ef4444' }}>DESPESA</strong>.
              </p>
              <div style={{ background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: '0.75rem', fontSize: 12, color: '#5f5874' }}>
                <strong style={{ color: '#1d4ed8' }}>Formatos suportados:</strong> Itaú · Bradesco · Santander · Nubank · Sicoob · qualquer CSV com colunas Data, Descrição, Valor
              </div>
              <label style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-block' }}>
                📂 Selecionar Extrato .csv
                <input type="file" accept=".csv,.txt,.ofx" style={{ display: 'none' }} onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = ev => {
                    try {
                      const text = ev.target.result;
                      // Detectar separador
                      const sep = text.includes(';') ? ';' : ',';
                      const linhas = text.split(/\r?\n/).filter(l => l.trim() && !l.startsWith('#'));
                      if (linhas.length < 2) { alert('Arquivo vazio ou sem dados.'); return; }

                      const parseHeader = (h) => (h || '').toString().toLowerCase().replace(/['"]/g,'').trim();
                      const header = linhas[0].split(sep).map(parseHeader);

                      const col = (nomes) => { for (const n of nomes) { const i = header.findIndex(h => h.includes(n)); if (i >= 0) return i; } return -1; };
                      const iData  = col(['data','date']);
                      const iDesc  = col(['descri','hist','memo','lançamento','titulo']);
                      const iValor = col(['valor','value','amount','credito','debito','moviment']);

                      if (iData < 0 || iValor < 0) {
                        alert('Não foi possível identificar as colunas de Data e Valor. Verifique se o CSV tem cabeçalho com essas colunas.');
                        return;
                      }

                      const novos = [];
                      linhas.slice(1).forEach((linha, li) => {
                        const cells = linha.split(sep).map(c => c.replace(/^["']|["']$/g,'').trim());
                        let dataVal = cells[iData] || '';
                        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dataVal)) {
                          const [d, m, y] = dataVal.split('/');
                          dataVal = `${y}-${m}-${d}`;
                        } else if (/^\d{2}\/\d{2}\/\d{2}$/.test(dataVal)) {
                          const [d, m, y] = dataVal.split('/');
                          dataVal = `20${y}-${m}-${d}`;
                        }
                        if (!/^\d{4}-\d{2}-\d{2}$/.test(dataVal)) return;

                        const valorStr = (cells[iValor] || '0').replace(/[R$\s.]/g,'').replace(',','.');
                        const valor = parseFloat(valorStr);
                        if (isNaN(valor) || valor === 0) return;

                        const descricao = iDesc >= 0 ? (cells[iDesc] || 'Importado') : 'Importado';
                        novos.push({
                          id: Date.now() + li,
                          data: dataVal,
                          tipo: valor > 0 ? 'RECEITA' : 'DESPESA',
                          categoria: 'A Classificar',
                          descricao,
                          valor: Math.abs(valor),
                          status: 'pendente',
                          origem: 'extrato',
                        });
                      });

                      if (novos.length === 0) { alert('Nenhuma transação válida encontrada no extrato.'); return; }
                      if (window.confirm(`Importar ${novos.length} transação(ões) do extrato? Serão adicionadas ao Fluxo de Caixa com categoria "A Classificar".`)) {
                        setFluxoCaixa(prev => {
                          const idsExist = new Set((prev || []).map(f => f.data + '_' + f.valor + '_' + f.descricao));
                          const semDup = novos.filter(n => !idsExist.has(n.data + '_' + n.valor + '_' + n.descricao));
                          const dupCount = novos.length - semDup.length;
                          if (dupCount > 0) alert(`ℹ ${dupCount} lançamento(s) ignorado(s) por serem duplicatas.`);
                          return [...(prev || []), ...semDup];
                        });
                        alert(`✓ Extrato importado! Acesse a aba Lançamentos para classificar as categorias.`);
                      }
                    } catch (err) {
                      alert('Erro ao processar extrato: ' + err.message);
                    }
                  };
                  reader.readAsText(file, 'UTF-8');
                  e.target.value = '';
                }} />
              </label>
              <div style={{ marginTop: '0.75rem', fontSize: 11, color: '#8a849c' }}>
                Após importar, vá em <strong>2. Lançamentos</strong> e classifique as categorias das transações importadas (marcadas como "A Classificar").
              </div>
            </div>}

            {/* ── Importar Dados SPED 2024 ── */}
            <div style={{ ...cardStyle, border: contabilConfig.dre2024 ? '1px solid rgba(16,185,129,0.4)' : '1px solid rgba(245,158,11,0.4)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ color: '#1c1530', margin: '0 0 4px 0' }}>📂 Dados Referenciais — SPED Contábil 2024</h3>
                  <p style={{ fontSize: 12, color: '#5f5874', margin: 0 }}>
                    Entidade: <strong style={{ color: '#1c1530' }}>{empNomeDemonstracoes}</strong> · CNPJ {empCnpj} · Período Jan–Dez/2024
                  </p>
                </div>
                {contabilConfig.dre2024
                  ? <span style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.4)', borderRadius: 20, padding: '4px 14px', fontSize: 12, color: '#10b981' }}>✓ Importado</span>
                  : <span style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 20, padding: '4px 14px', fontSize: 12, color: '#f59e0b' }}>Não importado</span>
                }
              </div>

              {/* Resumo dos dados */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                {[
                  { label: 'Receita Bruta 2024',      valor: 'R$ 107.992,00',  cor: '#10b981' },
                  { label: 'Receita Líquida 2024',     valor: 'R$ 101.512,42',  cor: '#10b981' },
                  { label: 'Despesas Operacionais',    valor: 'R$ 21.710,79',   cor: '#ef4444' },
                  { label: 'Resultado Líquido 2024',   valor: 'R$ 79.801,63',   cor: '#10b981' },
                  { label: 'Dividendos Distribuídos',  valor: 'R$ 26.997,21',   cor: '#60a5fa' },
                  { label: 'Lucros Acumulados (BP)',   valor: 'R$ 26.917,01',   cor: '#a78bfa' },
                  { label: 'Total Ativo (BP Dez/24)',  valor: 'R$ 50.415,61',   cor: '#5f5874' },
                  { label: 'Patrimônio Líquido',       valor: 'R$ 29.917,01',   cor: '#a78bfa' },
                ].map(item => (
                  <div key={item.label} style={{ background: '#f7f5fb', borderRadius: 8, padding: '10px 14px', border: '1px solid #e5e0ef' }}>
                    <div style={{ fontSize: 11, color: '#5f5874', marginBottom: 4 }}>{item.label}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: item.cor }}>{item.valor}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    if (!setContabilConfig) return;
                    setContabilConfig(prev => ({
                      ...(prev || {}),
                      dre2024: DADOS_REFERENCIA_2024.dre,
                      bp2024:  DADOS_REFERENCIA_2024.bp,
                      dlpa2024: DADOS_REFERENCIA_2024.dlpa,
                    }));
                  }}
                  style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  {contabilConfig.dre2024 ? '↺ Reimportar dados 2024' : '📥 Importar dados 2024 para o sistema'}
                </button>
                <button
                  onClick={() => {
                    if (!setContabilConfig) return;
                    setContabilConfig(prev => ({
                      ...(prev || {}),
                      mapeamentoContas: { ...MAPEAMENTO_PADRAO_CONTAS, ...((prev || {}).mapeamentoContas || {}) },
                    }));
                  }}
                  style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  🗂 Aplicar mapeamento sugerido (SPED)
                </button>
                {contabilConfig.dre2024 && (
                  <button
                    onClick={() => {
                      if (!setContabilConfig) return;
                      setContabilConfig(prev => { const n = { ...(prev || {}) }; delete n.dre2024; delete n.bp2024; delete n.dlpa2024; return n; });
                    }}
                    style={{ background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                  >
                    Remover dados 2024
                  </button>
                )}
              </div>
              <p style={{ fontSize: 11, color: '#8a849c', margin: '0.75rem 0 0 0' }}>
                "Importar dados 2024" salva os valores do SPED no sistema e ativa a coluna comparativa no DRE. "Aplicar mapeamento sugerido" pré-configura as categorias com base no plano de contas do SPED (sem sobrescrever mapeamentos já existentes).
              </p>
            </div>

            {/* Mapeamento de categorias */}
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div>
                  <h3 style={{ color: '#1c1530', margin: '0 0 4px 0' }}>Mapeamento: Categoria → Conta Contábil (Balancete) → DRE</h3>
                  <div style={{ fontSize: 12, color: '#5f5874', marginTop: 4, lineHeight: 1.6 }}>
                    <strong style={{ color: '#1d4ed8' }}>Passo 1:</strong> Vincule cada categoria do lançamento a uma conta do Plano de Contas (Balancete).<br />
                    <strong style={{ color: '#6d28d9' }}>Passo 2:</strong> O grupo do DRE é derivado automaticamente pelo prefixo da conta (5.2 → Pessoal, 5.3 → Admin, etc).
                  </div>
                </div>
                {naoMapeadas.length > 0 && (
                  <span style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 20, padding: '4px 14px', fontSize: 12, color: '#f59e0b' }}>
                    ⚠ {naoMapeadas.length} não mapeada(s)
                  </span>
                )}
              </div>

              {todasCategorias.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#5f5874', padding: '2rem', fontSize: 14 }}>
                  Nenhuma categoria encontrada no Fluxo de Caixa. Crie lançamentos com categorias no módulo Financeiro.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '1rem', padding: '8px 12px', background: '#f3f0f9', borderRadius: 8 }}>
                    <span style={{ fontSize: 11, color: '#5f5874', fontWeight: 700, textTransform: 'uppercase' }}>Categoria do Lançamento</span>
                    <span style={{ fontSize: 11, color: '#5f5874', fontWeight: 700, textTransform: 'uppercase' }}>Linha do DRE correspondente</span>
                  </div>

                  {todasCategorias.map(cat => {
                    const contaAtual = mapeamentoContas[cat] || '';
                    const grupoAtual = contaAtual ? contaParaGrupoDRE(contaAtual) : (mapeamento[cat] || '');
                    const meta = DRE_GRUPOS_META.find(m => m.grupo === grupoAtual);
                    const cor = meta?.cor || '#6e7681';
                    return (
                      <div key={cat} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '1rem', padding: '10px 12px', background: '#f7f5fb', borderRadius: 8, border: `1px solid ${grupoAtual ? cor + '55' : 'rgba(245,158,11,0.3)'}`, alignItems: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          <span style={{ color: '#1c1530', fontWeight: 600, fontSize: 14 }}>{cat}</span>
                          {mapeamentoContas[cat] ? (
                            <span style={{ fontSize: 11, color: cor }}>
                              <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: cor, marginRight: 5 }} />
                              {mapeamentoContas[cat]} → {contaParaGrupoDRE(mapeamentoContas[cat])}
                            </span>
                          ) : mapeamento[cat] ? (
                            <span style={{ fontSize: 11, color: '#5f5874' }}>⚠ Mapeamento legado: {mapeamento[cat]}</span>
                          ) : (
                            <span style={{ fontSize: 11, color: '#f59e0b' }}>⚠ Não mapeado</span>
                          )}
                        </div>
                        <select
                          value={mapeamentoContas[cat] || mapeamento[cat] || ''}
                          onChange={e => salvarMapeamento(cat, e.target.value)}
                          style={{ background: '#ffffff', border: `1px solid ${cor}`, borderRadius: 8, color: '#1c1530', padding: '8px 12px', fontSize: 13, width: '100%' }}
                        >
                          <option value="">— Selecione a conta contábil —</option>
                          {['R','D','DR'].map(tipo => {
                            const nomes = { R: '4 — RECEITAS', D: '5 — CUSTOS E DESPESAS', DR: '6 — DESTINAÇÃO' };
                            const contas = contasAnaliticasConf.filter(c => c.tipo === tipo);
                            if (!contas.length) return null;
                            return (
                              <optgroup key={tipo} label={nomes[tipo]}>
                                {contas.map(c => (
                                  <option key={c.codigo} value={`${c.codigo} — ${c.nome}`}>{c.codigo} — {c.nome}</option>
                                ))}
                              </optgroup>
                            );
                          })}
                        </select>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── ABA: PRÓ-LABORE ── */}
      {aba === 'proLabore' && (() => {
        const recibos = Array.isArray(contabilConfig.recibosProLabore) ? contabilConfig.recibosProLabore : [];
        const fmtBR = (v) => `R$ ${parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const bruto  = parseFloat(proLaboreForm.valorBruto || 0);
        const inss   = parseFloat(proLaboreForm.inss       || 0);
        const irrf   = parseFloat(proLaboreForm.irrf       || 0);
        const outros = parseFloat(proLaboreForm.outros     || 0);
        const liquidoCalc = bruto - inss - irrf - outros;
        const liquidoExibido = proLaboreForm.valorLiquido !== ''
          ? parseFloat(proLaboreForm.valorLiquido)
          : liquidoCalc;

        const setCampo = (campo) => (e) => setProLaboreForm(f => ({ ...f, [campo]: e.target.value }));

        const proximoNumero = () => {
          const ano = new Date().getFullYear();
          const doAno = recibos.filter(r => (r.numero || '').includes(`-${ano}-`));
          const seq = doAno.length + 1;
          const prefixo = (empresaAtiva?.nomeMarca || 'REC').replace(/[^A-Z0-9]/gi, '').slice(0, 4).toUpperCase() || 'REC';
          return `${prefixo}-${ano}-${String(seq).padStart(4, '0')}`;
        };

        // Monta o endereço em uma linha a partir dos campos separados (para exibir no PDF).
        const montarEndereco = (f) => {
          const linha1 = [f.empresaLogradouro, f.empresaNumero].filter(Boolean).join(', ');
          const cidadeUF = [f.empresaCidade, f.empresaUF].filter(Boolean).join('/');
          return [linha1, f.empresaComplemento, f.empresaBairro, cidadeUF, f.empresaCEP && `CEP ${f.empresaCEP}`]
            .filter(Boolean)
            .join(' — ');
        };

        const salvarERecibo = ({ imprimir }) => {
          if (!proLaboreForm.valorBruto) {
            alert('Informe o valor bruto do pró-labore.');
            return;
          }
          const numero = proLaboreForm.numero || proximoNumero();
          const registro = {
            ...proLaboreForm,
            numero,
            valorLiquido: liquidoExibido,
            empresaEndereco: montarEndereco(proLaboreForm),
            emitidoEm: new Date().toISOString(),
          };
          setContabilConfig(prev => ({
            ...(prev || {}),
            enderecoEmpresaCampos: {
              logradouro:  proLaboreForm.empresaLogradouro  || '',
              numero:      proLaboreForm.empresaNumero      || '',
              complemento: proLaboreForm.empresaComplemento || '',
              bairro:      proLaboreForm.empresaBairro      || '',
              cidade:      proLaboreForm.empresaCidade      || '',
              uf:          proLaboreForm.empresaUF          || '',
              cep:         proLaboreForm.empresaCEP         || '',
            },
            recibosProLabore: [registro, ...(prev?.recibosProLabore || [])],
          }));
          if (imprimir) {
            exportarPDF(htmlReciboProLabore(registro), `Recibo Pró-labore ${numero}`, { semCabecalho: true });
          }
          setProLaboreForm(proLaboreVazio);
        };

        const duplicarRecibo = (r) => {
          const dHoje = new Date();
          setProLaboreForm({
            empresaRazaoSocial: r.empresaRazaoSocial || '',
            empresaCNPJ:        r.empresaCNPJ || '',
            empresaLogradouro:  r.empresaLogradouro  || '',
            empresaNumero:      r.empresaNumero      || '',
            empresaComplemento: r.empresaComplemento || '',
            empresaBairro:      r.empresaBairro      || '',
            empresaCidade:      r.empresaCidade      || '',
            empresaUF:          r.empresaUF          || '',
            empresaCEP:         r.empresaCEP         || '',
            socioNome:          r.socioNome || '',
            socioCPF:           r.socioCPF || '',
            socioFuncao:        r.socioFuncao || 'Sócio Administrador',
            competencia:        `${dHoje.getFullYear()}-${String(dHoje.getMonth() + 1).padStart(2, '0')}`,
            dataPagamento:      dHoje.toISOString().slice(0, 10),
            cidadeAssinatura:   r.cidadeAssinatura || 'Canoas',
            valorBruto:         r.valorBruto || '',
            inss:               r.inss || '',
            irrf:               r.irrf || '',
            outros:             r.outros || '',
            valorLiquido:       '',
            observacoes:        r.observacoes || '',
          });
          window.scrollTo({ top: 0, behavior: 'smooth' });
        };

        const reimprimirRecibo = (r) => {
          exportarPDF(htmlReciboProLabore(r), `Recibo Pró-labore ${r.numero || ''}`, { semCabecalho: true });
        };

        const excluirRecibo = (idx) => {
          if (!window.confirm('Remover este recibo do histórico? A ação não pode ser desfeita.')) return;
          setContabilConfig(prev => ({
            ...(prev || {}),
            recibosProLabore: (prev?.recibosProLabore || []).filter((_, i) => i !== idx),
          }));
        };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1rem', flexWrap: 'wrap', gap: 8 }}>
                <h3 style={{ color: '#1c1530', margin: 0, fontSize: 16 }}>🧾 Novo Recibo de Pró-labore</h3>
                <span style={{ fontSize: 11, color: '#5f5874' }}>Preencha os campos abaixo e imprima o recibo.</span>
              </div>

              <div style={{ fontSize: 12, color: '#5f5874', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Dados da Empresa</div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>Razão social</label>
                  <input style={inputStyle} value={proLaboreForm.empresaRazaoSocial} onChange={setCampo('empresaRazaoSocial')} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>CNPJ <span style={{ color: '#8a849c', textTransform: 'none', fontWeight: 400 }}>— clique 🔍 ou saia do campo para buscar</span></label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      style={{ ...inputStyle, flex: 1 }}
                      value={proLaboreForm.empresaCNPJ}
                      onChange={(e) => { setCnpjErro(''); setCampo('empresaCNPJ')(e); }}
                      onBlur={(e) => {
                        const digs = e.target.value.replace(/\D/g, '');
                        if (digs.length === 14) buscarCnpj(digs);
                      }}
                      placeholder="00.000.000/0000-00"
                    />
                    <button
                      type="button"
                      onClick={() => buscarCnpj(proLaboreForm.empresaCNPJ)}
                      disabled={cnpjBuscando}
                      title="Buscar dados na Receita Federal (BrasilAPI)"
                      style={{ background: cnpjBuscando ? '#ebe7f3' : '#238636', color: 'white', border: 'none', borderRadius: 6, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: cnpjBuscando ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                    >
                      {cnpjBuscando ? '⏳' : '🔍'}
                    </button>
                  </div>
                  {cnpjErro && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>⚠ {cnpjErro}</span>}
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#5f5874', fontWeight: 700, textTransform: 'uppercase', margin: '4px 0 6px' }}>Endereço <span style={{ color: '#8a849c', textTransform: 'none', fontWeight: 400 }}>— preenchido pela busca do CNPJ, editável campo a campo</span></div>
              <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 10, marginBottom: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>Logradouro</label>
                  <input style={inputStyle} value={proLaboreForm.empresaLogradouro} onChange={setCampo('empresaLogradouro')} placeholder="Rua / Av." />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>Número</label>
                  <input style={inputStyle} value={proLaboreForm.empresaNumero} onChange={setCampo('empresaNumero')} placeholder="Nº" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>Complemento</label>
                  <input style={inputStyle} value={proLaboreForm.empresaComplemento} onChange={setCampo('empresaComplemento')} placeholder="Sala, andar, bloco…" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>Bairro</label>
                  <input style={inputStyle} value={proLaboreForm.empresaBairro} onChange={setCampo('empresaBairro')} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 80px 130px', gap: 10, marginBottom: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>Cidade</label>
                  <input style={inputStyle} value={proLaboreForm.empresaCidade} onChange={setCampo('empresaCidade')} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>UF</label>
                  <input style={inputStyle} value={proLaboreForm.empresaUF} onChange={setCampo('empresaUF')} maxLength={2} placeholder="RS" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>CEP</label>
                  <input style={inputStyle} value={proLaboreForm.empresaCEP} onChange={setCampo('empresaCEP')} placeholder="00000-000" />
                </div>
              </div>

              <div style={{ fontSize: 12, color: '#5f5874', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Dados do Sócio</div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>Nome completo</label>
                  <input style={inputStyle} value={proLaboreForm.socioNome} onChange={setCampo('socioNome')} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>CPF</label>
                  <input style={inputStyle} value={proLaboreForm.socioCPF} onChange={setCampo('socioCPF')} placeholder="000.000.000-00" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>Função</label>
                  <input style={inputStyle} value={proLaboreForm.socioFuncao} onChange={setCampo('socioFuncao')} />
                </div>
              </div>

              <div style={{ fontSize: 12, color: '#5f5874', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Competência e Pagamento</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>Competência (mês/ano)</label>
                  <input style={inputStyle} type="month" value={proLaboreForm.competencia} onChange={setCampo('competencia')} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>Data do pagamento</label>
                  <input style={inputStyle} type="date" value={proLaboreForm.dataPagamento} onChange={setCampo('dataPagamento')} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>Cidade da assinatura</label>
                  <input style={inputStyle} value={proLaboreForm.cidadeAssinatura} onChange={setCampo('cidadeAssinatura')} />
                </div>
              </div>

              <div style={{ fontSize: 12, color: '#5f5874', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Valores</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>Valor bruto (R$)</label>
                  <input style={inputStyle} type="number" step="0.01" min="0" value={proLaboreForm.valorBruto} onChange={setCampo('valorBruto')} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>INSS retido (R$)</label>
                  <input style={inputStyle} type="number" step="0.01" min="0" value={proLaboreForm.inss} onChange={setCampo('inss')} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>IRRF retido (R$)</label>
                  <input style={inputStyle} type="number" step="0.01" min="0" value={proLaboreForm.irrf} onChange={setCampo('irrf')} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>Outros descontos (R$)</label>
                  <input style={inputStyle} type="number" step="0.01" min="0" value={proLaboreForm.outros} onChange={setCampo('outros')} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: 10, marginBottom: 16, alignItems: 'end' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>Valor líquido (R$) <span style={{ color: '#8a849c', textTransform: 'none', fontWeight: 400 }}>— opcional, sobrescreve o cálculo</span></label>
                  <input style={inputStyle} type="number" step="0.01" min="0" value={proLaboreForm.valorLiquido} onChange={setCampo('valorLiquido')} placeholder={liquidoCalc.toFixed(2)} />
                </div>
                <div style={{ background: '#ffffff', border: '1px solid #eeeaf5', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: '#5f5874', textTransform: 'uppercase', fontWeight: 700 }}>Líquido a pagar</span>
                  <span style={{ fontSize: 20, color: '#10b981', fontWeight: 800 }}>{fmtBR(liquidoExibido)}</span>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
                <label style={labelStyle}>Observações (opcional)</label>
                <textarea style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }} value={proLaboreForm.observacoes} onChange={setCampo('observacoes')} placeholder="Ex.: pagamento via PIX na chave do sócio; INSS recolhido via GPS individual; etc." />
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  onClick={() => salvarERecibo({ imprimir: true })}
                  style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '10px 22px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                >
                  🖨️ Salvar e Imprimir Recibo
                </button>
                <button
                  onClick={() => salvarERecibo({ imprimir: false })}
                  style={{ background: 'rgba(124,58,237,0.15)', color: '#6d28d9', border: '1px solid rgba(124,58,237,0.4)', borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  💾 Salvar (sem imprimir)
                </button>
                <button
                  onClick={() => setProLaboreForm(proLaboreVazio)}
                  style={{ background: 'transparent', color: '#5f5874', border: '1px solid #e5e0ef', borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  Limpar formulário
                </button>
              </div>
            </div>

            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1rem', flexWrap: 'wrap', gap: 8 }}>
                <h3 style={{ color: '#1c1530', margin: 0, fontSize: 16 }}>📁 Recibos emitidos</h3>
                <span style={{ fontSize: 11, color: '#5f5874' }}>Reimprima ou duplique um recibo anterior.</span>
              </div>
              {recibos.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#8a849c', fontSize: 13 }}>
                  Nenhum recibo emitido ainda.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#5f5874', borderBottom: '1px solid #eeeaf5', textTransform: 'uppercase' }}>Número</th>
                      <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#5f5874', borderBottom: '1px solid #eeeaf5', textTransform: 'uppercase' }}>Competência</th>
                      <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#5f5874', borderBottom: '1px solid #eeeaf5', textTransform: 'uppercase' }}>Sócio</th>
                      <th style={{ textAlign: 'right', padding: '8px 10px', fontSize: 11, color: '#5f5874', borderBottom: '1px solid #eeeaf5', textTransform: 'uppercase' }}>Bruto</th>
                      <th style={{ textAlign: 'right', padding: '8px 10px', fontSize: 11, color: '#5f5874', borderBottom: '1px solid #eeeaf5', textTransform: 'uppercase' }}>Líquido</th>
                      <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#5f5874', borderBottom: '1px solid #eeeaf5', textTransform: 'uppercase' }}>Pagamento</th>
                      <th style={{ width: 260, borderBottom: '1px solid #eeeaf5' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recibos.map((r, i) => {
                      const compStr = r.competencia && /^\d{4}-\d{2}$/.test(r.competencia)
                        ? `${r.competencia.slice(5,7)}/${r.competencia.slice(0,4)}`
                        : '—';
                      const pagStr = r.dataPagamento
                        ? new Date(r.dataPagamento + 'T00:00').toLocaleDateString('pt-BR')
                        : '—';
                      return (
                        <tr key={r.numero || i} style={{ borderBottom: '1px solid #eeeaf5' }}>
                          <td style={{ padding: '8px 10px', fontSize: 12, color: '#1c1530', fontFamily: 'monospace' }}>{r.numero || '—'}</td>
                          <td style={{ padding: '8px 10px', fontSize: 13, color: '#2d2640' }}>{compStr}</td>
                          <td style={{ padding: '8px 10px', fontSize: 13, color: '#2d2640' }}>{r.socioNome || '—'}</td>
                          <td style={{ padding: '8px 10px', fontSize: 13, color: '#2d2640', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtBR(r.valorBruto)}</td>
                          <td style={{ padding: '8px 10px', fontSize: 13, color: '#10b981', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 700 }}>{fmtBR(r.valorLiquido)}</td>
                          <td style={{ padding: '8px 10px', fontSize: 12, color: '#5f5874' }}>{pagStr}</td>
                          <td style={{ padding: '8px 4px', display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button onClick={() => reimprimirRecibo(r)} title="Reimprimir" style={{ background: 'rgba(96,165,250,0.15)', color: '#1d4ed8', border: '1px solid rgba(96,165,250,0.4)', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>🖨️ Imprimir</button>
                            <button onClick={() => duplicarRecibo(r)} title="Duplicar no formulário" style={{ background: 'rgba(167,139,250,0.15)', color: '#6d28d9', border: '1px solid rgba(167,139,250,0.4)', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>📄 Duplicar</button>
                            <button onClick={() => excluirRecibo(i)} title="Excluir do histórico" style={{ background: 'transparent', color: '#ef4444', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: 'pointer' }}>✕</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        );
      })()}

      </div>
    </div>
  );
}

// Error Boundary para capturar erros de runtime
class AppErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, background: '#ffffff', color: '#1c1530', minHeight: '100vh', fontFamily: 'monospace' }}>
          <h1 style={{ color: '#ef4444' }}>Erro no sistema</h1>
          <pre style={{ color: '#f59e0b', whiteSpace: 'pre-wrap', marginTop: 20 }}>{String(this.state.error)}</pre>
          <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }} style={{ marginTop: 20, padding: '10px 20px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 16 }}>
            Recarregar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppWithBoundary() {
  // EmpresaProvider é montado DENTRO de App após a sessão autenticada,
  // para poder receber o email real do usuário e filtrar visibilidade.
  return (
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  );
}

export default AppWithBoundary;
